import type { Guide } from "./types";

export const allGuides: Guide[] = [
  {
    slug: "ungdomskraft-vs-tiptapp",
    title: "Ungdomskraft vs Tiptapp - vilken tjänst ska du välja?",
    excerpt:
      "Behöver du hjälp med bortforsling, transport eller flytt? Två vanliga alternativ är Ungdomskraft och Tiptapp — men de fungerar på helt olika sätt. I den här guiden förklarar vi modellskillnaden och visar varför många kunder väljer Ungdomskraft för fast pris, RUT-avdrag, anställd personal och kontaktperson.",
    coverImage: "/guide-ungdomskraft-team.png",
    coverImageAlt: "Ungdomskrafts team - anställda medarbetare med eget skåpbil",
    publishedAt: "2026-02-28",
    updatedAt: "2026-04-29",
    tags: ["bortforsling", "transport"],
    metaTitle:
      "Ungdomskraft vs Tiptapp (2026) – Vilken tjänst ska du välja?",
    metaDescription:
      "Ungdomskraft vs Tiptapp — så skiljer sig en tjänst med anställd personal från en marknadsplats. Fast pris, RUT-avdrag, Trygg Hansa-försäkring och dedikerad kontaktperson.",
    content: [
      {
        heading: "Ungdomskraft vs Tiptapp - vilken tjänst ska du välja?",
        html: `<p>Behöver du hjälp med <a href="/bortforsling">bortforsling</a>, <a href="/transporthjalp">transport</a> eller <a href="/flytthjalp">flytt</a>? Två vanliga alternativ är <strong>Ungdomskraft</strong> och <strong>Tiptapp</strong>. Båda kan hjälpa dig att bli av med möbler, flytta tunga föremål eller transportera saker du köpt på Blocket — men de fungerar på helt olika sätt.</p>
<p>Ungdomskraft är ett tjänsteföretag som skickar <strong>egna anställda medarbetare</strong> med ett eget skåpbil. Du bokar, vi kommer, vi gör jobbet. Tiptapp är en <strong>marknadsplats</strong> där privatpersoner och mindre företag tar emot uppdrag via en app.</p>
<p>Det är två olika modeller — och vilken som passar dig beror på jobbets storlek, din budget och vad du värdesätter. I den här guiden förklarar vi vad du kan förvänta dig av Ungdomskraft, så att du kan göra ett välgrundat val.</p>
<p>Vi är öppna med att vi har ett intresse i att du läser den här guiden — vi är Ungdomskraft. Vår uppgift här är inte att övertyga dig om att Tiptapp är dåligt, utan att tydligt visa vad du får av oss: fast pris, RUT-avdrag, anställd personal, försäkring via Trygg Hansa och en kontaktperson som du kan ringa direkt.</p>`,
      },
      {
        heading: "Så fungerar Ungdomskraft",
        html: `<p>Ungdomskraft är ett svenskt tjänsteföretag som erbjuder <a href="/bortforsling">bortforsling</a>, <a href="/flytthjalp">flytt</a>, <a href="/transporthjalp">transport</a>, <a href="/mobelmontering">möbelmontering</a> och <a href="/barhjalp">bärhjälp</a> i <a href="/bortforsling-stockholm">Stockholm</a> och <a href="/bortforsling-goteborg">Göteborg</a>. När du bokar via <a href="/boka">ungdomskraft.com/boka</a> får du ett <strong>fast pris</strong> direkt — inga överraskningar i efterhand.</p>
<p>På den bokade tiden kommer ett team av unga, anställda medarbetare med ett <strong>eget skåpbil</strong>. Du behöver varken hyra bil, organisera hjälp eller köra till återvinningscentralen själv. Teamet hanterar allt från bärande i trappor till lastning, transport och korrekt hantering på återvinningscentral.</p>
<p>Här är de viktigaste delarna av Ungdomskrafts modell:</p>
<ul>
  <li><strong>Anställda medarbetare</strong> — Alla som jobbar hos oss är anställda med kollektivavtalsmässiga villkor. Vi gör bakgrundskontroller på alla medarbetare innan de börjar.</li>
  <li><strong>Eget skåpbil ingår alltid</strong> — Du behöver inte gissa vilket fordon som dyker upp. Vi kommer alltid med ett fullt utrustat skåpbil.</li>
  <li><strong>Försäkrade via Trygg Hansa</strong> — Fullständig ansvarsförsäkring som täcker skador under hela uppdraget — direkt via en av Sveriges största försäkringsbolag.</li>
  <li><strong>RUT-avdrag (50 %)</strong> — Vi hanterar RUT-avdraget automatiskt på fakturan. Du betalar bara hälften av arbetskostnaden.</li>
  <li><strong>Dedikerad kontaktperson</strong> — Du får en personlig kontaktperson för ditt uppdrag. Inget anonymt formulär — du kan ringa <a href="tel:0851708281">08-517 082 81</a> och prata med en av oss.</li>
  <li><strong>Betala på faktura</strong> — Vi erbjuder fakturabetalning, inte bara Swish eller kortbetalning i en app.</li>
  <li><strong>Svensk- och engelsktalande personal</strong> — Våra medarbetare pratar svenska och engelska, så kommunikationen fungerar alltid — i trapphuset, vid lastning och om du behöver instruktioner kring känsliga föremål eller golv.</li>
</ul>
<p>Ungdomskrafts sociala mission är att hjälpa <strong>ungdomar ut i arbetslivet</strong>. När du bokar hos oss bidrar du till att unga människor får sin första anställning med riktiga arbetsvillkor.</p>
<p>Vi har <strong>4,9 på Trustpilot</strong> med över 500 recensioner, och våra kunder lyfter framför allt att våra team är snabba, professionella och trevliga att ha att göra med.</p>
<img src="/guide-ungdomskraft-worker-furniture.jpg" alt="Ungdomskraft-medarbetare bär möbler i källare under ett bortforslingsuppdrag" style="margin-top:1.5rem;border-radius:0.75rem;width:100%" />`,
      },
      {
        heading: "Så fungerar Tiptapp",
        html: `<p>Tiptapp är en app-baserad <strong>marknadsplats</strong> där du lägger ut ett uppdrag — till exempel "hämta en soffa från min lägenhet" — och privatpersoner eller mindre företag kan acceptera det. Tiptapp själva utför inga uppdrag; de är en förmedlingstjänst som kopplar samman beställare och utförare.</p>
<p>När någon accepterar ditt uppdrag betalar du genom appen. Tiptapp tar en provision av priset; resten går till den som utför jobbet.</p>
<p>Modellen är <strong>flexibel</strong>. För större eller mer komplexa uppdrag — flytt, dödsbotömning, hemleverans, transport av värdefulla föremål — väljer många istället ett tjänsteföretag som Ungdomskraft för att få fast pris, anställd personal och en namngiven kontaktperson att ringa.</p>
<p>För mer information om Tiptapp i respektive stad, se vår <a href="/guide/tiptapp-stockholm">Tiptapp Stockholm-guide</a> och <a href="/guide/tiptapp-goteborg">Tiptapp Göteborg-guide</a>.</p>`,
      },
      {
        heading: "Anställd personal — så skiljer sig Ungdomskraft",
        html: `<p>Den största skillnaden mellan en marknadsplats och ett tjänsteföretag är <strong>vem som faktiskt kommer hem till dig</strong>. Hos Ungdomskraft är alla medarbetare anställda med arbetsavtal. Det innebär:</p>
<ul>
  <li><strong>Bakgrundskontroll</strong> — Vi kontrollerar varje medarbetares bakgrund innan de börjar jobba hos oss.</li>
  <li><strong>Utbildning</strong> — Nya medarbetare får utbildning i lyft, bärande, kundbemötande, fordonshantering och hur man skyddar känsliga golv och dörröppningar.</li>
  <li><strong>Arbetsrätter</strong> — Våra medarbetare har semester, sjuklön och rimliga arbetstider. De skyddas av svensk arbetslagstiftning.</li>
  <li><strong>Ansvar</strong> — Om någon i vårt team gör ett misstag tar Ungdomskraft som företag fullt ansvar via vår Trygg Hansa-försäkring.</li>
  <li><strong>Social mission</strong> — Vi hjälper ungdomar in på arbetsmarknaden. Många av våra medarbetare får sin första riktiga anställning hos oss.</li>
</ul>
<p>För dig som kund innebär anställningsmodellen två konkreta saker: du vet att det kommer <strong>utbildade, kontrollerade personer</strong> hem till dig, och du vet att <strong>företaget tar ansvar</strong> om något går snett.</p>
<img src="/guide-ungdomskraft-team-transport.jpg" alt="Två Ungdomskraft-medarbetare transporterar möbler med kärra under ett uppdrag" style="margin-top:1.5rem;border-radius:0.75rem;width:100%" />`,
      },
      {
        heading: "Försäkring och ansvar hos Ungdomskraft",
        html: `<p>Att släppa in någon i sitt hem för att bära möbler, vitvaror eller andra tunga föremål innebär alltid en risk. Vad händer om golvet repas? Om en vas välts? Om en tvättmaskin tappas i trappan? Hos Ungdomskraft är det här tydligt reglerat.</p>
<h3>Trygg Hansa-försäkring + företagsansvar</h3>
<p>Ungdomskraft är försäkrade genom <strong>Trygg Hansa</strong>, en av Sveriges största och mest etablerade försäkringsbolag. Försäkringen täcker skador som uppstår under uppdraget — från början till slut.</p>
<p>Om något går fel kontaktar du din <strong>dedikerade kontaktperson</strong> hos Ungdomskraft. Vi hanterar ärendet direkt — du behöver inte fylla i formulär hos ett tredjepartsföretag eller argumentera med en privatperson. Ungdomskraft tar som företag <strong>fullt ansvar för uppdraget</strong>.</p>
<h3>Tiptapps försäkring</h3>
<p>Tiptapp erbjuder försäkring genom sin app.</p>
<p>Skillnaden i korthet: hos Ungdomskraft har du ett företag som tar fullt ansvar via Trygg Hansa. Det är en av de trygghetsfaktorer som många av våra kunder lyfter fram.</p>`,
      },
      {
        heading: "Kundtjänst och support",
        html: `<p>När du bokar en tjänst vill du veta att du kan nå någon om du har frågor, behöver ändra eller om något går fel.</p>
<h3>Ungdomskraft: dedikerad kontaktperson och telefonväxel</h3>
<p>Varje kund som bokar via Ungdomskraft får en <strong>dedikerad kontaktperson</strong> för sitt uppdrag. Du kan kontakta den personen via <strong>telefon och chatt</strong>. Vill du nå oss direkt ringer du vår växel på <a href="tel:0851708281"><strong>08-517 082 81</strong></a> så kommer du till en namngiven kontaktperson. Behöver du ändra tid, har frågor om vad som ingår eller om ett problem uppstår — du har en namngiven person att vända dig till.</p>
<p>Företaget tar dessutom <strong>operativt ansvar för hela uppdraget</strong>. Om teamet försenas, om något går sönder eller om du inte är nöjd hanterar vi det direkt.</p>
<h3>Tiptapp: support via app</h3>
<p>Tiptapp hanterar kundtjänst främst via app och e-post (support@tiptapp.com). Eftersom Tiptapp är en marknadsplats och inte utför uppdraget själva ligger kvalitetsfrågor i praktiken mellan dig och utföraren.</p>
<p>Sammanfattning: Ungdomskraft ger dig en personlig kontaktperson med både telefon och chatt. När du behöver hjälp snabbt gör det stor skillnad.</p>`,
      },
      {
        heading: "RUT-avdrag och betalning",
        html: `<p><a href="/guide/vad-ar-rut-avdrag">RUT-avdraget</a> är en skattesubvention som ger privatpersoner <strong>50 % tillbaka på arbetskostnaden</strong> för hushållstjänster som <a href="/flytthjalp">flytt</a> och <a href="/barhjalp">bärhjälp</a>, och <strong>25 %</strong> för <a href="/bortforsling">bortforsling</a> till återanvändning. Det är en av de största ekonomiska fördelarna du kan få — men det förutsätter att företaget du anlitar kan erbjuda det.</p>
<h3>Ungdomskraft: RUT-avdrag alltid tillgängligt</h3>
<p>Ungdomskraft är ett registrerat företag med F-skattsedel som <strong>alltid erbjuder RUT-avdrag</strong>. Avdraget hanteras automatiskt på fakturan — du ser direkt vad du betalar efter RUT. Du behöver inte själv kontakta Skatteverket; vi söker tillbaka beloppet åt dig.</p>
<p>Det innebär i praktiken att ett jobb som kostar 3 000 kr i arbetskostnad bara kostar dig <strong>1 500 kr efter RUT</strong>. Det maximala avdraget är 75 000 kr per person och år.</p>
<p>Dessutom erbjuder Ungdomskraft <strong>fakturabetalning</strong>. Du behöver inte betala via en app eller Swish — du får en ordentlig faktura som du kan betala inom betalningsvillkoren. Det är särskilt praktiskt för större uppdrag och för dig som vill ha full koll på bokföringen.</p>
<h3>Tiptapp och RUT</h3>
<p>RUT-avdrag kräver att utföraren är ett registrerat företag med F-skattsedel. Eftersom Tiptapps utförare oftast är privatpersoner är RUT-avdrag inte tillgängligt via plattformen — du betalar fullt pris.</p>
<h3>Räkneexempel</h3>
<p>Ett bortforslingsjobb med en arbetskostnad på 4 000 kr blir hos Ungdomskraft <strong>3 000 kr efter RUT</strong> — och då ingår fordon, anställd personal och försäkring. Använd vår <a href="/guide/kalkyl-rut-rot">RUT-kalkylator</a> för att räkna ut din exakta besparing.</p>`,
      },
      {
        heading: "Miljöansvar — så hanterar Ungdomskraft dina saker",
        html: `<p>När du blir av med möbler och annat vill du gärna veta att sakerna hamnar rätt.</p>
<p>Ungdomskraft tar miljöansvar på allvar. Saker som är i gott skick <strong>doneras till Myrorna</strong> (Frälsningsarméns secondhand-kedja) så att de får ett nytt liv istället för att bli avfall. Resten sorteras och lämnas på <strong>återvinningscentral</strong> enligt kommunens riktlinjer.</p>
<p>Du kan känna dig trygg med att dina saker hanteras på ett ansvarigt sätt — oavsett om de återvinns, doneras eller deponeras korrekt. Det är en av anledningarna till att RUT-avdraget finns för bortforsling till återanvändning: staten vill premiera att möbler används vidare istället för att slängas.</p>`,
      },
      {
        heading: "Jämförelsetabell: Ungdomskraft vs Tiptapp",
        html: `<p>Här är en översiktlig jämförelse av de viktigaste faktorerna:</p>
<table>
  <thead>
    <tr>
      <th></th>
      <th>Ungdomskraft</th>
      <th>Tiptapp</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td><strong>Modell</strong></td>
      <td>Tjänsteföretag med anställda</td>
      <td>Marknadsplats (gigekonomi)</td>
    </tr>
    <tr>
      <td><strong>Personal</strong></td>
      <td>Anställda, bakgrundskontrollerade ungdomar</td>
      <td>Privatpersoner och enskilda firmor</td>
    </tr>
    <tr>
      <td><strong>Fordon</strong></td>
      <td>Eget skåpbil ingår alltid</td>
      <td>Varierar per utförare</td>
    </tr>
    <tr>
      <td><strong>Ansvar vid skada</strong></td>
      <td>Ungdomskraft tar fullt företagsansvar</td>
      <td>Hanteras enligt Tiptapps villkor</td>
    </tr>
    <tr>
      <td><strong>RUT-avdrag (50 %)</strong></td>
      <td>Alltid tillgängligt, hanteras automatiskt</td>
      <td>Inte tillgängligt (utförare är privatpersoner)</td>
    </tr>
    <tr>
      <td><strong>Betalning</strong></td>
      <td>Faktura, kort</td>
      <td>Kort, Swish via appen</td>
    </tr>
    <tr>
      <td><strong>Kundtjänst</strong></td>
      <td>Dedikerad kontaktperson, växel 08-517 082 81 + chatt</td>
      <td>Support via app och e-post</td>
    </tr>
    <tr>
      <td><strong>Språk</strong></td>
      <td>Svenska och engelska</td>
      <td>Varierar per utförare</td>
    </tr>
    <tr>
      <td><strong>Miljöhantering</strong></td>
      <td>Donation till Myrorna + återvinningscentral</td>
      <td>Enligt Tiptapps egna riktlinjer</td>
    </tr>
  </tbody>
</table>`,
      },
      {
        heading: "När passar Tiptapp bättre?",
        html: `<p>Vi vill vara ärliga — det finns situationer där Tiptapp kan vara ett rimligt alternativ:</p>
<ul>
  <li><strong>Ge bort gratissaker</strong> — Om du bara vill bli av med något som är gratis att hämta (typ en fungerande soffa) kan Tiptapp fungera bra. Du behöver inte betala någon, och någon får en gratis möbel.</li>
  <li><strong>Mycket småjobb</strong> — För ett enstaka, litet föremål där du inte bryr dig om fordon, försäkring eller RUT kan det vara enkelt att lägga ut på Tiptapp.</li>
</ul>
<p>För allt som involverar <strong>större mängder</strong>, <strong>tunga föremål</strong>, <strong>värdefulla saker</strong> eller där du vill ha <strong>fast pris, försäkring och RUT-avdrag</strong> är Ungdomskraft det markant bättre valet. Det gäller särskilt <a href="/bortforsling-mobler">bortforsling av möbler</a>, <a href="/tomma-kallare">bohagstömningar</a>, <a href="/bortforsling-dodsbo">dödsbon</a>, <a href="/flytthjalp">flytt</a>, <a href="/transport-vitvaror">transport av vitvaror</a>, <a href="/hemleverans-ikea">hemleverans från IKEA</a> och allt där du behöver ett skåpbil och professionell personal.</p>`,
      },
      {
        heading: "Varför välja Ungdomskraft?",
        html: `<p>Låt oss sammanfatta varför tusentals kunder i Stockholm och Göteborg väljer Ungdomskraft:</p>
<ul>
  <li><strong>Anställda med schyssta villkor</strong> — Inga gigarbetare. Våra ungdomar är anställda med arbetsavtal, sjuklön och semester.</li>
  <li><strong>Bakgrundskontrollerade medarbetare</strong> — Du vet vem du släpper in i ditt hem.</li>
  <li><strong>Eget skåpbil ingår alltid</strong> — Inget gissande om vad som dyker upp.</li>
  <li><strong>Försäkrade via Trygg Hansa</strong> — Fullständig ansvarsförsäkring direkt via ett av Sveriges största försäkringsbolag.</li>
  <li><strong>RUT-avdrag (50 %)</strong> — Alltid tillgängligt, hanteras automatiskt. Du betalar bara hälften.</li>
  <li><strong>Fast pris i förväg</strong> — Se ditt pris på 2 minuter på <a href="/boka">ungdomskraft.com/boka</a>. Inga bud, inga överraskningar på plats.</li>
  <li><strong>Telefonväxel</strong> — Ring <a href="tel:0851708281">08-517 082 81</a> och prata direkt med en namngiven kontaktperson.</li>
  <li><strong>Svensk- och engelsktalande personal</strong> — Kommunikationen fungerar i trapphuset, vid lastning och kring känsliga föremål.</li>
  <li><strong>Betala på faktura</strong> — För dig som vill ha ordentlig bokföring.</li>
  <li><strong>4,9 på Trustpilot</strong> — Över 500 kunder har betygsatt oss.</li>
  <li><strong>Miljöansvar</strong> — Donation till Myrorna och korrekt sortering på återvinningscentral.</li>
  <li><strong>Social mission</strong> — Du hjälper ungdomar in på arbetsmarknaden.</li>
</ul>
<p>Oavsett om du behöver <a href="/bortforsling-mobler">bortforsling av möbler</a>, hjälp med <a href="/flytthjalp">flytten</a>, <a href="/transporthjalp">transport</a> av något du köpt på Blocket, <a href="/tomma-dodsbo">tömning av dödsbo</a> eller en hel <a href="/tomma-kallare">bohagstömning</a> — Ungdomskraft är det trygga, prisvärda och ansvarstagande valet.</p>
<p><strong>Boka på under 2 minuter</strong> och se ditt pris direkt på <a href="/boka">ungdomskraft.com/boka</a>. Föredrar du att prata med någon? Ring <a href="tel:0851708281"><strong>08-517 082 81</strong></a>.</p>`,
      },
    ],
  },
  {
    slug: "tiptapp-stockholm",
    title: "Tiptapp Stockholm – hur fungerar det och vad kostar det?",
    excerpt:
      "Allt du behöver veta om Tiptapp i Stockholm: hur appen fungerar, vad det kostar och hur Ungdomskraft erbjuder ett professionellt alternativ med fast pris, RUT-avdrag, anställd personal och dedikerad kontaktperson.",
    coverImage: "/guide-tiptapp-stockholm.jpg",
    coverImageAlt:
      "Tiptapp Stockholm - guide om priser och alternativ",
    publishedAt: "2026-03-06",
    updatedAt: "2026-04-29",
    tags: ["stockholm", "bortforsling", "transport"],
    metaTitle:
      "Tiptapp Stockholm (2026) – Priser, hur det fungerar & bästa alternativet",
    metaDescription:
      "Hur fungerar Tiptapp i Stockholm? Vi går igenom priser och hur det fungerar — och visar varför Ungdomskraft är ett tryggt alternativ med fast pris, RUT-avdrag, anställd personal och kontaktperson.",
    faq: [
      {
        question: "Hur fungerar Tiptapp-appen?",
        answer:
          "Du laddar ner appen, skapar ett konto, tar bilder på det du behöver hjälp med och beskriver uppdraget. Utförare kan sedan lägga bud eller acceptera ditt pris. När du godkänner en utförare betalar du via appen, och utföraren kommer vid överenskommen tid.",
      },
      {
        question: "Vad får jag med Ungdomskraft i Stockholm?",
        answer:
          "Ungdomskraft skickar anställda, bakgrundskontrollerade medarbetare med ett eget skåpbil till ditt uppdrag i Stockholm. Du får fast pris i förväg på ungdomskraft.com/boka, RUT-avdrag automatiskt på fakturan, fullständig ansvarsförsäkring via Trygg Hansa, och en namngiven kontaktperson som du kan ringa på vår växel 08-517 082 81.",
      },
      {
        question: "Kan man få RUT-avdrag med Tiptapp?",
        answer:
          "RUT-avdrag kräver att utföraren är ett registrerat företag med F-skattsedel. Eftersom Tiptapps utförare oftast är privatpersoner är RUT-avdrag inte tillgängligt via plattformen. Med Ungdomskraft får du alltid RUT-avdrag — vilket halverar arbetskostnaden.",
      },
      {
        question: "Vad händer om jag bokar med Ungdomskraft istället?",
        answer:
          "Hos Ungdomskraft bokar du en fast tid via ungdomskraft.com/boka. Vårt team kommer på den bokade tiden med ett fullt utrustat skåpbil. Behöver du ändra tid eller har frågor? Ring vår växel på 08-517 082 81 så kommer du till en namngiven kontaktperson.",
      },
      {
        question: "Hur är försäkringsskyddet hos Ungdomskraft?",
        answer:
          "Ungdomskraft är försäkrade via Trygg Hansa med fullständig ansvarsförsäkring som täcker hela uppdraget. Om något skulle gå fel tar Ungdomskraft som företag fullt ansvar — du behöver inte hantera ärendet själv. Tiptapp erbjuder försäkring genom sin app.",
      },
      {
        question:
          "Vem tar ansvar om något går sönder under ett uppdrag?",
        answer:
          "Hos Ungdomskraft tar företaget fullt ansvar via vår Trygg Hansa-försäkring. Du kontaktar din dedikerade kontaktperson och vi hanterar ärendet direkt. På Tiptapp regleras ansvar enligt deras egna villkor — kontrollera dem i appen innan du bokar.",
      },
      {
        question: "Får jag fast pris hos Ungdomskraft?",
        answer:
          "Ja. Du ser ditt fasta pris direkt när du bokar på ungdomskraft.com/boka — inga bud, inga överraskningar, inga extrakrav på plats. Priset inkluderar anställd personal, eget skåpbil, försäkring och RUT-avdrag.",
      },
      {
        question:
          "Hur hanterar Ungdomskraft mina möbler efter upphämtning?",
        answer:
          "Saker i gott skick doneras till Myrorna (Frälsningsarméns secondhand-kedja) så att de får ett nytt liv. Resten sorteras och lämnas på återvinningscentral enligt kommunens riktlinjer. Det är därför RUT-avdrag är tillgängligt för bortforsling till återanvändning.",
      },
      {
        question: "Vilka alternativ till Tiptapp finns i Stockholm?",
        answer:
          "Det främsta alternativet är Ungdomskraft, som erbjuder bortforsling, transport och flytt med anställd personal, eget skåpbil, Trygg Hansa-försäkring och RUT-avdrag. Andra alternativ inkluderar att köra själv till återvinningscentralen eller anlita ett traditionellt flyttföretag.",
      },
      {
        question: "Gör Ungdomskraft bakgrundskontroller på sina medarbetare?",
        answer:
          "Ja. Ungdomskraft bakgrundskontrollerar alla anställda medarbetare innan de börjar jobba hos oss. Du vet vem du släpper in i ditt hem.",
      },
      {
        question: "Kan jag boka flytt i Stockholm med Ungdomskraft?",
        answer:
          "Ja. Ungdomskraft skickar utbildade, anställda medarbetare med ett skåpbil och fullständig försäkring för flytt i Stockholm. Boka på ungdomskraft.com/boka — RUT-avdrag på flytt är 50 % av arbetskostnaden.",
      },
      {
        question: "Hur snabbt kan jag få hjälp av Ungdomskraft i Stockholm?",
        answer:
          "Ungdomskraft erbjuder bokning med fast tid via ungdomskraft.com/boka, ofta med tillgänglighet samma dag. Vill du diskutera ett snabbare uppdrag? Ring vår växel på 08-517 082 81.",
      },
      {
        question: "Vad är skillnaden mellan Tiptapp och Ungdomskraft?",
        answer:
          "Tiptapp är en marknadsplats där privatpersoner tar uppdrag via en app. Ungdomskraft är ett tjänsteföretag med anställda medarbetare, eget skåpbil, Trygg Hansa-försäkring och RUT-avdrag. Läs vår fullständiga jämförelse för alla detaljer.",
      },
      {
        question:
          "Kan man använda Tiptapp för att ge bort möbler gratis i Stockholm?",
        answer:
          "Ja, Tiptapp har en funktion för att ge bort saker gratis. Någon hämtar och tar hand om sakerna utan kostnad. För enkla gratishämtningar kan det fungera bra.",
      },
    ],
    content: [
      {
        heading:
          "Tiptapp Stockholm – Hur fungerar det och vad kostar det?",
        html: `<p>Funderar du på att använda <strong>Tiptapp i Stockholm</strong>? Många stockholmare laddar ner appen för att få hjälp med <a href="/bortforsling">bortforsling</a>, <a href="/transporthjalp">transport</a> eller flytt av möbler och andra föremål. Men hur fungerar tjänsten egentligen, vad kostar det, och vilka alternativ finns?</p>
<p>I den här guiden går vi igenom hur Tiptapp fungerar steg för steg och vad det kostar. Vi presenterar också <strong>Ungdomskraft</strong> — Stockholms ledande tjänsteföretag för bortforsling, transport och flytt med anställd personal, fast pris och RUT-avdrag.</p>
<p>Vi är öppna med att vi är Ungdomskraft och har ett intresse av att du läser denna guide. Vårt mål är att du ska kunna jämföra modellerna och välja det som passar ditt uppdrag bäst. Läs gärna vår <a href="/guide/ungdomskraft-vs-tiptapp">fullständiga jämförelse mellan Ungdomskraft och Tiptapp</a> för fler detaljer.</p>
<p>Bor du i Göteborg? Läs vår <a href="/guide/tiptapp-goteborg">Tiptapp Göteborg-guide</a> istället.</p>
<p>Oavsett om du ska bli av med en soffa, transportera ett <a href="/transport-blocket">Blocket-köp</a> eller tömma en källare — vi hjälper dig att förstå dina alternativ.</p>`,
      },
      {
        heading: "Vad är Tiptapp?",
        html: `<p><strong>Tiptapp</strong> är en svensk app-baserad marknadsplats som grundades 2016. Det är viktigt att förstå att Tiptapp inte är ett transportföretag eller ett bortforslingsföretag — det är en <strong>förmedlingsplattform</strong> som kopplar samman privatpersoner som behöver hjälp med privatpersoner eller mindre företag som vill utföra jobbet.</p>
<p>Affärsmodellen bygger på att du som kund lägger ut ett uppdrag i appen — till exempel "hämta en soffa från Södermalm" — och att någon som har tid och möjlighet accepterar uppdraget. Tiptapp tar en provision av priset; resten går till utföraren.</p>
<p>Plattformen är verksam främst i <strong>Stockholm, Göteborg och Malmö</strong>, men det är i Stockholm som den har störst användarbas. Tjänsterna omfattar bortforsling av möbler och skräp, transport av köpta och sålda varor, flytt och gratishämtningar.</p>
<p>Tiptapp är en marknadsplats — utförarna är inte anställda av Tiptapp utan privatpersoner eller enskilda firmor. För enkla gratishämtningar och småjobb kan det vara ett smidigt alternativ. För större uppdrag, värdefulla föremål eller där du vill ha fast pris och RUT-avdrag är ett tjänsteföretag som Ungdomskraft ofta ett bättre val.</p>`,
      },
      {
        heading: "Hur fungerar Tiptapp i Stockholm?",
        html: `<p>Att använda Tiptapp i Stockholm är relativt enkelt rent tekniskt. Här är en steg-för-steg-genomgång av hela processen, från nedladdning till slutfört uppdrag.</p>

<h3>Steg för steg</h3>
<ol>
  <li><strong>Ladda ner appen</strong> — Tiptapp finns på App Store och Google Play. Appen är gratis att ladda ner.</li>
  <li><strong>Skapa ett konto</strong> — Registrera dig med e-post eller telefonnummer. Du behöver verifiera din identitet.</li>
  <li><strong>Fotografera det du behöver hjälp med</strong> — Ta tydliga bilder från flera vinklar. Bra bilder ökar chansen att en utförare accepterar uppdraget snabbt.</li>
  <li><strong>Beskriv uppdraget</strong> — Ange vad som ska göras, vilken våning du bor på, om det finns hiss, och eventuella andra detaljer. Ju mer information, desto färre missförstånd.</li>
  <li><strong>Sätt ett pris eller låt utförare buda</strong> — Du kan antingen ange ett fast pris eller låta utförare lägga bud. Priset inkluderar Tiptapps provision på 25 %.</li>
  <li><strong>Publicera uppdraget</strong> — Ditt uppdrag blir synligt för utförare i Stockholm-området.</li>
  <li><strong>Utförare accepterar eller budar</strong> — En eller flera utförare visar intresse. Du kan se deras profil och eventuella betyg.</li>
  <li><strong>Godkänn en utförare</strong> — Välj den utförare du vill anlita och bestäm en tid.</li>
  <li><strong>Utföraren kommer</strong> — Utföraren dyker upp vid överenskommen tid (förhoppningsvis) med sitt eget fordon.</li>
  <li><strong>Betala via appen</strong> — Betalningen sker genom appen. Betala aldrig kontant vid sidan av.</li>
</ol>

<h3>Vilka tjänster finns?</h3>
<p>I Stockholm kan du använda Tiptapp för flera typer av uppdrag:</p>
<ul>
  <li><strong>Bortforsling</strong> — Hämtning och borttransport av möbler, vitvaror, byggmaterial och annat skräp. Se vårt alternativ: <a href="/bortforsling">bortforsling med Ungdomskraft</a>.</li>
  <li><strong>Blocket-transport</strong> — Transport av varor du köpt eller sålt på Blocket. Läs mer om <a href="/transport-blocket">transport av Blocket-köp</a>.</li>
  <li><strong>Facebook Marketplace-transport</strong> — Samma sak för köp via Facebook. Se <a href="/transport-facebook-marketplace">transport av Facebook Marketplace-köp</a>.</li>
  <li><strong>Flytt</strong> — Hjälp med flytt av bohag, men utfört av privatpersoner. Jämför med <a href="/flytthjalp-stockholm">professionell flytthjälp i Stockholm</a>. Visste du att du kan få <a href="/guide/rut-avdrag-flytt">RUT-avdrag på flytt</a>?</li>
  <li><strong>Gratishämtningar</strong> — Ge bort saker gratis till någon som hämtar dem. En av Tiptapps mest populära funktioner.</li>
</ul>
<p>För alla dessa tjänster erbjuder Ungdomskraft ett professionellt alternativ med anställd personal, eget skåpbil och <a href="/transporthjalp">fast pris</a>.</p>`,
      },
      {
        heading: "Vad kostar Tiptapp i Stockholm?",
        html: `<p>Priserna på Tiptapp drivs av <strong>marknadskrafter</strong> — utförare sätter sina egna priser eller budar på uppdrag. Det innebär att det inte finns någon officiell prislista. Priset beror på vad du behöver, hur mycket det väger, vilken våning du bor på, hur långt det ska transporteras och hur många utförare som är tillgängliga just då.</p>
<h3>RUT-avdrag — en av de största prisfaktorerna</h3>
<p>RUT-avdrag kräver att utföraren är ett registrerat företag med F-skattsedel. Eftersom Tiptapps utförare oftast är privatpersoner är <a href="/guide/vad-ar-rut-avdrag">RUT-avdrag</a> inte tillgängligt via plattformen — du betalar fullt pris.</p>
<p>Med Ungdomskraft är RUT alltid tillgängligt. Ett bortforslingsjobb med 4 000 kr i arbetskostnad blir <strong>3 000 kr efter RUT (25 % på bortforsling till återanvändning)</strong> hos Ungdomskraft — och då ingår anställd personal, eget skåpbil och försäkring i priset. Läs mer om <a href="/guide/rut-avdrag-bortforsling">RUT-avdrag på bortforsling</a>.</p>
<p>För <a href="/bortforsling-mobler">bortforsling av möbler</a> i Stockholm är Ungdomskraft ofta billigare efter RUT-avdrag — och du får fast pris i förväg. Se vår sida om <a href="/bortforsling-stockholm">bortforsling i Stockholm</a> för mer information.</p>`,
      },
      {
        heading: "Vad får du med Ungdomskraft i Stockholm?",
        html: `<p>När du bokar Ungdomskraft i Stockholm kommer ett team av anställda, bakgrundskontrollerade medarbetare med ett eget skåpbil till ditt uppdrag. Det här ingår alltid:</p>
<ul>
  <li><strong>Fast pris</strong> — Boka på <a href="/boka">ungdomskraft.com/boka</a> och se ditt pris direkt. Inga bud, inga överraskningar, inga extrakrav på plats.</li>
  <li><strong>Anställd personal</strong> — Alla våra medarbetare är anställda med arbetsavtal. Vi gör bakgrundskontroller på alla innan de börjar.</li>
  <li><strong>Eget skåpbil ingår alltid</strong> — Inget gissande om vad som dyker upp. Vi kommer alltid med rätt fordon för jobbet.</li>
  <li><strong>RUT-avdrag automatiskt på fakturan</strong> — Du betalar bara hälften av arbetskostnaden för flytt och bärhjälp, eller 25 % rabatt på bortforsling till återanvändning.</li>
  <li><strong>Trygg Hansa-försäkring</strong> — Fullständig ansvarsförsäkring direkt via en av Sveriges största försäkringsbolag.</li>
  <li><strong>Telefonväxel</strong> — Ring <a href="tel:0851708281"><strong>08-517 082 81</strong></a> och prata direkt med en kontaktperson. Du har en namngiven person att vända dig till om du behöver ändra tid eller har frågor.</li>
  <li><strong>Svensk- och engelsktalande personal</strong> — Kommunikationen fungerar i trapphuset, vid lastning och kring känsliga föremål eller golv.</li>
  <li><strong>Miljöansvar</strong> — Saker i gott skick doneras till Myrorna. Resten sorteras och lämnas på återvinningscentral.</li>
</ul>
<p>Ungdomskraft har <strong>4,9 på Trustpilot</strong> med över 500 recensioner. Våra kunder lyfter framför allt snabbheten, professionalismen och den trevliga personalen.</p>
<img src="/guide-tiptapp-stockholm-team.jpg" alt="Ungdomskraft-medarbetare i Stockholm — anställda ungdomar med schyssta villkor" style="margin-top:1.5rem;border-radius:0.75rem;width:100%" />`,
      },
      {
        heading: "Försäkring och ansvar — så fungerar det hos Ungdomskraft",
        html: `<p>Att släppa in någon i sitt hem för att bära möbler, vitvaror eller andra tunga föremål innebär alltid en risk. Vad händer om golvet repas? Om en vas välts? Om en tvättmaskin tappas i trappan?</p>
<h3>Trygg Hansa-försäkring + företagsansvar</h3>
<p>Ungdomskraft är försäkrade via <strong>Trygg Hansa</strong> med fullständig ansvarsförsäkring som täcker hela uppdraget — från början till slut. Om något går fel kontaktar du din dedikerade kontaktperson hos Ungdomskraft. Vi hanterar ärendet direkt — du behöver inte fylla i formulär hos ett tredjepartsföretag eller argumentera med en privatperson. Ungdomskraft tar som företag fullt ansvar för uppdraget.</p>
<h3>Tiptapps försäkring</h3>
<p>Tiptapp erbjuder försäkring genom sin app.</p>
<h3>Stockholm-specifika utmaningar</h3>
<p>Stockholms äldre byggnader skapar extra utmaningar där professionell hantering verkligen gör skillnad:</p>
<ul>
  <li><strong>Södermalm och Gamla stan</strong> — Smala trapphus, trånga dörröppningar och ofta avsaknad av hiss. Vårt team använder skyddsmaterial och har erfarenhet av lyft i utmanande utrymmen.</li>
  <li><strong>Parkettgolv</strong> — Många stockholmslägenheter har känsliga parkettgolv som behöver skyddas vid flytt och bortforsling. Vi tar med skyddsmaterial som standard.</li>
  <li><strong>Hissbegränsningar i äldre byggnader</strong> — Många äldre hissar i Stockholm har begränsad kapacitet. Tunga föremål som tvättmaskiner eller soffor kräver trappbärning, något våra utbildade team hanterar dagligen.</li>
</ul>
<p>Med Ungdomskraft tar företaget fullt ansvar — du behöver aldrig hantera en tvist med en privatperson.</p>`,
      },
      {
        heading:
          "Vad ska man tänka på innan man bokar Tiptapp i Stockholm?",
        html: `<p>Om du ändå bestämmer dig för att använda Tiptapp i Stockholm finns det flera saker du bör tänka på för att minimera riskerna. Här är vår praktiska checklista.</p>

<h3>Generella tips</h3>
<ul>
  <li><strong>Ta tydliga bilder från flera vinklar</strong> — Fotografera alla föremål som ska forslas bort eller transporteras. Ta bilder som visar storlek, skick och eventuella svårigheter (trappor, trånga passager).</li>
  <li><strong>Kom överens om exakt omfattning skriftligt</strong> — Ange hur många föremål det gäller, vilken våning du bor på, om det finns hiss, och exakt vad som ingår. Gör det i appen så att det finns dokumenterat.</li>
  <li><strong>Kontrollera utförarens profil och betyg</strong> — Titta på hur många uppdrag utföraren har gjort och vilka betyg de fått. Välj utförare med minst 10 utförda uppdrag och höga betyg.</li>
  <li><strong>Bekräfta fordonstyp innan du accepterar</strong> — Fråga vilken typ av fordon utföraren har. En personbil räcker sällan för bortforsling av möbler. Se till att fordonet matchar jobbet.</li>
  <li><strong>Betala aldrig kontant utanför appen</strong> — All betalning ska ske via appen. Om en utförare ber om kontant betalning vid sidan av är det ett varningstecken.</li>
</ul>

<h3>Stockholm-specifika tips</h3>
<ul>
  <li><strong>Boka hiss i förväg</strong> — Många bostadsrättsföreningar i Stockholm kräver att du bokar hissen i förväg för flytt och bortforsling. Kontrollera detta med din BRF.</li>
  <li><strong>Södermalm: räkna med smala dörrar och trappor</strong> — Äldre byggnader på Södermalm har ofta trånga trapphus och smala dörröppningar. Mät dina möbler och dörröppningar innan du bokar.</li>
  <li><strong>Gamla stan: extremt begränsad åtkomst</strong> — Gator i Gamla stan är ofta så smala att skåpbilar inte kan parkera nära porten. Räkna med extra bäravstånd och tid.</li>
  <li><strong>Helger och högtider: begränsad tillgänglighet</strong> — Färre utförare är aktiva under helger och högtider, vilket kan innebära längre väntetid eller högre priser.</li>
  <li><strong>Parkeringstillstånd kan behövas</strong> — I centrala Stockholm kan det krävas parkeringstillstånd för lastning. Kontrollera med kommunen om det finns lastzon nära din port.</li>
</ul>
<p>Alla dessa saker slipper du tänka på med Ungdomskraft. Vi hanterar logistiken — du behöver bara öppna dörren.</p>`,
      },
      {
        heading: "Alternativ till Tiptapp i Stockholm",
        html: `<h3>Ungdomskraft</h3>
<p>Det främsta alternativet till Tiptapp i Stockholm är <strong>Ungdomskraft</strong>. Medan Tiptapp är en marknadsplats där privatpersoner tar uppdrag via en app, är Ungdomskraft ett <strong>tjänsteföretag med anställd personal</strong>. Skillnaden märks i varje del av upplevelsen.</p>
<p>Här är vad du får med Ungdomskraft:</p>
<ul>
  <li><strong>Fast pris utan överraskningar</strong> — Boka på <a href="/boka">ungdomskraft.com/boka</a> och se ditt pris direkt. Inga bud, inga extrakrav på plats, inga dolda avgifter.</li>
  <li><strong>Anställda och bakgrundskontrollerade medarbetare</strong> — Alla som jobbar hos Ungdomskraft är anställda med arbetsavtal, sjuklön och semester. Vi gör bakgrundskontroller på alla medarbetare. Du vet vem som kommer hem till dig.</li>
  <li><strong>Eget skåpbil ingår alltid</strong> — Inget gissande om vad som dyker upp. Vi kommer alltid med ett fullt utrustat skåpbil, redo för jobbet.</li>
  <li><strong>Trygg Hansa-försäkring</strong> — Fullständig ansvarsförsäkring via en av Sveriges största försäkringsbolag.</li>
  <li><strong>RUT-avdrag</strong> — Alltid tillgängligt, hanteras automatiskt på fakturan. Det gör Ungdomskraft ofta billigare totalt.</li>
  <li><strong>Dedikerad kontaktperson + telefonväxel</strong> — Du får en namngiven kontaktperson som du kan nå via telefon och chatt. Ring <a href="tel:0851708281">08-517 082 81</a> så når du oss direkt.</li>
  <li><strong>Svensk- och engelsktalande personal</strong> — Kommunikationen fungerar alltid, oavsett om du pratar svenska eller engelska.</li>
  <li><strong>Miljöansvar</strong> — Saker i gott skick doneras till Myrorna. Resten sorteras och lämnas på återvinningscentral.</li>
</ul>
<p>Ungdomskraft erbjuder flera tjänster i Stockholm: <a href="/bortforsling-stockholm">bortforsling i Stockholm</a>, <a href="/transporthjalp">transport</a>, <a href="/flytthjalp-stockholm">flytthjälp i Stockholm</a>, <a href="/barhjalp-flytt">bärhjälp vid flytt</a>, <a href="/tomma-dodsbo">tömning av dödsbo</a>, <a href="/bortforsling-vitvaror">bortforsling av vitvaror</a> och <a href="/mobelmontering">möbelmontering</a>. Oavsett vilken typ av hjälp du behöver finns det ett paket som passar.</p>
<img src="/guide-tiptapp-stockholm-van.jpg" alt="Ungdomskraft-teamet med skåpbil redo för uppdrag i Stockholm" style="margin-top:1.5rem;border-radius:0.75rem;width:100%" />
<h3>Andra alternativ</h3>
<p>Utöver Ungdomskraft finns det andra alternativ:</p>
<ul>
  <li><strong>Kör själv till återvinningscentralen</strong> — Om du har tillgång till bil med släpvagn eller skåpbil kan du köra dina saker själv till en av Stockholms återvinningscentraler. Det är gratis men kräver tid, fordon och fysisk ansträngning. Stockholms kommun har 13 återvinningscentraler.</li>
  <li><strong>Traditionellt flyttföretag</strong> — Etablerade flyttfirmor erbjuder professionell service men är ofta avsevärt dyrare, särskilt för mindre jobb som enstaka möbler eller bortforsling.</li>
</ul>
<p>För de allra flesta stockholmare som behöver hjälp med bortforsling, transport eller flytt är Ungdomskraft den bästa kombinationen av <strong>pris, trygghet och service</strong>.</p>`,
      },
      {
        heading: "Ungdomskraft eller Tiptapp – jämförelse",
        html: `<p>För att göra det enkelt att jämföra de två alternativen har vi sammanställt en översiktlig tabell med de viktigaste faktorerna:</p>
<table>
  <thead>
    <tr>
      <th></th>
      <th>Ungdomskraft</th>
      <th>Tiptapp</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td><strong>Modell</strong></td>
      <td>Tjänsteföretag med anställda</td>
      <td>Marknadsplats (gigekonomi)</td>
    </tr>
    <tr>
      <td><strong>Personal</strong></td>
      <td>Anställda, bakgrundskontrollerade</td>
      <td>Privatpersoner och enskilda firmor</td>
    </tr>
    <tr>
      <td><strong>Fordon</strong></td>
      <td>Eget skåpbil ingår alltid</td>
      <td>Varierar per utförare</td>
    </tr>
    <tr>
      <td><strong>RUT-avdrag</strong></td>
      <td>Alltid tillgängligt</td>
      <td>Inte tillgängligt</td>
    </tr>
    <tr>
      <td><strong>Kundtjänst</strong></td>
      <td>Kontaktperson + växel 08-517 082 81</td>
      <td>Support via app och e-post</td>
    </tr>
    <tr>
      <td><strong>Språk</strong></td>
      <td>Svenska och engelska</td>
      <td>Varierar per utförare</td>
    </tr>
    <tr>
      <td><strong>Miljöansvar</strong></td>
      <td>Myrorna-donation + återvinning</td>
      <td>Enligt Tiptapps egna riktlinjer</td>
    </tr>
  </tbody>
</table>
<p><strong>Välj Tiptapp om:</strong> Du vill ge bort en gratismöbel eller det gäller ett mycket litet och enkelt jobb där du inte behöver fast pris, RUT eller försäkring.</p>
<p><strong>Välj Ungdomskraft om:</strong> Du vill ha fast pris, anställd personal, eget skåpbil, Trygg Hansa-försäkring och RUT-avdrag. Det gäller särskilt bortforsling, transport, flytt, bärhjälp och bohagstömningar.</p>
<p>Läs vår <a href="/guide/ungdomskraft-vs-tiptapp">fullständiga jämförelse mellan Ungdomskraft och Tiptapp</a> för ännu mer detaljer om skillnaderna.</p>`,
      },
      {
        heading: "Vanliga frågor om Tiptapp i Stockholm",
        html: `<h3>Hur fungerar Tiptapp-appen?</h3>
<p>Du laddar ner appen, skapar ett konto, tar bilder på det du behöver hjälp med och beskriver uppdraget. Utförare kan sedan lägga bud eller acceptera ditt pris. När du godkänner en utförare betalar du via appen, och utföraren kommer vid överenskommen tid. Hela processen sker digitalt.</p>
<h3>Vad får jag med Ungdomskraft i Stockholm?</h3>
<p>Ungdomskraft skickar anställda, bakgrundskontrollerade medarbetare med ett eget skåpbil till ditt uppdrag i Stockholm. Du får fast pris i förväg på <a href="/boka">ungdomskraft.com/boka</a>, RUT-avdrag automatiskt på fakturan, fullständig ansvarsförsäkring via Trygg Hansa, och en namngiven kontaktperson som du kan ringa på vår växel <a href="tel:0851708281">08-517 082 81</a>.</p>
<h3>Kan man få RUT-avdrag med Tiptapp?</h3>
<p>RUT-avdrag kräver att utföraren är ett registrerat företag med F-skattsedel. Eftersom Tiptapps utförare oftast är privatpersoner är RUT-avdrag inte tillgängligt via plattformen. Med <a href="/bortforsling">Ungdomskraft</a> får du alltid RUT-avdrag — vilket halverar arbetskostnaden för flytt och bärhjälp eller ger 25 % rabatt på bortforsling till återanvändning.</p>
<h3>Hur snabbt kan jag få hjälp av Ungdomskraft?</h3>
<p>Ungdomskraft erbjuder bokning med fast tid via <a href="/boka">ungdomskraft.com/boka</a>, ofta med tillgänglighet samma dag. Vill du diskutera ett snabbare uppdrag? Ring vår växel på <a href="tel:0851708281">08-517 082 81</a>.</p>
<h3>Hur är försäkringsskyddet hos Ungdomskraft?</h3>
<p>Ungdomskraft är försäkrade via Trygg Hansa med fullständig ansvarsförsäkring som täcker hela uppdraget. Tiptapp erbjuder försäkring genom sin app.</p>
<h3>Vem tar ansvar om något går sönder?</h3>
<p>Hos Ungdomskraft tar företaget fullt ansvar via sin Trygg Hansa-försäkring. Du kontaktar din dedikerade kontaktperson och vi hanterar ärendet direkt.</p>
<h3>Får jag fast pris hos Ungdomskraft?</h3>
<p>Ja. Du ser ditt fasta pris direkt när du bokar på <a href="/boka">ungdomskraft.com/boka</a> — inga bud, inga överraskningar. Priset inkluderar anställd personal, eget skåpbil, försäkring och RUT-avdrag.</p>
<h3>Hur hanterar Ungdomskraft mina möbler efter upphämtning?</h3>
<p>Saker i gott skick doneras till Myrorna (Frälsningsarméns secondhand-kedja) så att de får ett nytt liv. Resten sorteras och lämnas på återvinningscentral enligt kommunens riktlinjer. Det är därför RUT-avdrag finns för bortforsling till återanvändning.</p>
<h3>Vilka alternativ till Tiptapp finns i Stockholm?</h3>
<p>Det främsta alternativet är <a href="/bortforsling">Ungdomskraft</a>, som erbjuder bortforsling, <a href="/transporthjalp">transport</a> och <a href="/flytthjalp">flytt</a> med anställd personal, eget skåpbil, Trygg Hansa-försäkring och RUT-avdrag. Andra alternativ inkluderar att köra själv till en av Stockholms 13 återvinningscentraler eller anlita ett traditionellt flyttföretag.</p>
<h3>Gör Ungdomskraft bakgrundskontroller på sina medarbetare?</h3>
<p>Ja. Ungdomskraft bakgrundskontrollerar alla anställda medarbetare innan de börjar jobba hos oss. Du vet vem du släpper in i ditt hem.</p>
<h3>Kan jag boka flytt i Stockholm med Ungdomskraft?</h3>
<p>Ja. Ungdomskraft skickar utbildade, anställda medarbetare med ett skåpbil och fullständig försäkring för <a href="/flytthjalp">flytt</a>. Boka på <a href="/boka">ungdomskraft.com/boka</a> — RUT-avdrag på flytt är 50 % av arbetskostnaden.</p>
<h3>Når jag Ungdomskraft per telefon?</h3>
<p>Ja. Ring vår växel på <a href="tel:0851708281"><strong>08-517 082 81</strong></a> så kommer du direkt till en namngiven kontaktperson. Du kan också chatta med oss eller boka direkt på <a href="/boka">ungdomskraft.com/boka</a>.</p>
<h3>Vad är skillnaden mellan Tiptapp och Ungdomskraft?</h3>
<p>Tiptapp är en marknadsplats där privatpersoner tar uppdrag via en app. Ungdomskraft är ett tjänsteföretag med anställda medarbetare, eget skåpbil, Trygg Hansa-försäkring och RUT-avdrag. Läs vår <a href="/guide/ungdomskraft-vs-tiptapp">fullständiga jämförelse</a> för alla detaljer.</p>
<h3>Kan man använda Tiptapp för att ge bort möbler gratis i Stockholm?</h3>
<p>Ja, Tiptapp har en funktion för att ge bort saker gratis. Någon hämtar och tar hand om sakerna utan kostnad. För enkla gratishämtningar av fungerande möbler och prylar kan det fungera bra.</p>`,
      },
      {
        heading: "Sammanfattning",
        html: `<p><strong>Tiptapp</strong> kan fungera för enkla gratishämtningar och mycket små jobb i Stockholm. För bortforsling, transport, flytt och bärhjälp — där du vill ha fast pris, anställd personal, försäkring och RUT-avdrag — väljer många <strong>Ungdomskraft</strong>.</p>
<p>Med Ungdomskraft får du:</p>
<ul>
  <li>Anställda, bakgrundskontrollerade medarbetare</li>
  <li>Eget skåpbil som alltid ingår</li>
  <li>Trygg Hansa-försäkring med fullständigt företagsansvar</li>
  <li>RUT-avdrag som ofta gör oss billigare totalt</li>
  <li>Fast pris i förväg på ungdomskraft.com/boka</li>
  <li>Dedikerad kontaktperson + växel <a href="tel:0851708281">08-517 082 81</a></li>
  <li>Svensk- och engelsktalande personal</li>
  <li>Miljöansvar med donation till Myrorna</li>
</ul>
<p><strong>Boka på under 2 minuter</strong> och se ditt pris direkt på <a href="/boka">ungdomskraft.com/boka</a>. Läs gärna vår <a href="/guide/ungdomskraft-vs-tiptapp">fullständiga jämförelse mellan Ungdomskraft och Tiptapp</a>.</p>
<p>Behöver du <a href="/bortforsling">bortforsling i Stockholm</a>? Vi finns här för dig — med fast pris, anställd personal och RUT-avdrag. Ring <a href="tel:0851708281"><strong>08-517 082 81</strong></a> om du vill prata med någon.</p>`,
      },
    ],
  },
  {
    slug: "tiptapp-goteborg",
    title:
      "Tiptapp Göteborg – Priser och bästa alternativet (2026)",
    excerpt:
      "Allt om Tiptapp i Göteborg: priser och hur det fungerar. Plus: varför Ungdomskraft erbjuder ett professionellt alternativ med fast pris, RUT-avdrag, anställd personal och dedikerad kontaktperson.",
    coverImage: "/guide-tiptapp-goteborg.jpg",
    coverImageAlt:
      "Tiptapp Göteborg – guide om priser och alternativ för bortforsling och transport",
    publishedAt: "2026-03-06",
    updatedAt: "2026-04-29",
    tags: ["göteborg", "bortforsling", "transport"],
    metaTitle:
      "Tiptapp Göteborg – Hur fungerar det, vad kostar det och finns det bättre alternativ?",
    metaDescription:
      "Allt om Tiptapp i Göteborg: priser och hur det fungerar. Plus: varför Ungdomskraft erbjuder fast pris, RUT-avdrag, anställd personal och kontaktperson.",
    faq: [
      {
        question: "Hur fungerar Tiptapp i Göteborg?",
        answer:
          "Tiptapp är en marknadsplats där du lägger ut ett uppdrag i appen och privatpersoner eller mindre företag kan acceptera det. Göteborg är Tiptapps näst största marknad. I ytterområden som Hisingen, Askim och Angered kan tillgängligheten variera.",
      },
      {
        question:
          "Vad kostar bortforsling av en soffa via Tiptapp i Göteborg?",
        answer:
          "Typiskt 300–700 kr för en enstaka möbel. Med Tiptapp vet du inte exakt vad du betalar förrän en utförare lägger bud. Med Ungdomskraft ser du det fasta priset direkt på ungdomskraft.com/boka — och skåpbil ingår alltid.",
      },
      {
        question: "Kan man använda RUT-avdrag med Tiptapp i Göteborg?",
        answer:
          "Nej. RUT-avdrag kräver ett registrerat företag med F-skattsedel. Tiptapps utförare är oftast privatpersoner och kan inte erbjuda RUT. Med Ungdomskraft halveras din arbetskostnad automatiskt via RUT — vilket ofta gör oss billigare totalt.",
      },
      {
        question: "Hur är försäkringsskyddet hos Ungdomskraft i Göteborg?",
        answer:
          "Ungdomskraft har fullständig ansvarsförsäkring via Trygg Hansa som täcker hela uppdraget. Tiptapp erbjuder försäkring genom sin app.",
      },
      {
        question: "Finns Ungdomskraft i hela Göteborg?",
        answer:
          "Ja. Vi är verksamma i hela Göteborgsområdet — Centrum, Majorna-Linné, Hisingen, Örgryte-Härlanda, Västra Göteborg, Askim-Frölunda-Högsbo, Lundby och Angered. Boka på ungdomskraft.com/boka och se direkt vad det kostar.",
      },
      {
        question:
          "Hur hanterar Ungdomskraft mina möbler efter upphämtning?",
        answer:
          "Saker i gott skick doneras till Myrorna i Göteborg så att de får ett nytt liv. Resten sorteras och lämnas på Göteborgs återvinningscentraler enligt kommunens riktlinjer.",
      },
      {
        question: "Kan jag boka flytt i Göteborg med Ungdomskraft?",
        answer:
          "Ja. Ungdomskraft skickar utbildade, anställda medarbetare med ett skåpbil och fullständig försäkring för flytt i hela Göteborg. Boka på ungdomskraft.com/boka — RUT-avdrag på flytt är 50 % av arbetskostnaden.",
      },
      {
        question: "Hur snabbt kan jag få hjälp i Göteborg?",
        answer:
          "Med Ungdomskraft bokar du en fast tid via ungdomskraft.com/boka, ofta med tillgänglighet samma dag. Vill du diskutera ett snabbare uppdrag? Ring vår växel på 08-517 082 81.",
      },
    ],
    content: [
      {
        heading:
          "Tiptapp Göteborg – Priser och bästa alternativet (2026)",
        html: `<p>Tiptapp är populärt i Göteborg — men vad är det egentligen, vad kostar det, och vilka alternativ finns? Den här guiden går igenom hur Tiptapp fungerar i Göteborg specifikt och presenterar Ungdomskraft som ett professionellt alternativ med fast pris, RUT-avdrag, anställd personal och dedikerad kontaktperson.</p>
<p>Vi är öppna med att vi är Ungdomskraft. Vårt mål här är att du ska kunna jämföra modellerna och välja det som passar ditt uppdrag bäst.</p>
<p>Bor du i Stockholm istället? Läs vår <a href="/guide/tiptapp-stockholm">Tiptapp Stockholm-guide</a>.</p>`,
      },
      {
        heading:
          "Vad är Tiptapp — och hur skiljer sig Göteborg från Stockholm?",
        html: `<p>Tiptapp är en app-baserad förmedlingsplattform som kopplar samman privatpersoner som behöver hjälp med <a href="/bortforsling">bortforsling</a> eller <a href="/transporthjalp">transport</a> med privatpersoner eller mindre företag som vill utföra jobbet. Tiptapp själva utför inga uppdrag.</p>
<p>Göteborg är Tiptapps näst största marknad efter Stockholm. Tillgängligheten kan variera mellan stadsdelarna — i ytterområden som Hisingen, Askim och Angered kan det ta längre tid att hitta en utförare än i innerstaden, särskilt under flyttintensiva sommarmånader.</p>
<p>Tiptapp tar provision på varje uppdrag; resten går till utföraren.</p>`,
      },
      {
        heading: "Hur fungerar Tiptapp i Göteborg steg för steg?",
        html: `<p>Processen ser likadan ut oavsett stad, men det finns lokala skillnader i hur smidigt det fungerar i praktiken.</p>
<ol>
  <li><strong>Ladda ner appen</strong> — finns på App Store och Google Play</li>
  <li><strong>Skapa ett konto</strong> — kräver e-post eller telefonnummer och ID-verifiering</li>
  <li><strong>Fotografera uppdraget</strong> — ta bilder från flera vinklar, inkludera trappor och passager</li>
  <li><strong>Beskriv uppdraget</strong> — våningsplan, hiss ja/nej, vad som ska göras</li>
  <li><strong>Sätt ett pris eller bjud in bud</strong> — du kan föreslå ett pris eller låta utförare buda</li>
  <li><strong>Publicera</strong> — uppdraget syns för utförare i Göteborgsområdet</li>
  <li><strong>Välj utförare</strong> — se profil och eventuella betyg, välj den du känner dig trygg med</li>
  <li><strong>Utföraren kommer</strong> — med sitt eget fordon vid avtalad tid</li>
  <li><strong>Betala via appen</strong> — aldrig kontant utanför plattformen</li>
</ol>
<p><strong>Göteborgsspecifikt att tänka på:</strong> Uppdrag i Majorna och Linnéstaden — med sina smala gaturum och äldre bebyggelse — tar längre tid än ett standarduppdrag i en modern förort. Räkna med detta när du tidsbokar.</p>`,
      },
      {
        heading: "Vad kostar Tiptapp i Göteborg?",
        html: `<p>Det finns ingen officiell prislista. Priserna sätts av marknaden — utförare budar eller accepterar det pris du föreslår. I Göteborg är det generella priset något lägre än i Stockholm, men skillnaden är marginell för de flesta uppdrag.</p>
<h3>RUT-avdrag — en av de största prisfaktorerna</h3>
<p>RUT-avdrag kräver att utföraren är ett registrerat företag med F-skattsedel. Eftersom Tiptapps utförare oftast är privatpersoner är <a href="/guide/vad-ar-rut-avdrag">RUT-avdrag</a> inte tillgängligt via plattformen — du betalar fullt pris.</p>
<p>Ett bortforslingsjobb med 3 000 kr i arbetskostnad blir <strong>2 250 kr efter RUT (25 % på bortforsling till återanvändning)</strong> hos Ungdomskraft — och då ingår skåpbil, anställd personal och försäkring i priset. Läs mer om <a href="/guide/rut-avdrag-bortforsling">RUT-avdrag på bortforsling</a>.</p>`,
      },
      {
        heading: "Göteborgsspecifika utmaningar — och hur Ungdomskraft hanterar dem",
        html: `<p>Göteborg har sin egen karaktär som ställer höga krav på den som hjälper dig hemma.</p>
<h3>Majorna och Linnéstaden — landshövdingehus och trånga trapphus</h3>
<p>Göteborg är känt för sina landshövdingehus — de unika trevåningsbyggnaderna med bottenvåning i tegel och de två övre våningarna i trä. De är vackra. De är också besvärliga att flytta i.</p>
<p>Smala trapphus, branta trappor och ofta avsaknad av hiss är standardutmaningen i stora delar av Majorna och Linnéstaden. Ungdomskrafts team är utbildade i lyftteknik, använder skyddsmaterial för trapphus och dörröppningar och har erfarenhet av just den här typen av äldre bebyggelse.</p>
<h3>Hisingen — avstånd och tillgänglighet</h3>
<p>Hisingen är Göteborgs folkrikaste stadsdel. Med Ungdomskraft når du hela Göteborg — Biskopsgården, Backa och Torslanda inkluderat — med fast pris i förväg.</p>
<h3>Askim, Hovås och södra Göteborg</h3>
<p>Vi täcker även de södra förorterna med samma fasta pris och samma anställda personal som i innerstaden. Boka på <a href="/boka">ungdomskraft.com/boka</a>.</p>
<h3>Västra Frölunda och Angered</h3>
<p>Behöver du hjälp med <a href="/bortforsling-goteborg">bortforsling</a> i Frölunda Torg-området eller i Angered? Ungdomskraft erbjuder samma professionella service oavsett stadsdel.</p>`,
      },
      {
        heading: "Vad får du med Ungdomskraft i Göteborg?",
        html: `<p>När du bokar Ungdomskraft i Göteborg kommer ett team av anställda, bakgrundskontrollerade medarbetare med ett eget skåpbil. Det här ingår alltid:</p>
<ul>
  <li><strong>Fast pris</strong> — Boka på <a href="/boka">ungdomskraft.com/boka</a> och se ditt pris direkt. Inga bud, inga överraskningar, inga extrakrav på plats.</li>
  <li><strong>Anställd personal</strong> — Alla våra medarbetare är anställda med arbetsavtal. Vi gör bakgrundskontroller på alla innan de börjar.</li>
  <li><strong>Eget skåpbil ingår alltid</strong> — Inget gissande om vad som dyker upp.</li>
  <li><strong>RUT-avdrag automatiskt på fakturan</strong> — 50 % på flytt och bärhjälp, 25 % på bortforsling till återanvändning.</li>
  <li><strong>Trygg Hansa-försäkring</strong> — Fullständig ansvarsförsäkring direkt via en av Sveriges största försäkringsbolag.</li>
  <li><strong>Telefonväxel</strong> — Ring <a href="tel:0851708281"><strong>08-517 082 81</strong></a> och prata direkt med en kontaktperson.</li>
  <li><strong>Svensk- och engelsktalande personal</strong> — Kommunikationen fungerar i trapphus, vid lastning och kring känsliga föremål.</li>
  <li><strong>Göteborgskännedom</strong> — Vi vet hur landshövdingehusen ser ut, hur BRF-reglerna fungerar och hur trafiken rör sig.</li>
  <li><strong>Miljöansvar</strong> — Saker i gott skick doneras till Myrorna i Göteborg. Resten sorteras och lämnas på kommunens återvinningscentraler.</li>
</ul>
<p>Ungdomskraft har <strong>4,9 på Trustpilot</strong> med över 500 recensioner.</p>
<img src="/guide-tiptapp-goteborg-team.png" alt="Ungdomskraft-teamet i Göteborg — anställda ungdomar redo för uppdrag" style="margin-top:1.5rem;border-radius:0.75rem;width:100%" />`,
      },
      {
        heading: "Försäkring och ansvar — så fungerar det hos Ungdomskraft",
        html: `<p>Att släppa in någon i sitt hem för att bära möbler innebär alltid en risk. Hos Ungdomskraft är ansvar tydligt reglerat.</p>
<h3>Trygg Hansa-försäkring + företagsansvar</h3>
<p>Ungdomskraft är försäkrade via <strong>Trygg Hansa</strong> med fullständig ansvarsförsäkring. Försäkringen täcker hela uppdraget — från första lyft till sista lossning. Om något skulle gå fel kontaktar du din dedikerade kontaktperson hos Ungdomskraft. Vi hanterar ärendet direkt — du behöver inte argumentera med en privatperson eller fylla i formulär hos ett tredjepartsföretag.</p>
<h3>Tiptapps försäkring</h3>
<p>Tiptapp erbjuder försäkring genom sin app.</p>
<p>Praktiskt exempel: skulle något hända ditt trägolv i lägenheten på Linnégatan tar Ungdomskraft som företag fullt ansvar via vår Trygg Hansa-försäkring. Du har en namngiven kontaktperson och vi hanterar hela ärendet.</p>`,
      },
      {
        heading: "Tänk på det här innan du bokar i Göteborg",
        html: `<h3>Allmänna råd</h3>
<ul>
  <li><strong>Ta bilder på allt</strong> — inklusive trappor, dörröppningar och eventuella svåra passager</li>
  <li><strong>Dokumentera uppdraget skriftligt</strong> — antal föremål, våning, hiss ja/nej</li>
  <li><strong>Kontrollera profil och betyg på marknadsplatser</strong> — välj utförare med många genomförda uppdrag</li>
  <li><strong>Bekräfta fordonstyp</strong> — en personbil räcker sällan för bortforsling av möbler</li>
  <li><strong>Boka hissen i din BRF</strong> — många bostadsrättsföreningar i Göteborg kräver förhandsbokning</li>
</ul>
<h3>Göteborgsspecifika råd</h3>
<ul>
  <li><strong>Mät dörröppningar i landshövdingehus</strong> — smala dörrkar i äldre göteborgshus är en vanlig orsak till skador vid flytt</li>
  <li><strong>Helger och högtider</strong> — boka i god tid, oavsett tjänst</li>
  <li><strong>Kontrollera parkeringsmöjligheter</strong> — centrala Göteborg har begränsad parkering för lastbilar. Kolla om det finns lastzon nära din port</li>
</ul>
<p>Med Ungdomskraft hanterar vi logistiken åt dig. Du öppnar dörren — vi sköter resten. Boka på <a href="/boka">ungdomskraft.com/boka</a> eller ring <a href="tel:0851708281">08-517 082 81</a>.</p>`,
      },
      {
        heading: "Ungdomskraft i Göteborg — ett professionellt alternativ",
        html: `<p>Ungdomskraft är verksamma i Göteborg med anställda medarbetare, egna skåpbilar och fast prissättning. Vi erbjuder <a href="/bortforsling-goteborg">bortforsling</a>, <a href="/transporthjalp">transport</a>, <a href="/flytthjalp-goteborg">flytt</a>, <a href="/mobelmontering">möbelmontering</a> och <a href="/barhjalp">bärhjälp</a> i hela Göteborgsområdet — inklusive Hisingen, södra Göteborg och ytterförorterna.</p>
<p>Det här är vad du alltid får med oss:</p>
<ul>
  <li><strong>Fast pris</strong> — boka på <a href="/boka">ungdomskraft.com/boka</a> och se priset direkt. Inga bud, inga extrakrav på plats</li>
  <li><strong>Anställda och bakgrundskontrollerade</strong> — alla våra medarbetare har arbetsavtal. Vi gör bakgrundskontroller innan de börjar</li>
  <li><strong>Eget skåpbil</strong> — vi kommer alltid med rätt fordon för jobbet. Inget gissande</li>
  <li><strong>Trygg Hansa-försäkring</strong> — fullständigt ansvar, inte en begränsad tredjepartslösning</li>
  <li><strong>RUT-avdrag (50 %)</strong> — alltid tillgängligt, hanteras automatiskt på fakturan</li>
  <li><strong>Göteborgskännedom</strong> — vi vet hur landshövdingehusen ser ut, vi vet hur BRF-reglerna fungerar, vi vet hur trafiken rör sig</li>
  <li><strong>Miljöansvar</strong> — saker i gott skick doneras till Myrorna i Göteborg. Resten lämnas på kommunens återvinningscentraler</li>
  <li><strong>Svenska och engelska</strong> — kommunikationen fungerar alltid</li>
</ul>
<p>Vi grundades 2014 med ett tydligt syfte: ge unga människor deras första jobb och göra det enkelt för privatpersoner och företag att få professionell hjälp. I Göteborg jobbar vi med samma modell — varje uppdrag du bokar ger en ung göteborgare arbetslivserfarenhet.</p>
<img src="/guide-tiptapp-goteborg-crew.png" alt="Ungdomskraft-medarbetare i Göteborg på väg till uppdrag" style="margin-top:1.5rem;border-radius:0.75rem;width:100%" />`,
      },
      {
        heading: "Ungdomskraft eller Tiptapp — jämförelse",
        html: `<table>
  <thead>
    <tr>
      <th></th>
      <th>Ungdomskraft</th>
      <th>Tiptapp</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td><strong>Modell</strong></td>
      <td>Tjänsteföretag med anställda</td>
      <td>Förmedlingsplattform (gigekonomi)</td>
    </tr>
    <tr>
      <td><strong>Personal</strong></td>
      <td>Anställda, bakgrundskontrollerade</td>
      <td>Privatpersoner och enskilda firmor</td>
    </tr>
    <tr>
      <td><strong>Fordon</strong></td>
      <td>Eget skåpbil ingår alltid</td>
      <td>Varierar per utförare</td>
    </tr>
    <tr>
      <td><strong>RUT-avdrag</strong></td>
      <td>Alltid tillgängligt</td>
      <td>Inte tillgängligt</td>
    </tr>
    <tr>
      <td><strong>Kundtjänst</strong></td>
      <td>Kontaktperson + växel 08-517 082 81</td>
      <td>Support via app och e-post</td>
    </tr>
    <tr>
      <td><strong>Tillgänglighet Göteborg</strong></td>
      <td>Hela staden inklusive ytterområden</td>
      <td>Bäst i innerstaden, varierar i ytterområden</td>
    </tr>
    <tr>
      <td><strong>Miljöansvar</strong></td>
      <td>Myrorna + återvinning</td>
      <td>Enligt Tiptapps egna riktlinjer</td>
    </tr>
  </tbody>
</table>
<p><strong>Välj Tiptapp om:</strong> Du vill ge bort möbler gratis eller det gäller ett mycket litet jobb där du inte behöver fast pris, RUT eller försäkring.</p>
<p><strong>Välj Ungdomskraft om:</strong> Du vill ha fast pris, anställd personal, försäkring via Trygg Hansa och RUT-avdrag. Det gäller särskilt bortforsling, flytt, transport och källar- eller vindsrensning i hela Göteborg.</p>`,
      },
      {
        heading: "Vanliga frågor om Tiptapp i Göteborg",
        html: `<h3>Hur fungerar Tiptapp i Göteborg?</h3>
<p>Tiptapp är en marknadsplats där du lägger ut ett uppdrag i appen och privatpersoner eller mindre företag kan acceptera det. Göteborg är Tiptapps näst största marknad. I ytterområden som Hisingen, Askim och Angered kan tillgängligheten variera.</p>
<h3>Vad kostar bortforsling av en soffa via Tiptapp i Göteborg?</h3>
<p>Typiskt 300–700 kr för en enstaka möbel. Med Tiptapp vet du inte exakt vad du betalar förrän en utförare lägger bud. Med Ungdomskraft ser du det fasta priset direkt på <a href="/boka">ungdomskraft.com/boka</a> — och skåpbil ingår alltid.</p>
<h3>Kan man använda RUT-avdrag med Tiptapp i Göteborg?</h3>
<p>Nej. RUT-avdrag kräver ett registrerat företag med F-skattsedel. Tiptapps utförare är oftast privatpersoner och kan inte erbjuda RUT. Med Ungdomskraft halveras din arbetskostnad automatiskt via RUT — vilket ofta gör oss billigare totalt.</p>
<h3>Hur är försäkringsskyddet hos Ungdomskraft i Göteborg?</h3>
<p>Ungdomskraft har fullständig ansvarsförsäkring via Trygg Hansa som täcker hela uppdraget. Tiptapp erbjuder försäkring genom sin app.</p>
<h3>Finns Ungdomskraft i hela Göteborg?</h3>
<p>Ja. Vi är verksamma i hela Göteborgsområdet — Centrum, Majorna-Linné, Hisingen, Örgryte-Härlanda, Västra Göteborg, Askim-Frölunda-Högsbo, Lundby och Angered. Boka på <a href="/boka">ungdomskraft.com/boka</a> och se direkt vad det kostar.</p>
<h3>Hur hanterar Ungdomskraft mina möbler efter upphämtning?</h3>
<p>Saker i gott skick doneras till Myrorna i Göteborg så att de får ett nytt liv. Resten sorteras och lämnas på Göteborgs återvinningscentraler enligt kommunens riktlinjer.</p>
<h3>Kan jag boka flytt i Göteborg med Ungdomskraft?</h3>
<p>Ja. Ungdomskraft skickar utbildade, anställda medarbetare med ett skåpbil och fullständig försäkring för <a href="/flytthjalp">flytt i Göteborg</a>. Boka på <a href="/boka">ungdomskraft.com/boka</a> — RUT-avdrag på flytt är 50 % av arbetskostnaden.</p>
<h3>Hur snabbt kan jag få hjälp i Göteborg?</h3>
<p>Med Ungdomskraft bokar du en fast tid via <a href="/boka">ungdomskraft.com/boka</a>, ofta med tillgänglighet samma dag. Vill du diskutera ett snabbare uppdrag? Ring vår växel på <a href="tel:0851708281">08-517 082 81</a>.</p>
<h3>Når jag Ungdomskraft per telefon?</h3>
<p>Ja. Ring vår växel på <a href="tel:0851708281"><strong>08-517 082 81</strong></a> så kommer du direkt till en namngiven kontaktperson.</p>`,
      },
      {
        heading: "Sammanfattning",
        html: `<p>Tiptapp kan fungera för att ge bort möbler gratis och för småjobb. För <a href="/bortforsling-mobler">bortforsling av möbler</a>, flytt, <a href="/tomma-dodsbo">tömning av dödsbo</a> och allt där du vill ha fast pris, RUT-avdrag, anställd personal och försäkring väljer många <strong>Ungdomskraft</strong>. Läs vår djupgående <a href="/guide/ungdomskraft-vs-tiptapp">jämförelse Ungdomskraft vs Tiptapp</a> och om <a href="/guide/rut-avdrag-flytt">RUT-avdrag på flytt</a>.</p>
<p>Med Ungdomskraft i Göteborg får du:</p>
<ul>
  <li>Anställda, bakgrundskontrollerade medarbetare med göteborgskännedom</li>
  <li>Eget skåpbil som alltid ingår</li>
  <li>Trygg Hansa-försäkring</li>
  <li>RUT-avdrag som ofta gör oss billigare</li>
  <li>Fast pris i förväg på ungdomskraft.com/boka</li>
  <li>Svensk- och engelsktalande personal</li>
  <li>Telefonväxel på 08-517 082 81</li>
  <li>Miljöansvar med Myrorna och återvinning</li>
</ul>
<p>Boka på <a href="/boka"><strong>ungdomskraft.com/boka</strong></a> och se ditt pris direkt.</p>
<p>Ring oss på <a href="tel:0851708281"><strong>08-517 082 81</strong></a> om du vill prata med någon.</p>`,
      },
    ],
  },
  {
    slug: "vad-ar-rut-avdrag",
    title: "Vad är RUT-avdrag? Så fungerar det - enkelt förklarat",
    excerpt:
      "RUT-avdrag ger dig skattereduktion på arbetskostnaden för hushållsnära tjänster — 50 % på flytt och städning, 25 % på bortforsling till återanvändning. Här förklarar vi hur det fungerar, vad som ingår och hur du använder avdraget i praktiken.",
    coverImage: "/guide-vad-ar-rut-avdrag-cover.png",
    coverImageAlt:
      "Ungdomskraft-medarbetare monterar möbler med borrmaskin – hushållsnära tjänst med RUT-avdrag",
    publishedAt: "2026-03-06",
    tags: ["rut-avdrag", "bortforsling", "flytt"],
    metaTitle:
      "Vad är RUT-avdrag? Så fungerar det – enkelt förklarat (2025)",
    metaDescription:
      "RUT-avdrag ger skattereduktion på arbetskostnaden — 50 % för flytt och städning, 25 % för bortforsling till återanvändning. Läs hur det fungerar och hur du använder det.",
    content: [
      {
        heading: "Vad är RUT-avdrag? Allt du behöver veta",
        html: `<p><strong>RUT-avdrag är en skattereduktion som innebär att staten betalar en del av arbetskostnaden när du anlitar ett företag för hushållsnära tjänster.</strong> För de flesta tjänster får du 50 % tillbaka — för bortforsling till återanvändning är avdraget 25 %. Företaget drar avdraget direkt från Skatteverket åt dig.</p>
<p>I praktiken betyder det att en <a href="/flytthjalp">flytt</a> som kostar 6 000 kr i arbetskostnad bara kostar dig 3 000 kr (50 % RUT). En <a href="/bortforsling">bortforsling</a> på 4 000 kr där sakerna går till återanvändning kostar dig 3 000 kr (25 % RUT). Du behöver inte fylla i någon blankett eller ansöka i förväg — allt sköts automatiskt av företaget du anlitar.</p>
<p>RUT står för <strong>Rengöring, Underhåll och Tvätt</strong>, men namnet är missvisande. Sedan avdraget infördes 2007 har listan över tjänster som berättigar till RUT vuxit rejält. Idag ingår bland annat flytt, bortforsling, möbelmontering, städning, trädgårdsarbete, barnpassning och mycket mer.</p>
<p>Det maximala avdraget är <strong>75 000 kr per person och år</strong>. Eftersom avdraget gäller per person kan ett sammanboende par utnyttja upp till 150 000 kr tillsammans. Det räcker till väldigt många timmar hushållsnära hjälp.</p>
<p>RUT-avdraget är en av de mest generösa skattereduktionerna i Sverige, men trots det använder många svenskar det aldrig. Ofta beror det på att man inte vet vilka tjänster som ingår, eller att man tror att det krävs komplicerad byråkrati. I den här guiden förklarar vi allt du behöver veta — från grunderna till hur du rent praktiskt använder avdraget nästa gång du bokar en tjänst.</p>`,
      },
      {
        heading: "Vad betyder RUT?",
        html: `<p>RUT är en förkortning av <strong>Rengöring, Underhåll och Tvätt</strong>. Namnet kommer från de tre huvudkategorierna av hushållstjänster som avdraget ursprungligen var tänkt att täcka när det infördes den 1 juli 2007.</p>
<p>Syftet med RUT-avdraget var tvåfaldigt. För det första ville riksdagen <strong>bekämpa svartarbete</strong> inom hushållssektorn. Innan RUT infördes var det vanligt att privatpersoner anlitade städhjälp, flytthjälp eller trädgårdsarbetare utan att skatt betalades. Genom att subventionera hälften av kostnaden blev det plötsligt prisvärt att välja en seriös firma med F-skattsedel.</p>
<p>För det andra ville man <strong>göra vardagslivet enklare</strong> för svenska hushåll. Tanken var att om fler kunde få hjälp med tunga sysslor — flytt, bortforsling, städning, trädgårdsarbete — så skulle människor få mer tid till arbete, familj och fritid.</p>
<p>Sedan 2007 har RUT-avdraget utvidgats i flera omgångar. Här är de viktigaste milstolparna:</p>
<ul>
  <li><strong>2007</strong> — RUT-avdraget införs. Täcker städning, tvätt, trädgårdsskötsel, barnpassning och enklare hushållsarbete.</li>
  <li><strong>2009</strong> — Taket höjs och fler tjänster läggs till, bland annat snöröjning.</li>
  <li><strong>2016</strong> — Flytt- och bortforslingstjänster läggs till. Nu kan du använda <a href="/guide/rut-avdrag-flytt">RUT-avdrag på flytt</a> och <a href="/guide/rut-avdrag-bortforsling">RUT-avdrag på bortforsling</a>.</li>
  <li><strong>2021</strong> — Möbelmontering, tvätt vid tvättinrättning och enklare reparationer av vitvaror och IT-utrustning läggs till.</li>
  <li><strong>2024</strong> — Taket höjs till 75 000 kr per person och år.</li>
</ul>
<p>Idag är RUT-avdraget alltså betydligt bredare än vad förkortningen antyder. Men namnet lever kvar som en igenkännbar term för alla skattesubventionerade hushållstjänster i Sverige.</p>
<img src="/guide-ungdomskraft-team-transport.jpg" alt="Ungdomskraft-teamet utför transport med RUT-avdrag" style="margin-top:1.5rem;border-radius:0.75rem;width:100%" />
<p><strong>RUT vs ROT — vad är skillnaden?</strong></p>
<p>RUT och ROT är två separata skatteavdrag som ofta blandas ihop. RUT gäller <strong>hushållsnära tjänster</strong> — städning, flytt, bortforsling, möbelmontering och liknande. ROT gäller <strong>reparation, ombyggnad och tillbyggnad</strong> av bostaden — alltså hantverksarbete som målning, VVS, elarbete och renovering.</p>
<p>RUT ger 50 % och ROT ger 30 % reduktion på arbetskostnaden. Men de har separata tak: 75 000 kr för RUT och 50 000 kr för ROT per person och år (för de flesta). De kan användas samtidigt, men arbetet måste utföras av ett företag med F-skattsedel. Materialkostnader ger aldrig avdrag — varken för RUT eller ROT.</p>`,
      },
      {
        heading: "Hur mycket är RUT-avdraget?",
        html: `<p>RUT-avdraget ger dig en skattereduktion på arbetskostnaden. För de flesta hushållsnära tjänster — som flytt, städning och möbelmontering — är avdraget <strong>50 %</strong>. För bortforsling som går till återanvändning är avdraget <strong>25 %</strong>. Observera att avdraget bara gäller arbetskostnaden, inte material eller varor.</p>
<p>Det maximala beloppet du kan dra av per kalenderår är:</p>
<ul>
  <li><strong>75 000 kr per person</strong> (gäller från 2024)</li>
  <li>Sammanboende par kan alltså använda upp till <strong>150 000 kr</strong> tillsammans</li>
  <li>Du måste ha tillräckligt med skatt att dra av mot — avdraget kan aldrig ge dig pengar tillbaka, bara minska din skatt</li>
</ul>
<p>Här är ett par konkreta exempel som visar vad du faktiskt betalar:</p>
<table style="width:100%;border-collapse:collapse;margin:1.5rem 0">
  <thead>
    <tr style="background:#2a4a63;color:white">
      <th style="padding:0.75rem 1rem;text-align:left;border-radius:0.5rem 0 0 0">Tjänst</th>
      <th style="padding:0.75rem 1rem;text-align:right">Arbetskostnad</th>
      <th style="padding:0.75rem 1rem;text-align:right">RUT-avdrag</th>
      <th style="padding:0.75rem 1rem;text-align:right;border-radius:0 0.5rem 0 0">Du betalar</th>
    </tr>
  </thead>
  <tbody>
    <tr style="background:#f8f9fa">
      <td style="padding:0.75rem 1rem;border-bottom:1px solid #e9ecef">Bortforsling av möbler (till återanvändning, 25 %)</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef">2 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;color:#2a8c4a">−500 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;font-weight:700">1 500 kr</td>
    </tr>
    <tr>
      <td style="padding:0.75rem 1rem;border-bottom:1px solid #e9ecef">Flytt (halvdag)</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef">6 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;color:#2a8c4a">−3 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;font-weight:700">3 000 kr</td>
    </tr>
    <tr style="background:#f8f9fa">
      <td style="padding:0.75rem 1rem;border-bottom:1px solid #e9ecef">Heldag flytt + bortforsling</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef">10 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;color:#2a8c4a">−5 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;font-weight:700">5 000 kr</td>
    </tr>
    <tr>
      <td style="padding:0.75rem 1rem">Möbelmontering (IKEA-kök)</td>
      <td style="padding:0.75rem 1rem;text-align:right">14 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;color:#2a8c4a">−7 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;font-weight:700">7 000 kr</td>
    </tr>
  </tbody>
</table>
<p>Eftersom det maximala avdraget är 75 000 kr per person innebär det att du kan köpa hushållsnära tjänster för upp till <strong>150 000 kr per år</strong> och fortfarande få halva beloppet tillbaka via skattereduktionen. De allra flesta hushåll kommer aldrig i närheten av taket. Vill du räkna på ditt eget utrymme? Prova vår <a href="/guide/kalkyl-rut-rot">RUT- och ROT-kalkylator</a>.</p>
<p><strong>Särskilda regler för pensionärer</strong></p>
<p>Om du är <a href="/guide/rut-avdrag-pensionar">pensionär</a> och född 1957 eller tidigare gäller ett <strong>gemensamt tak på 75 000 kr för RUT och ROT tillsammans</strong>. Det betyder att om du har använt 30 000 kr i ROT-avdrag har du 45 000 kr kvar i RUT. För övriga (födda 1958 eller senare) är taken separata: 75 000 kr RUT + 50 000 kr ROT.</p>
<p><strong>Viktigt att komma ihåg:</strong> RUT-avdraget är en skattereduktion, inte ett bidrag. Det innebär att du måste ha betalat tillräckligt med skatt under året för att kunna utnyttja avdraget fullt ut. Om du exempelvis har 40 000 kr i slutlig skatt kan du maximalt få 40 000 kr i RUT-avdrag, även om taket är 75 000 kr. De allra flesta som arbetar eller har pension betalar dock mer än 75 000 kr i skatt per år, så i praktiken är det sällan ett problem.</p>`,
      },
      {
        heading: "Vad ingår i RUT-avdraget?",
        html: `<p>Listan över tjänster som ger rätt till RUT-avdrag har vuxit stadigt sedan 2007. Här är en komplett översikt över allt som ingår idag:</p>
<p><strong>Tjänster som ger RUT-avdrag:</strong></p>
<ul>
  <li><strong><a href="/flytthjalp">Flytt och flytthjälp</a></strong> — Packning, bärning, lastning, transport till ny bostad. Gäller privatpersoners bohag, inte företagsflytt.</li>
  <li><strong><a href="/bortforsling">Bortforsling</a></strong> — Hämtning och borttransport av möbler, skräp, grovsopor och annat du vill bli av med. Inkluderar <a href="/bortforsling-mobler">bortforsling av möbler</a>. Läs mer i vår <a href="/guide/rut-avdrag-bortforsling">guide om RUT på bortforsling</a>.</li>
  <li><strong><a href="/mobelmontering">Möbelmontering</a></strong> — Montering och uppsättning av möbler, till exempel <a href="/hemleverans-ikea">IKEA-möbler</a>, hyllsystem och garderober. Läs vår <a href="/guide/rut-avdrag-mobelmontering">guide om RUT på möbelmontering</a>.</li>
  <li><strong>Städning</strong> — Hemstädning, storstädning, flyttstädning, fönsterputs (inomhus).</li>
  <li><strong>Trädgårdsarbete</strong> — Gräsklippning, häckklippning, ogräsrensning, beskärning, plantering, snöröjning.</li>
  <li><strong>Barnpassning</strong> — Barnvakt i hemmet, hämtning/lämning på förskola och skola.</li>
  <li><strong>Tvätt och strykning</strong> — Tvätt, mangling och strykning av kläder och textilier.</li>
  <li><strong><a href="/tomma-dodsbo">Dödsbo och tömning</a></strong> — <a href="/tomma-kallare">Tömning av källare</a>, förråd och hela bostäder. Läs vår <a href="/guide/rut-avdrag-dodsbostadning">guide om RUT på dödsbostädning</a>.</li>
  <li><strong><a href="/transporthjalp">Transporthjälp</a></strong> — Hjälp med transport av möbler, varor och bohag.</li>
  <li><strong>Enklare tillsyn av bostad</strong> — Tillsyn av fritidshus, vattning av blommor, hämtning av post.</li>
  <li><strong>IT-tjänster</strong> — Installation av dator, router, smarta hem-system och liknande i hemmet.</li>
  <li><strong>Reparation av vitvaror</strong> — Enklare reparation av tvättmaskin, diskmaskin, kyl/frys i hemmet.</li>
  <li><strong>Kläd- och textilvård</strong> — Skrädderiarbete, lagning av kläder.</li>
  <li><strong>Personlig omsorg</strong> — Enklare tillsyn och omsorg (ej sjukvård).</li>
</ul>
<img src="/guide-ungdomskraft-worker-furniture.jpg" alt="Ungdomskraft-medarbetare monterar möbler - tjänst som ger RUT-avdrag" style="margin-top:1.5rem;border-radius:0.75rem;width:100%" />
<p><strong>Vad ger INTE RUT-avdrag?</strong></p>
<ul>
  <li><strong>Material och varor</strong> — Du får aldrig avdrag för material. Om du köper en garderob och betalar för montering ger monteringsarbetet RUT, men själva garderoben ger inget avdrag.</li>
  <li><strong>Renovering och ombyggnad</strong> — Målning, tapetsering, VVS-arbete, elinstallation och renovering av kök eller badrum faller under ROT-avdraget, inte RUT.</li>
  <li><strong>Nybyggnation</strong> — Arbete på en bostad som inte är färdigställd ger varken RUT eller ROT.</li>
  <li><strong>Företagstjänster</strong> — RUT gäller bara privatpersoner. Företag kan inte använda det.</li>
  <li><strong>Transport av köpta varor</strong> — Ren varutransport (t.ex. hemleverans från en butik) ger inte RUT. Men om transporten är en del av en flytt eller bortforsling ingår den.</li>
</ul>
<p>En bra tumregel: om arbetet utförs <strong>i eller vid din bostad</strong> och handlar om <strong>hushållssysslor</strong> (inte byggnation) — då ger det sannolikt RUT-avdrag. Är du osäker? Fråga företaget du anlitar eller kolla Skatteverkets lista.</p>`,
      },
      {
        heading: "Vem har rätt till RUT-avdrag?",
        html: `<p>RUT-avdraget är tillgängligt för alla som betalar skatt i Sverige. Det spelar ingen roll om du bor i villa, bostadsrätt, hyresrätt eller fritidshus — alla boendeformer kvalificerar. Här är de exakta kraven:</p>
<p><strong>Du har rätt till RUT-avdrag om du:</strong></p>
<ul>
  <li><strong>Är obegränsat skattskyldig i Sverige</strong> — Det vill säga att du har din bostad eller stadigvarande vistelse i Sverige. De flesta som bor och arbetar i Sverige uppfyller detta automatiskt.</li>
  <li><strong>Har tillräckligt med skatt att dra av mot</strong> — RUT-avdraget är en skattereduktion. Du måste ha betalat tillräckligt med inkomstskatt under året. De allra flesta som arbetar, är pensionärer eller har annan beskattad inkomst uppfyller detta.</li>
  <li><strong>Anlitar ett företag med F-skattsedel</strong> — Företaget som utför arbetet måste ha F-skattsedel. Du kan inte använda RUT-avdrag om du betalar en privatperson.</li>
</ul>
<p><strong>Boendeform spelar ingen roll:</strong></p>
<ul>
  <li><strong>Hyresrätt</strong> — Ja, du kan använda RUT-avdrag även om du hyr din bostad. Exempelvis flytt, städning och bortforsling.</li>
  <li><strong>Bostadsrätt</strong> — Ja, full rätt till RUT. Även ROT-avdrag gäller för bostadsrätter.</li>
  <li><strong>Villa / radhus</strong> — Ja, alla RUT-tjänster gäller. Plus trädgårdsarbete som gräsklippning och häckklippning.</li>
  <li><strong>Fritidshus</strong> — Ja, RUT gäller även för ditt fritidshus i Sverige. Tillsyn, städning, trädgårdsarbete och snöröjning ger alla avdrag.</li>
</ul>
<p><strong>Vem kan INTE använda RUT?</strong></p>
<ul>
  <li><strong>Företag och organisationer</strong> — RUT gäller bara privatpersoner. Ett aktiebolag eller en förening kan inte utnyttja RUT-avdrag.</li>
  <li><strong>Personar utan svensk skatt</strong> — Om du inte betalar skatt i Sverige kan du inte använda avdraget. Turister och utländska medborgare utan svensk skattsedel är alltså undantagna.</li>
  <li><strong>Om du saknar personnummer</strong> — Företaget behöver ditt personnummer för att begära utbetalning från Skatteverket.</li>
</ul>
<p>I praktiken är det väldigt enkelt: om du bor i Sverige, har ett personnummer och betalar skatt — då kan du använda RUT-avdrag. Det är inte krångligare än så.</p>`,
      },
      {
        heading: "Hur använder man RUT-avdraget i praktiken?",
        html: `<p>Det bästa med RUT-avdraget är att du knappt behöver göra någonting. Det finns ingen ansökan att skicka in och inga blanketter att fylla i. Hela processen sköts av företaget du anlitar. Så här fungerar det steg för steg:</p>
<p><strong>Steg 1: Boka en tjänst</strong></p>
<p>Välj ett företag som erbjuder RUT-avdrag och boka den tjänst du behöver. Exempelvis kan du boka <a href="/bortforsling">bortforsling</a>, <a href="/flytthjalp">flytt</a> eller <a href="/mobelmontering">möbelmontering</a> via <a href="/boka">ungdomskraft.com/boka</a>.</p>
<p><strong>Steg 2: Uppge ditt personnummer</strong></p>
<p>Företaget behöver ditt personnummer för att kunna begära utbetalning från Skatteverket. Det är det enda du behöver göra utöver att boka.</p>
<p><strong>Steg 3: Arbetet utförs</strong></p>
<p>Företaget utför arbetet i din bostad — vare sig det gäller flytt, bortforsling, städning eller något annat som berättigar till RUT.</p>
<p><strong>Steg 4: Du betalar halva priset</strong></p>
<p>På fakturan ser du det totala beloppet, RUT-avdraget och vad du faktiskt ska betala. Du betalar bara 50 % av arbetskostnaden. Företaget skickar sedan en begäran till Skatteverket om att få resterande 50 % utbetalt.</p>
<p><strong>Steg 5: Klart!</strong></p>
<p>Skatteverket betalar ut pengarna direkt till företaget. Du behöver inte göra mer. Avdraget syns automatiskt i din deklaration nästa år, men du har redan fått fördelen direkt på fakturan.</p>
<p><strong>Så kontrollerar du ditt kvarvarande RUT-utrymme</strong></p>
<p>Om du vill se hur mycket RUT du har kvar att använda under året kan du logga in på <strong>Skatteverkets "Mina sidor"</strong>. Där ser du exakt hur mycket som har begärts i ditt namn och hur mycket utrymme som återstår upp till taket på 75 000 kr.</p>
<p><strong>Viktigt: Välj rätt företag</strong></p>
<p>För att RUT-avdraget ska fungera måste företaget du anlitar ha <strong>F-skattsedel</strong> (F-skatt). Det innebär att företaget är registrerat som näringsidkare hos Skatteverket och ansvarar för att betala arbetsgivaravgifter och skatt. Anlitar du någon utan F-skatt — till exempel en privatperson via en marknadsplats som Tiptapp — kan du <strong>inte</strong> använda RUT-avdrag.</p>
<p>Du kan kontrollera om ett företag har F-skatt på Skatteverkets webbplats eller helt enkelt fråga företaget. Seriösa aktörer som <a href="/boka">Ungdomskraft</a> hanterar allt automatiskt och anger alltid RUT-avdrag tydligt i sitt erbjudande.</p>`,
      },
      {
        heading:
          "RUT-avdrag på flytt och bortforsling - så fungerar det med Ungdomskraft",
        html: `<p>Ungdomskraft erbjuder flera tjänster som alla kvalificerar för RUT-avdrag. Eftersom vi är ett registrerat företag med F-skattsedel och anställda medarbetare hanterar vi avdraget automatiskt — du betalar bara hälften av arbetskostnaden direkt på fakturan.</p>
<p><strong>Tjänster hos Ungdomskraft som ger RUT-avdrag:</strong></p>
<ul>
  <li><strong><a href="/flytthjalp">Flytt och flytthjälp</a></strong> — Packning, bärning i trappor, lastning, transport till ny adress. Finns i <a href="/flytthjalp-stockholm">Stockholm</a> och <a href="/flytthjalp-goteborg">Göteborg</a>.</li>
  <li><strong><a href="/bortforsling">Bortforsling</a></strong> — Hämtning av möbler, skräp, grovsopor och annat du vill bli av med. Finns i <a href="/bortforsling-stockholm">Stockholm</a> och <a href="/bortforsling-goteborg">Göteborg</a>.</li>
  <li><strong><a href="/bortforsling-mobler">Bortforsling av möbler</a></strong> — Soffor, sängar, garderober, matbord och annat du behöver forsla bort.</li>
  <li><strong><a href="/mobelmontering">Möbelmontering</a></strong> — Montering av IKEA-möbler, hyllsystem, garderober och annat.</li>
  <li><strong><a href="/barhjalp">Bärhjälp</a></strong> — Hjälp att bära tunga föremål i trappor, genom trånga utrymmen eller till och från fordon.</li>
</ul>
<p><strong>Konkret exempel: Så mycket sparar du</strong></p>
<p>Säg att du bokar en bortforsling av möbler från din lägenhet. Uppdraget kostar 5 000 kr i arbetskostnad. Med RUT-avdrag betalar du bara <strong>2 500 kr</strong>. De andra 2 500 kr begär Ungdomskraft direkt från Skatteverket. Du behöver inte lyfta ett finger utöver att uppge ditt personnummer.</p>
<p>Samma sak gäller för en flytt: om arbetskostnaden är 8 000 kr betalar du <strong>4 000 kr</strong>. Resten hanteras automatiskt.</p>
<p><strong>Varför Tiptapp inte erbjuder RUT-avdrag</strong></p>
<p>En vanlig fråga vi får är om man kan få RUT-avdrag via <strong>Tiptapp</strong>. Svaret är i de flesta fall <strong>nej</strong>. Tiptapp är en marknadsplats som förmedlar uppdrag till privatpersoner och gigarbetare — inte ett företag som utför arbetet med anställda. Privatpersoner har inte F-skattsedel, och utan F-skatt finns ingen möjlighet till RUT-avdrag.</p>
<p>Det innebär att ett jobb som kostar 3 000 kr via Tiptapp faktiskt kan bli <strong>dyrare</strong> än samma jobb hos Ungdomskraft — eftersom vårt pris halveras med RUT. Om Ungdomskrafts pris före RUT är 4 000 kr betalar du 2 000 kr, medan Tiptapps pris utan RUT förblir 3 000 kr. Läs vår fullständiga <a href="/guide/ungdomskraft-vs-tiptapp">jämförelse mellan Ungdomskraft och Tiptapp</a> för mer detaljer.</p>
<p><strong>Så bokar du Ungdomskraft med RUT-avdrag</strong></p>
<p>Det tar under 2 minuter:</p>
<ol>
  <li>Gå till <a href="/boka">ungdomskraft.com/boka</a></li>
  <li>Välj tjänst (flytt, bortforsling, möbelmontering eller bärhjälp)</li>
  <li>Ange detaljer (adress, våning, vad som ska göras)</li>
  <li>Se ditt fasta pris — redan med RUT-avdraget inräknat</li>
  <li>Bekräfta bokningen — klart!</li>
</ol>
<p>Vi kommer med eget skåpbil, anställda medarbetare och fullständig försäkring via Trygg Hansa. Och du betalar bara halva priset tack vare RUT.</p>
<p style="margin-top:2rem;display:flex;flex-wrap:wrap;gap:1rem">
  <a href="/boka" style="display:inline-block;background:#f89544;color:#fff;font-weight:700;padding:0.9rem 2rem;border-radius:0.5rem;text-decoration:none;font-size:1.05rem">Boka med RUT-avdrag — fast pris direkt</a>
</p>
<p style="margin-top:1.5rem">Letar du efter mer information? Här är våra mest populära sidor:</p>
<ul>
  <li><a href="/bortforsling">Bortforsling</a> — allt om våra bortforslingstjänster</li>
  <li><a href="/flytthjalp">Flytt och flytthjälp</a> — professionell flytt med fast pris</li>
  <li><a href="/mobelmontering">Möbelmontering</a> — IKEA-möbler, garderober och mer</li>
  <li><a href="/bortforsling-mobler">Bortforsling av möbler</a> — soffor, sängar, skåp</li>
  <li><a href="/guide/ungdomskraft-vs-tiptapp">Ungdomskraft vs Tiptapp</a> — fullständig jämförelse</li>
</ul>`,
      },
    ],
    faq: [
      {
        question: "Vad är RUT-avdrag?",
        answer:
          "RUT-avdrag är en skattereduktion som innebär att staten betalar en del av arbetskostnaden när du anlitar ett företag för hushållsnära tjänster som flytt, städning, möbelmontering och trädgårdsarbete (50 %) eller bortforsling till återanvändning (25 %). Företaget begär avdraget direkt från Skatteverket åt dig.",
      },
      {
        question: "Hur mycket är RUT-avdraget?",
        answer:
          "Du får 50 % avdrag på arbetskostnaden, upp till ett tak på 75 000 kr per person och år. Sammanboende par kan alltså utnyttja upp till 150 000 kr tillsammans. Avdraget gäller bara arbetskostnad — inte material eller varor.",
      },
      {
        question: "Vilka tjänster ingår i RUT-avdraget?",
        answer:
          "RUT-avdraget täcker bland annat flytt och flytthjälp, bortforsling av möbler och skräp, möbelmontering, städning, trädgårdsarbete, barnpassning, tvätt och strykning, enklare IT-tjänster, reparation av vitvaror och tillsyn av bostad. Listan har utvidgats flera gånger sedan avdraget infördes 2007.",
      },
      {
        question: "Vem kan få RUT-avdrag?",
        answer:
          "Alla privatpersoner som betalar skatt i Sverige kan få RUT-avdrag. Det gäller oavsett om du bor i hyresrätt, bostadsrätt, villa eller fritidshus. Företag och organisationer kan inte använda RUT-avdrag. Du behöver ett personnummer och måste anlita ett företag med F-skattsedel.",
      },
      {
        question: "Hur ansöker man om RUT-avdrag?",
        answer:
          "Du behöver inte ansöka alls. Företaget du anlitar hanterar allt automatiskt. Du uppger ditt personnummer, betalar halva fakturan, och företaget begär resterande 50 % direkt från Skatteverket. Avdraget syns sedan i din deklaration.",
      },
      {
        question: "Vad är skillnaden mellan RUT och ROT?",
        answer:
          "RUT gäller hushållsnära tjänster som flytt, städning, möbelmontering och trädgårdsarbete (50 % avdrag) samt bortforsling till återanvändning (25 % avdrag). ROT gäller reparation, ombyggnad och tillbyggnad av bostaden — alltså hantverksarbete som målning, VVS, elarbete och renovering (30 % avdrag). RUT och ROT har separata tak.",
      },
      {
        question: "Får man RUT-avdrag på flytt?",
        answer:
          "Ja, flytt och flytthjälp ger RUT-avdrag sedan 2016. Det gäller packning, bärning, lastning och transport av ditt bohag till en ny bostad. Med Ungdomskraft betalar du bara halva arbetskostnaden — vi hanterar avdraget automatiskt på fakturan.",
      },
      {
        question: "Får man RUT-avdrag på bortforsling?",
        answer:
          "Bortforsling kan ge RUT-avdrag, men bara om sakerna lämnas till återanvändning (t.ex. Myrorna). Avdraget är 25 % av arbetskostnaden. Om sakerna istället måste slängas på tippen gäller inget avdrag. Ungdomskraft kör alltid förbi återanvändning först för att maximera din besparing.",
      },
      {
        question: "Får man RUT-avdrag med Tiptapp?",
        answer:
          "I de flesta fall nej. Tiptapp är en marknadsplats som förmedlar uppdrag till privatpersoner och gigarbetare. Privatpersoner har inte F-skattsedel, och utan F-skatt finns ingen möjlighet till RUT-avdrag. Vill du utnyttja RUT behöver du anlita ett företag som Ungdomskraft som har F-skatt och anställda medarbetare.",
      },
      {
        question: "Får man RUT-avdrag med Ungdomskraft?",
        answer:
          "Ja, alla Ungdomskrafts tjänster kvalificerar för RUT-avdrag. Vi har F-skattsedel och hanterar avdraget automatiskt. Du betalar bara 50 % av arbetskostnaden direkt på fakturan — vi begär resten från Skatteverket åt dig.",
      },
      {
        question: "Hur vet jag hur mycket RUT jag har kvar?",
        answer:
          "Du kan logga in på Skatteverkets 'Mina sidor' för att se exakt hur mycket RUT-avdrag som har begärts i ditt namn under året och hur mycket utrymme som återstår. Taket är 75 000 kr per person och år.",
      },
      {
        question: "Kan man få RUT-avdrag för möbelmontering?",
        answer:
          "Ja, möbelmontering ger RUT-avdrag sedan 2021. Det gäller montering av IKEA-möbler, garderober, hyllsystem och liknande. Med Ungdomskraft betalar du halva priset — vi hanterar RUT-avdraget på fakturan.",
      },
      {
        question: "Går det att kombinera RUT och ROT samma år?",
        answer:
          "Ja, RUT och ROT har separata tak för de flesta. Du kan använda upp till 75 000 kr i RUT-avdrag och 50 000 kr i ROT-avdrag under samma kalenderår. Undantaget är pensionärer födda 1957 eller tidigare, som har ett gemensamt tak på 75 000 kr för RUT och ROT tillsammans.",
      },
      {
        question: "Måste man äga sin bostad för att få RUT?",
        answer:
          "Nej, du kan använda RUT-avdrag oavsett boendeform. Det gäller i hyresrätt, bostadsrätt, villa, radhus och fritidshus. Även om du hyr din bostad kan du använda RUT för exempelvis flytt, bortforsling och städning.",
      },
      {
        question: "Får pensionärer högre RUT-avdrag?",
        answer:
          "Pensionärer får inte ett högre procentavdrag — det är fortfarande 50 % av arbetskostnaden. Däremot har pensionärer födda 1957 eller tidigare ett gemensamt tak på 75 000 kr för RUT och ROT, medan yngre har separata tak (75 000 kr RUT + 50 000 kr ROT).",
      },
      {
        question:
          "Vad händer om man använder mer RUT än man har rätt till?",
        answer:
          "Företaget kontrollerar ditt tillgängliga utrymme hos Skatteverket innan de begär utbetalning. Om du har nått taket kommer företaget att meddela dig och du betalar hela beloppet istället. Du kan inte 'av misstag' dra av för mycket — systemet har inbyggda kontroller.",
      },
    ],
  },
  {
    slug: "rut-avdrag-flytt",
    title: "RUT-avdrag på flytt – Så får du halva priset på flytthjälp",
    excerpt:
      "Du kan använda RUT-avdrag på flytt och betala halva arbetskostnaden. Här förklarar vi exakt vad som ingår, hur mycket du sparar med räkneexempel och hur du bokar flytt med RUT-avdrag direkt inräknat hos Ungdomskraft.",
    coverImage: "/guide-ungdomskraft-team-transport.jpg",
    coverImageAlt:
      "Ungdomskrafts team lastar möbler i skåpbil under en flytt med RUT-avdrag",
    publishedAt: "2026-03-06",
    tags: ["flytt", "rut-avdrag"],
    metaTitle:
      "RUT-avdrag på flytt – Så får du halva priset på flytthjälp (2025)",
    metaDescription:
      "Du kan använda RUT-avdrag på flytt och betala halva arbetskostnaden. Läs hur det fungerar, vad som ingår och boka direkt med RUT inräknat.",
    content: [
      {
        heading: "RUT-avdrag på flytt – så fungerar det och vad kostar det?",
        html: `<p><strong>Ja, flytt är RUT-berättigat.</strong> Det innebär att du betalar bara halva arbetskostnaden när du anlitar professionell <a href="/flytthjalp">flytthjälp</a> — resten står staten för genom RUT-avdraget. En flytt som kostar 12 000 kr i arbetskostnad kostar dig alltså bara <strong>6 000 kr</strong>.</p>
<p>RUT-avdrag på flytt är en av de smartaste besparingarna du kan göra. Trots det är det förvånansvärt många som inte vet att flytt berättigar till avdraget. Många tror att RUT bara gäller städning och hemhjälp, men sanningen är att <strong>flytt av bohag och personliga tillhörigheter</strong> uttryckligen räknas som hushållsarbete enligt Skatteverket.</p>
<p>I den här guiden går vi igenom allt du behöver veta: vad som ingår och inte ingår, hur mycket du faktiskt sparar med konkreta räkneexempel, och hur du bokar flytt med RUT-avdrag direkt inräknat — utan pappersarbete och utan att kontakta Skatteverket.</p>`,
      },
      {
        heading: "Är flytt RUT-berättigat?",
        html: `<p><strong>Ja, flytt är RUT-berättigat.</strong> Skatteverket klassar flytt av bohag och personliga tillhörigheter som hushållsarbete, vilket ger rätt till 50 % skattereduktion på arbetskostnaden. Det gäller oavsett om du anlitar hjälp för en hel <a href="/flytthjalp">bohagsflytt</a> eller bara behöver hjälp att bära ner en soffa.</p>
<p>RUT-avdraget gäller för alla typer av bohagsflytt som utförs av ett registrerat företag med F-skattsedel. Det spelar ingen roll vilken typ av bostad du bor i — avdraget gäller för:</p>
<ul>
  <li><strong>Hyresrätt</strong> — Ja, du får RUT på flytt även om du hyr</li>
  <li><strong>Bostadsrätt</strong> — Ja, full rätt till RUT-avdrag</li>
  <li><strong>Villa och radhus</strong> — Ja, alla RUT-tjänster gäller</li>
  <li><strong>Fritidshus</strong> — Ja, RUT gäller även vid flytt till eller från fritidshus</li>
</ul>
<p>Det enda kravet är att arbetet utförs av ett företag som är registrerat hos Skatteverket och har F-skattsedel. Anlitar du privatpersoner via appar som Tiptapp — där utförarna inte har F-skatt — får du <strong>inget RUT-avdrag</strong>. Det kan göra att en till synes billigare lösning faktiskt kostar mer än en professionell flyttfirma med RUT.</p>
<p>Flytt lades till som RUT-berättigad tjänst av Skatteverket just för att bekämpa svartarbete inom flyttbranschen och göra det enklare för privatpersoner att anlita professionell hjälp till ett rimligt pris.</p>`,
      },
      {
        heading: "Hur mycket sparar du med RUT-avdrag på flytt?",
        html: `<p>Du sparar <strong>50 % av arbetskostnaden</strong> — alltid exakt hälften. Det maximala avdraget är 75 000 kr per person och år. Eftersom det gäller per person kan ett par utnyttja upp till 150 000 kr tillsammans, men de allra flesta flytt landar långt under taket.</p>
<p><strong>Viktigt att förstå:</strong> RUT-avdraget gäller bara arbetskostnaden. Det gäller <em>inte</em> för material, fordon, drivmedel eller moms på material. Men hos de flesta flyttfirmor — inklusive Ungdomskraft — är den allra största delen av fakturan arbetskostnad, vilket innebär att besparingen blir betydande.</p>
<p>Här är konkreta räkneexempel som visar vad du faktiskt betalar:</p>
<table style="width:100%;border-collapse:collapse;margin:1.5rem 0">
  <thead>
    <tr style="background:#2a4a63;color:white">
      <th style="padding:0.75rem 1rem;text-align:left;border-radius:0.5rem 0 0 0">Typ av flytt</th>
      <th style="padding:0.75rem 1rem;text-align:right">Arbetskostnad</th>
      <th style="padding:0.75rem 1rem;text-align:right">RUT-avdrag (50 %)</th>
      <th style="padding:0.75rem 1rem;text-align:right;border-radius:0 0.5rem 0 0">Du betalar</th>
    </tr>
  </thead>
  <tbody>
    <tr style="background:#f8f9fa">
      <td style="padding:0.75rem 1rem;border-bottom:1px solid #e9ecef">Liten flytt (1–2 rum)</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef">8 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;color:#2a8c4a">−4 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;font-weight:700">4 000 kr</td>
    </tr>
    <tr>
      <td style="padding:0.75rem 1rem;border-bottom:1px solid #e9ecef">Medelstor flytt (2–3 rum)</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef">12 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;color:#2a8c4a">−6 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;font-weight:700">6 000 kr</td>
    </tr>
    <tr style="background:#f8f9fa">
      <td style="padding:0.75rem 1rem;border-bottom:1px solid #e9ecef">Stor flytt (4+ rum / villa)</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef">20 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;color:#2a8c4a">−10 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;border-bottom:1px solid #e9ecef;font-weight:700">10 000 kr</td>
    </tr>
    <tr>
      <td style="padding:0.75rem 1rem">Flytt + bortforsling av gammalt</td>
      <td style="padding:0.75rem 1rem;text-align:right">16 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;color:#2a8c4a">−8 000 kr</td>
      <td style="padding:0.75rem 1rem;text-align:right;font-weight:700">8 000 kr</td>
    </tr>
  </tbody>
</table>
<img src="/guide-rut-avdrag-flytt-van.png" alt="Ungdomskraft-teamet vid skåpbil med flyttlådor — flytt med RUT-avdrag" style="margin-top:1.5rem;border-radius:0.75rem;width:100%" />
<p>En typisk flytt av en tvårumslägenhet med Ungdomskraft kostar runt 10 000–14 000 kr i arbetskostnad. Med RUT betalar du alltså <strong>5 000–7 000 kr</strong> för professionell flytt med anställda medarbetare, eget skåpbil och Trygg Hansa-försäkring. Det är ofta billigare än att hyra en bil och be kompisar om hjälp — och definitivt tryggare.</p>
<p>Besparingen blir ännu större om du kombinerar flytt med andra RUT-berättigade tjänster samma år. Behöver du <a href="/bortforsling">bortforsling</a> av gamla möbler (25 % avdrag)? Eller <a href="/mobelmontering">möbelmontering</a> i den nya lägenheten (50 % avdrag)? Testa vår <a href="/guide/kalkyl-rut-rot">RUT-kalkylator</a> för att räkna ut din exakta besparing.</p>`,
      },
      {
        heading: "Vad ingår i RUT-avdraget vid flytt?",
        html: `<p>Skatteverket har tydliga regler för vad som räknas som RUT-berättigat arbete vid flytt. Här är en komplett översikt:</p>
<p><strong>Det här ger RUT-avdrag vid flytt:</strong></p>
<ul>
  <li><strong>Packning och uppackning</strong> — Personalen packar ner dina tillhörigheter i kartonger och packar upp dem i den nya bostaden</li>
  <li><strong>Bärning av möbler och kartonger</strong> — Allt bärande i trappor, genom korridorer och in/ut genom dörrar. Gäller oavsett våning och oavsett om det finns hiss</li>
  <li><strong>Transport av bohag</strong> — Lastning, körning och avlastning av dina saker från gammal bostad till ny. Se även <a href="/bohagsflytt">bohagsflytt</a> och <a href="/transporthjalp">transporthjälp</a></li>
  <li><strong>Demontering och montering av möbler</strong> — Plocka isär garderober, sängar och bokhyllor för transport och montera dem igen i den nya bostaden</li>
  <li><strong>Skyddande av möbler</strong> — Inslagning av ömtåliga möbler i skyddsfiltar och plastfolie under transporten</li>
  <li><strong>Bärhjälp i trappor</strong> — Om du behöver hjälp att bära tunga saker som soffor, tvättmaskiner eller pianon. Se även vår sida om <a href="/barhjalp">bärhjälp</a> och <a href="/barhjalp-flytt">bärhjälp vid flytt</a></li>
</ul>
<p><strong>Det här ger INTE RUT-avdrag vid flytt:</strong></p>
<ul>
  <li><strong>Flyttstädning</strong> — Städning är en separat RUT-berättigad tjänst, men ingår inte i flytten. Du kan dock använda RUT-avdrag på städningen separat om du anlitar ett städföretag</li>
  <li><strong>Material</strong> — Flyttkartonger, tejp, bubbelplast och skyddsfiltar ger inget avdrag</li>
  <li><strong>Hyra av lastbil utan personal</strong> — Om du bara hyr ett fordon utan att anlita personal för arbetet får du inget RUT-avdrag. Det är arbetet som ger avdrag, inte fordonet</li>
  <li><strong>Magasinering</strong> — Förvaring av bohag i lager eller förråd ger inte RUT</li>
  <li><strong>Företagsflytt</strong> — RUT gäller bara privatpersoner. En kontorsflytt ger inget avdrag</li>
</ul>
<p>Hos Ungdomskraft ingår alltid eget skåpbil, skyddsfiltar och all nödvändig utrustning i priset. Den absolut största delen av vår faktura är arbetskostnad — vilket innebär att nästan hela beloppet berättigar till RUT-avdrag.</p>`,
      },
      {
        heading: "Hur använder du RUT-avdraget praktiskt?",
        html: `<p>Det enklaste med RUT-avdraget är att du knappt behöver göra någonting själv. Det finns ingen ansökan att skicka in, inga blanketter och inget du behöver redovisa i deklarationen. Så här går det till:</p>
<p><strong>Steg 1: Boka flytt hos ett registrerat företag</strong></p>
<p>Välj ett flyttföretag som erbjuder RUT-avdrag och har F-skattsedel. Med <a href="/boka">Ungdomskraft</a> ser du priset med RUT-avdrag redan inräknat direkt vid bokningen.</p>
<p><strong>Steg 2: Uppge ditt personnummer</strong></p>
<p>Företaget behöver ditt personnummer för att kunna begära utbetalning från Skatteverket. Det är det enda du behöver göra.</p>
<p><strong>Steg 3: Du betalar bara din halva</strong></p>
<p>På fakturan betalar du bara 50 % av arbetskostnaden. Företaget begär resterande 50 % direkt från Skatteverket. Du behöver inte kontakta Skatteverket, inte spara kvitton och inte redovisa något i deklarationen — allt sköts automatiskt.</p>
<p><strong>Viktigt: Anlita rätt företag</strong></p>
<p>För att RUT ska fungera måste företaget ha <strong>F-skattsedel</strong>. Det innebär att företaget är registrerat som näringsidkare hos Skatteverket. Anlitar du privatpersoner via marknadsplatser som Tiptapp — där utförarna saknar F-skatt — får du <strong>inget RUT-avdrag</strong>. Det kan betyda att en till synes billig lösning faktiskt kostar mer.</p>
<p>Räkneexempel: En flytt via Tiptapp kostar 8 000 kr utan RUT. Samma flytt hos Ungdomskraft kostar 12 000 kr — men med RUT betalar du bara <strong>6 000 kr</strong>. Du sparar 2 000 kr och får dessutom anställd personal, försäkring och eget skåpbil.</p>
<p>Läs mer i vår <a href="/guide/ungdomskraft-vs-tiptapp">jämförelse mellan Ungdomskraft och Tiptapp</a> eller vår <a href="/guide/tiptapp-stockholm">Tiptapp Stockholm-guide</a>.</p>`,
      },
      {
        heading: "RUT-avdrag på flytt i hyresrätt — gäller det?",
        html: `<p><strong>Ja, RUT-avdrag på flytt gäller i hyresrätt.</strong> Det är en vanlig missuppfattning att RUT bara gäller för dem som äger sin bostad — villa eller bostadsrätt. Men sanningen är att RUT-avdraget gäller oavsett boendeform.</p>
<p>Skatteverkets regler är tydliga: RUT-avdraget baseras på att <strong>arbetet utförs åt en privatperson</strong>, inte på vilken typ av bostad personen bor i. Det innebär att du har full rätt till RUT oavsett om du:</p>
<ul>
  <li>Flyttar från en hyresrätt till en annan hyresrätt</li>
  <li>Flyttar från hyresrätt till bostadsrätt (eller tvärtom)</li>
  <li>Flyttar till eller från en villa</li>
  <li>Flyttar till eller från ett fritidshus</li>
  <li>Flyttar till ett seniorboende eller äldreboende</li>
</ul>
<p>Bor du i hyresrätt och ska flytta? Då kan du spara tusentals kronor med RUT-avdraget. En flytt av en tvårumslägenhet som kostar 10 000 kr i arbetskostnad kostar dig bara <strong>5 000 kr</strong> med RUT.</p>
<p>Behöver du flytta i <a href="/flytthjalp-stockholm">Stockholm</a> eller <a href="/flytthjalp-goteborg">Göteborg</a>? Ungdomskraft erbjuder professionell flytt med RUT-avdrag i båda städerna.</p>`,
      },
      {
        heading: "Kan pensionärer få RUT-avdrag på flytt?",
        html: `<p><strong>Ja, pensionärer kan absolut använda RUT-avdrag på flytt.</strong> Faktum är att RUT-avdraget är särskilt värdefullt för pensionärer — många äldre flyttar till ett mindre boende, ett seniorboende eller ett äldreboende och behöver professionell hjälp med packning, bärning och transport.</p>
<p>Reglerna för pensionärer ser ut så här:</p>
<ul>
  <li><strong>Samma procentavdrag</strong> — 50 % av arbetskostnaden, precis som för alla andra</li>
  <li><strong>Maximalt avdrag</strong> — Pensionärer födda 1957 eller tidigare har ett gemensamt tak på 75 000 kr för RUT och ROT tillsammans. Yngre pensionärer har separata tak (75 000 kr RUT + 50 000 kr ROT)</li>
  <li><strong>Krav på skatt att dra av mot</strong> — Du måste ha betalat tillräckligt med skatt under året. De flesta pensionärer med inkomstpension betalar tillräckligt för att utnyttja RUT fullt ut</li>
</ul>
<p>RUT-avdraget gör professionell flytt tillgängligt och prisvärt för äldre. Istället för att be barn eller barnbarn om hjälp — eller riskera skador vid tunga lyft — kan du anlita ett professionellt team som hanterar allt tryggt och försäkrat, till halva priset.</p>
<p>Läs mer i vår guide om <a href="/guide/rut-avdrag-pensionar">RUT-avdrag för pensionärer</a>. Ungdomskrafts team har stor erfarenhet av att hjälpa äldre med flytt. Vi tar hand om packning, bärning, transport och uppställning — lugnt och omsorgsfullt. Och du betalar bara halva kostnaden tack vare RUT.</p>
<img src="/guide-rut-avdrag-flytt-loading.png" alt="Ungdomskraft-medarbetare lastar däck i skåpbil under flytt med RUT-avdrag" style="margin-top:1.5rem;border-radius:0.75rem;width:100%" />`,
      },
      {
        heading: "Boka flytt med RUT-avdrag hos Ungdomskraft",
        html: `<p>Ungdomskraft gör det så enkelt som möjligt att använda RUT-avdrag på din flytt. <strong>RUT-avdraget räknas in direkt i offerten</strong> — du ser exakt vad du faktiskt betalar redan vid bokningen. Inget krångel med Skatteverket, inga blanketter, inga överraskningar.</p>
<p><strong>Det här får du med Ungdomskraft:</strong></p>
<ul>
  <li><strong>RUT-avdrag direkt i priset</strong> — Du ser vad du betalar redan vid bokningen. Vi sköter allt med Skatteverket</li>
  <li><strong>Fast pris</strong> — Inga dolda avgifter, inga tilläggsavgifter, inga överraskningar i efterhand</li>
  <li><strong>Eget skåpbil ingår alltid</strong> — Du behöver inte hyra fordon eller organisera transport</li>
  <li><strong>Anställda medarbetare</strong> — Bakgrundskontrollerade, engagerade ungdomar med riktiga anställningar. Inga gigarbetare</li>
  <li><strong>Försäkring via Trygg Hansa</strong> — Fullständig ansvarsförsäkring som täcker skador under hela flytten</li>
  <li><strong>Dedikerad kontaktperson</strong> — Ring eller chatta med din personliga kontaktperson före, under och efter flytten</li>
  <li><strong>Finns i Stockholm och Göteborg</strong> — <a href="/flytthjalp-stockholm">Flytt i Stockholm</a> och <a href="/flytthjalp-goteborg">flytt i Göteborg</a> med alla kranskommuner</li>
</ul>
<p><strong>Så bokar du:</strong></p>
<ol>
  <li>Gå till <a href="/boka">ungdomskraft.com/boka</a></li>
  <li>Välj "Flytt" som tjänst</li>
  <li>Ange adresser, våning och vad som ska flyttas</li>
  <li>Se ditt fasta pris — redan med RUT-avdraget inräknat</li>
  <li>Bekräfta bokningen — klart!</li>
</ol>
<p>Det tar under 2 minuter att boka, och du vet exakt vad du betalar. Inga överraskningar, inget krångel.</p>
<p style="margin-top:2rem;display:flex;flex-wrap:wrap;gap:1rem">
  <a href="/boka" style="display:inline-block;background:#f89544;color:#fff;font-weight:700;padding:0.9rem 2rem;border-radius:0.5rem;text-decoration:none;font-size:1.05rem">Få pris på din flytt med RUT</a>
  <a href="tel:+46101234567" style="display:inline-block;background:#2a4a63;color:#fff;font-weight:700;padding:0.9rem 2rem;border-radius:0.5rem;text-decoration:none;font-size:1.05rem">Ring oss — 010-123 45 67</a>
</p>
<p style="margin-top:1.5rem">Behöver du mer hjälp? Här är fler populära sidor:</p>
<ul>
  <li><a href="/flytthjalp">Flytt och flytthjälp</a> — allt om våra flyttjänster</li>
  <li><a href="/bortforsling">Bortforsling</a> — bli av med gamla möbler i samband med flytten</li>
  <li><a href="/mobelmontering">Möbelmontering</a> — montering av IKEA-möbler i nya bostaden</li>
  <li><a href="/studentflytt">Studentflytt</a> — flytt för studenter med RUT-avdrag</li>
  <li><a href="/tomma-dodsbo">Tömning av dödsbo</a> — professionell hjälp med dödsbo</li>
  <li><a href="/guide/rut-avdrag-bortforsling">RUT-avdrag på bortforsling</a> — läs om RUT vid bortforsling</li>
  <li><a href="/guide/rut-avdrag-mobelmontering">RUT-avdrag på möbelmontering</a> — halvera monteringskostnaden</li>
  <li><a href="/guide/vad-ar-rut-avdrag">Vad är RUT-avdrag?</a> — komplett guide om RUT</li>
  <li><a href="/guide/ungdomskraft-vs-tiptapp">Ungdomskraft vs Tiptapp</a> — fullständig jämförelse</li>
</ul>`,
      },
    ],
    faq: [
      {
        question: "Är flytt RUT-berättigat?",
        answer:
          "Ja, flytt av bohag och personliga tillhörigheter är RUT-berättigat. Du betalar bara 50 % av arbetskostnaden — resten begär företaget direkt från Skatteverket. Det gäller packning, bärning, transport och montering av möbler.",
      },
      {
        question: "Hur mycket sparar jag med RUT-avdrag på flytt?",
        answer:
          "Du sparar 50 % av arbetskostnaden. En flytt som kostar 12 000 kr i arbetskostnad kostar dig alltså bara 6 000 kr. Det maximala avdraget är 75 000 kr per person och år.",
      },
      {
        question: "Gäller RUT-avdrag på flytt i hyresrätt?",
        answer:
          "Ja, RUT-avdrag gäller oavsett boendeform — hyresrätt, bostadsrätt, villa, radhus och fritidshus. Det är en vanlig missuppfattning att det bara gäller för dem som äger sin bostad.",
      },
      {
        question: "Vad ingår i RUT-avdraget vid flytt?",
        answer:
          "RUT-avdraget gäller arbetskostnaden för packning, bärning, transport av bohag, demontering och montering av möbler samt skyddande av möbler under transporten. Material, fordon och flyttstädning ger inte RUT på flytten, men flyttstädning kan ge RUT separat.",
      },
      {
        question: "Hur ansöker jag om RUT-avdrag på flytt?",
        answer:
          "Du behöver inte ansöka alls. Flyttfirman hanterar allt automatiskt. Du uppger ditt personnummer, betalar halva fakturan, och företaget begär resterande 50 % direkt från Skatteverket. Det syns sedan i din deklaration automatiskt.",
      },
      {
        question: "Kan pensionärer få RUT-avdrag på flytt?",
        answer:
          "Ja, pensionärer har full rätt till RUT-avdrag på flytt. Avdraget är 50 % av arbetskostnaden. Pensionärer födda 1957 eller tidigare har ett gemensamt tak på 75 000 kr för RUT och ROT, medan yngre har separata tak.",
      },
      {
        question: "Får man RUT-avdrag på flyttstädning?",
        answer:
          "Flyttstädning ingår inte i flytt-RUT, men det är en separat RUT-berättigad tjänst. Du kan alltså använda RUT-avdrag både på flytten och på städningen — men de faktureras som separata tjänster.",
      },
      {
        question: "Får man RUT-avdrag med Tiptapp vid flytt?",
        answer:
          "I de flesta fall nej. Tiptapp förmedlar uppdrag till privatpersoner som saknar F-skattsedel, och utan F-skatt finns ingen möjlighet till RUT-avdrag. Vill du utnyttja RUT behöver du anlita ett företag som Ungdomskraft med F-skatt och anställda medarbetare.",
      },
      {
        question: "Vad kostar en flytt med RUT-avdrag?",
        answer:
          "Priset beror på storlek och avstånd. En liten flytt (1–2 rum) kostar runt 4 000–5 000 kr efter RUT. En medelstor flytt (2–3 rum) kostar runt 6 000–7 000 kr efter RUT. En stor flytt (4+ rum) kostar runt 10 000–12 000 kr efter RUT. Med Ungdomskraft ser du priset direkt vid bokningen.",
      },
      {
        question: "Ingår skåpbil i priset vid flytt med RUT?",
        answer:
          "Hos Ungdomskraft ingår alltid eget skåpbil i priset. Du behöver inte hyra fordon eller organisera transport. Skåpbilen och all nödvändig utrustning som skyddsfiltar ingår utan extra kostnad.",
      },
      {
        question: "Kan jag kombinera RUT på flytt och bortforsling?",
        answer:
          "Ja, både flytt och bortforsling är RUT-berättigade tjänster. Du kan använda avdraget på båda under samma år. Perfekt om du vill forsla bort gamla möbler i samband med flytten — du betalar halva arbetskostnaden på allt.",
      },
      {
        question: "Hur snabbt kan jag boka flytt med RUT?",
        answer:
          "Hos Ungdomskraft kan du ofta boka flytt samma vecka, ibland redan nästa dag beroende på tillgänglighet. Du bokar direkt på ungdomskraft.com/boka — det tar under 2 minuter och du ser priset med RUT direkt.",
      },
      {
        question: "Gäller RUT-avdrag på flytt för möbelmontering?",
        answer:
          "Ja, demontering och montering av möbler i samband med flytt ger RUT-avdrag. Det gäller till exempel att plocka isär garderober och sängar för transport och sedan montera dem i den nya bostaden.",
      },
      {
        question: "Måste jag kontakta Skatteverket för att få RUT på flytt?",
        answer:
          "Nej, du behöver inte kontakta Skatteverket alls. Flyttfirman sköter hela processen åt dig. Du betalar bara din halva av fakturan, och företaget begär resten från Skatteverket. Avdraget syns automatiskt i din deklaration.",
      },
      {
        question: "Är Ungdomskraft försäkrade under flytten?",
        answer:
          "Ja, alla Ungdomskrafts uppdrag täcks av en fullständig ansvarsförsäkring via Trygg Hansa. Om något skulle gå sönder under flytten tar vi fullt ansvar och ersätter skadan. Du behöver inte teckna någon egen försäkring.",
      },
      {
        question: "Kan jag flytta på helgen med RUT-avdrag?",
        answer:
          "Ja, Ungdomskraft erbjuder flytt alla dagar i veckan inklusive helger. RUT-avdraget gäller oavsett vilken dag flytten utförs. Du väljer dag och tid som passar dig vid bokningen.",
      },
    ],
  },
  {
    slug: "rut-avdrag-bortforsling",
    title: "RUT-avdrag på bortforsling – så fungerar det",
    excerpt:
      "Bortforsling kan ge 25 % RUT-avdrag — men bara om sakerna går till återanvändning. Läs hur det fungerar och varför Ungdomskraft alltid kör förbi återanvändning först.",
    coverImage: "/guide-rut-avdrag-bortforsling-cover.png",
    coverImageAlt:
      "Ungdomskraft-medarbetare bär bort skräp i storsäck – bortforsling med RUT-avdrag",
    publishedAt: "2026-03-06",
    tags: ["rut-avdrag", "bortforsling"],
    metaTitle:
      "RUT-avdrag på bortforsling – Spara 25 % via återanvändning (2026)",
    metaDescription:
      "Bortforsling kan ge 25 % RUT-avdrag — men bara om sakerna går till återanvändning. Läs hur det fungerar och varför Ungdomskraft alltid kör förbi återanvändning först.",
    content: [
      {
        heading: "RUT-avdrag på bortforsling – så fungerar det (2026)",
        html: `<p><strong>Bortforsling kan vara RUT-berättigat — men inte alltid.</strong> Du kan få <strong>25 % RUT-avdrag</strong> på arbetskostnaden, men bara om sakerna som forslas bort går till <strong>återanvändning</strong>. Det innebär att möbler, husgeråd och andra brukbara föremål lämnas till exempelvis Myrorna eller en liknande organisation som tar emot dem för vidareförsäljning.</p>
<p>Om sakerna däremot inte kan återanvändas — och måste köras till tippen — gäller <strong>inget RUT-avdrag</strong> alls. Det är alltså destinationen som avgör om avdraget kan göras eller inte.</p>`,
      },
      {
        heading: "Är bortforsling RUT-berättigat?",
        html: `<p><strong>Ja, men med ett viktigt villkor.</strong> Bortforsling räknas som en hushållsnära tjänst och kan ge RUT-avdrag — men bara om de bortforslade sakerna lämnas till <strong>återanvändning</strong>. Det innebär att föremålen ska gå till en organisation som Myrorna, Erikshjälpen eller liknande som tar emot dem för vidareförsäljning.</p>
<p>Om sakerna inte kan återanvändas och istället måste slängas på en återvinningscentral eller deponi, gäller <strong>inget RUT-avdrag</strong>.</p>
<p>Avdragsnivån är <strong>25 %</strong> av arbetskostnaden. Läs mer i vår kompletta guide: <a href="/guide/vad-ar-rut-avdrag">Vad är RUT-avdrag?</a></p>`,
      },
      {
        heading: "Vad kan du få bortforslat med RUT-avdrag?",
        html: `<p>Följande är exempel på vad som är RUT-berättigat vid bortforsling:</p>
<ul>
  <li><a href="/bortforsling-mobler">Gamla möbler</a> och inredning</li>
  <li><a href="/bortforsling-vitvaror">Vitvaror</a> och elektronik</li>
  <li>Byggmaterial och rivningsrester</li>
  <li>Trädgårdsavfall och grenar</li>
  <li>Skräp från <a href="/tomma-kallare">källare</a>, vind eller <a href="/tomma-forrad">förråd</a></li>
  <li><a href="/bortforsling-dodsbo">Bohag från dödsbo</a></li>
  <li>Kontorsmöbler och företagsinventarier</li>
</ul>
<p><strong>Vad som inte ingår:</strong> Farligt avfall, asbest och kemikalier kan inte hanteras inom RUT-avdraget och kräver separat hantering.</p>`,
      },
      {
        heading: "Hur mycket sparar du med RUT-avdrag på bortforsling?",
        html: `<p>Om sakerna går till återanvändning täcker RUT-avdraget <strong>25 % av arbetskostnaden</strong>. Du betalar din del direkt till företaget — resten ansöker företaget om från Skatteverket.</p>
<p><strong>Räkneexempel (när sakerna går till återanvändning):</strong></p>
<table>
  <thead><tr><th>Arbetskostnad</th><th>Du betalar</th><th>Staten betalar (25 %)</th></tr></thead>
  <tbody>
    <tr><td>2 000 kr</td><td>1 500 kr</td><td>500 kr</td></tr>
    <tr><td>4 000 kr</td><td>3 000 kr</td><td>1 000 kr</td></tr>
    <tr><td>8 000 kr</td><td>6 000 kr</td><td>2 000 kr</td></tr>
  </tbody>
</table>
<p><strong>Observera:</strong> om sakerna inte kan återanvändas och måste köras till tippen gäller inget RUT-avdrag alls — du betalar hela arbetskostnaden.</p>
<p>Maxbeloppet för RUT-avdraget är <strong>50 000 kr per person och år</strong>. Det kombinerade taket för RUT och ROT tillsammans är 75 000 kr — använder du inget ROT kan du alltså utnyttja hela 50 000 kr till RUT.</p>
<p><strong>Viktigt:</strong> avdraget gäller bara arbetskostnaden — inte material, körsträcka eller moms. Hos Ungdomskraft ser du alltid exakt vad som ingår i priset innan du bokar. Testa gärna vår <a href="/guide/kalkyl-rut-rot">RUT-kalkylator</a> för att räkna ut exakt hur mycket du sparar.</p>`,
      },
      {
        heading: "Hur använder du RUT-avdraget i praktiken?",
        html: `<p>Det är enklare än vad de flesta tror:</p>
<ol>
  <li><strong>Du bokar <a href="/bortforsling">bortforsling</a></strong> hos ett företag som är godkänt hos Skatteverket</li>
  <li><strong>Du betalar din del</strong> — 75 % av arbetskostnaden om sakerna går till återanvändning, annars hela kostnaden</li>
  <li><strong>Företaget ansöker</strong> om resten från Skatteverket</li>
  <li><strong>Du gör ingenting</strong> i deklarationen — det sköts automatiskt</li>
</ol>
<p>Det enda du behöver tänka på är att anlita ett registrerat företag. <a href="/guide/ungdomskraft-vs-tiptapp">Privatpersoner via appar som Tiptapp</a> ger ingen rätt till RUT-avdrag — avdraget kräver ett momsregistrerat företag.</p>`,
      },
      {
        heading: "RUT-avdrag på bortforsling i hyresrätt – gäller det?",
        html: `<p><strong>Ja.</strong> En vanlig missuppfattning är att RUT-avdraget bara gäller villor eller bostadsrätter. Men avdraget gäller oavsett boendeform — hyresrätt, bostadsrätt, villa och fritidshus omfattas alla.</p>`,
      },
      {
        heading: "RUT-avdrag vid tömning av dödsbo",
        html: `<p>Att tömma ett dödsbo är ofta både fysiskt och känslomässigt tungt. Bortforsling av möbler och bohag från ett dödsbo är RUT-berättigat, vilket gör det billigare att anlita professionell hjälp.</p>
<p>Ungdomskraft hjälper regelbundet till vid dödsbon — vi hanterar allt praktiskt med respekt och effektivitet, och RUT-avdraget räknas in direkt i priset. Läs mer i vår guide om <a href="/guide/rut-avdrag-dodsbostadning">RUT-avdrag vid dödsbostädning</a>.</p>`,
      },
      {
        heading: "Ungdomskraft kör alltid förbi återanvändning först",
        html: `<p>Hos Ungdomskraft kör vi <strong>alltid förbi en återanvändningsorganisation först</strong> — som Myrorna eller liknande — för att se om de vill ta emot sakerna. Om de tar emot föremålen kan vi göra RUT-avdrag på 25 % av arbetskostnaden, och du sparar pengar.</p>
<p>Men om återanvändningen tackar nej — för att sakerna är i för dåligt skick eller av annan anledning — är vi tvungna att köra vidare till tippen. I det fallet kan <strong>inget RUT-avdrag</strong> göras, och du betalar hela arbetskostnaden.</p>
<p>Vi är ett registrerat företag hos Skatteverket och hanterar alltid RUT-avdraget åt dig när det är möjligt.</p>
<ul>
  <li><strong>Fast pris</strong> — inga överraskningar i efterhand</li>
  <li><strong>RUT inräknat</strong> — du ser direkt vad du faktiskt betalar</li>
  <li><strong>Försäkrat uppdrag</strong> — du är trygg om något skulle hända</li>
  <li><strong>Boka på 2 minuter</strong> — direkt i mobilen, ingen app att ladda ner</li>
  <li><strong>Eller ring oss</strong> — så löser vi det tillsammans</li>
</ul>
<p>Vi erbjuder även <a href="/flytthjalp">flytthjälp</a>, <a href="/mobelmontering">möbelmontering</a> och <a href="/barhjalp">bärhjälp</a> — allt med RUT-avdrag. Läs också om <a href="/guide/rut-avdrag-flytt">RUT-avdrag på flytt</a>. Vi finns i hela Sverige, med extra snabb service i <a href="/bortforsling-stockholm">Stockholm</a> och <a href="/bortforsling-goteborg">Göteborg</a>.</p>
<p>Sedan 2014 har vi genomfört över 50 000 uppdrag med ett snittbetyg på 4,9/5. Varje uppdrag utförs av ungdomar som får sitt första jobb — du gör en insats för samhället samtidigt som du får jobbet gjort.</p>
<p><strong><a href="/boka">Boka bortforsling med RUT-avdrag →</a></strong></p>
<p><strong><a href="tel:0851708281">Ring oss direkt → 08-517 082 81</a></strong></p>`,
      },
    ],
    faq: [
      {
        question: "Kan jag använda RUT-avdrag om jag inte deklarerar i Sverige?",
        answer:
          "Nej, RUT-avdraget kräver att du betalar skatt i Sverige.",
      },
      {
        question: "Gäller RUT-avdraget om jag hyr ut min bostad?",
        answer:
          "Nej, avdraget gäller bara för din permanenta eller fritidsbostad — inte en bostad du hyr ut.",
      },
      {
        question: "Hur vet jag att Ungdomskraft är godkänt för RUT?",
        answer:
          "Ungdomskraft är momsregistrerat och godkänt hos Skatteverket. Du ser avdraget specificerat direkt på fakturan.",
      },
      {
        question: "Kan två personer dela på RUT-avdraget?",
        answer:
          "Ja, om ni är två i hushållet kan ni dela på arbetskostnaden och vardera utnyttja avdraget upp till maxbeloppet.",
      },
    ],
  },
  {
    slug: "rut-avdrag-dodsbostadning",
    title:
      "RUT-avdrag vid dödsbostädning – vad gäller och hur fungerar det? (2026)",
    excerpt:
      "Kan man använda RUT-avdrag vid tömning av dödsbo? Ja – och det halverar din kostnad. Läs vad som gäller, vad som ingår och hur du bokar.",
    coverImage: "/guide-rut-avdrag-dodsbostadning-cover.png",
    coverImageAlt:
      "Ungdomskraft-medarbetare utför dödsbostädning med RUT-avdrag",
    publishedAt: "2026-03-06",
    tags: ["rut-avdrag", "dödsbo", "bortforsling"],
    metaTitle:
      "RUT-avdrag vid dödsbostädning – Så fungerar det (2026)",
    metaDescription:
      "Kan man använda RUT-avdrag vid tömning av dödsbo? Ja – och det halverar din kostnad. Läs vad som gäller, vad som ingår och hur du bokar.",
    faq: [
      {
        question: "Gäller RUT-avdrag om den avlidne bodde i hyresrätt?",
        answer:
          "Ja. RUT-avdraget gäller oavsett om det är en hyresrätt, bostadsrätt eller villa. Det enda kravet är att arbetet utförs i bostaden och att dödsboet är beställare.",
      },
      {
        question:
          "Kan jag använda RUT-avdraget om jag inte är arvinge?",
        answer:
          "Nej. Det är dödsboet och dess delägare som har rätt till avdraget. En vän eller annan person som hjälper till praktiskt kan inte använda avdraget.",
      },
      {
        question:
          "Vad händer om vi är flera arvingar — kan vi dela på avdraget?",
        answer:
          "Ja. Avdraget är personbundet och varje dödsbodelägare kan utnyttja sitt eget utrymme upp till 50 000 kr per år. Ju fler delägare, desto mer total avdragsrätt. Ange de personnummer som ska utnyttja avdraget när ni faktureras.",
      },
      {
        question: "Måste arbetet utföras före bouppteckning?",
        answer:
          "Nej, det finns inget krav på att bouppteckning ska vara klar. Arbetet kan beställas och utföras parallellt med dödsboförvaltningen.",
      },
      {
        question:
          "Kan vi använda RUT för både tömning och städning?",
        answer:
          "Ja. Både tömning/bortforsling och städning är RUT-berättigade. De kan beställas som ett samlat uppdrag eller separat — avdraget gäller i båda fallen.",
      },
      {
        question: "Hur lång tid tar det att tömma ett dödsbo?",
        answer:
          "Det beror på storlek och mängd. En 2-rumslägenhet tar vanligtvis 3–6 timmar med ett erfaret team. En villa med källare kan ta en heldag. Ungdomskraft ger dig en tidsuppskattning när du bokar.",
      },
      {
        question: "Erbjuder ni värdering av dödsboets innehåll?",
        answer:
          "Nej, det ingår inte i vår tjänst. För värdering av möbler, konst och föremål rekommenderar vi en auktionsfirma eller värderingsman. Vi kan hjälpa till efter att värderingen är klar.",
      },
      {
        question:
          "Kan man använda RUT-avdrag vid dödsbostädning?",
        answer:
          "Ja. Tömning och städning av dödsbo är RUT-berättigat. Dödsboet är beställare och avdraget fördelas på de dödsbodelägare vars personnummer anges på fakturan. Du betalar bara halva arbetskostnaden — företaget ansöker om resten hos Skatteverket.",
      },
      {
        question:
          "Vad kostar det att tömma ett dödsbo med RUT-avdrag?",
        answer:
          "Priset beror på bostadens storlek och mängden bohag. Som riktmärke kostar tömning av en 2-rk lägenhet 2 500–4 500 kr efter RUT-avdrag. En villa med källare kan kosta 6 000–12 500 kr efter avdrag. Ungdomskraft ger alltid fast pris i förväg.",
      },
      {
        question:
          "Behöver jag sortera innan ni kommer för dödsbostädning?",
        answer:
          "Nej. Ungdomskraft tar hand om hela processen — sortering, bortforsling och städning. Saker i gott skick doneras till välgörenhet, återvinningsbart material sorteras och skräp lämnas till miljöstation.",
      },
      {
        question:
          "Hur snabbt kan ni hjälpa till med dödsbostädning?",
        answer:
          "Vi har ofta tillgänglighet inom några dagar. Boka direkt på ungdomskraft.com/boka eller ring oss på 08-517 082 81 så hjälper vi dig att hitta en tid som passar.",
      },
      {
        question:
          "Vad händer med värdefulla föremål vid dödsbostädning?",
        answer:
          "Vi rekommenderar att arvingarna går igenom bostaden och plockar ut värdesaker, dokument och personliga föremål innan vi börjar. Om det finns saker av potentiellt värde som ni inte vill behålla, rekommenderar vi att kontakta en auktionsfirma först.",
      },
      {
        question:
          "Kan Ungdomskraft hjälpa till med dödsbostädning i hela Stockholm och Göteborg?",
        answer:
          "Ja. Vi är verksamma i hela Stockholmsområdet och Göteborgsområdet, inklusive kranskommuner. Fast pris, RUT-avdrag och Trygg Hansa-försäkring ingår alltid.",
      },
      {
        question:
          "Vad är skillnaden mellan dödsbostädning och vanlig bortforsling?",
        answer:
          "Dödsbostädning innebär tömning av en avliden persons bostad och kräver extra omsorg och respekt. Ungdomskraft behandlar varje dödsbo med diskretion — vi sorterar noggrant, donerar det som går att donera och hanterar allt med värdighet.",
      },
    ],
    content: [
      {
        heading:
          "RUT-avdrag vid dödsbostädning – vad gäller och hur fungerar det? (2026)",
        html: `<p>Att ta hand om ett dödsbo är en av de tyngsta uppgifterna man kan ställas inför. Det är praktiskt krävande, det tar tid, och det sker ofta mitt i en period av sorg. Det många inte vet är att en stor del av arbetet — tömning, <a href="/bortforsling">bortforsling</a> och städning — är RUT-berättigat. Det innebär att du bara behöver betala halva arbetskostnaden. Staten betalar resten.</p>
<p>Den här guiden förklarar exakt vad som gäller för <a href="/guide/vad-ar-rut-avdrag">RUT-avdrag</a> vid dödsbostädning: vad som ingår, vad det kostar efter avdrag, vilka regler som gäller och hur du går tillväga praktiskt.</p>`,
      },
      {
        heading: "Kan man använda RUT-avdrag vid dödsbostädning?",
        html: `<p>Ja. Tömning och städning av dödsbo är RUT-berättigat under vissa förutsättningar. Skatteverket godkänner RUT-avdrag för hushållsnära tjänster utförda i den avlidnes bostad — men det finns specifika regler att känna till.</p>
<p><strong>Grundregeln:</strong> Arbetet måste ha påbörjats eller utförts medan den avlidne fortfarande levde, eller så måste dödsboet äga och förvalta bostaden. Det är dödsboet — alltså arvingarna gemensamt — som är beställare och kan utnyttja avdraget.</p>
<p>I praktiken innebär det att om du anlitar ett företag för att tömma och städa en förälders lägenhet efter ett dödsfall, kan dödsboet använda RUT-avdrag på arbetskostnaden.</p>`,
      },
      {
        heading: "Vad ingår i RUT-avdraget vid dödsbo?",
        html: `<p>Följande tjänster är RUT-berättigade i samband med dödsbostädning:</p>
<ul>
  <li>Tömning av bohag — möbler, kläder, husgeråd ✓</li>
  <li><a href="/bortforsling">Bortforsling</a> av skräp och inventarier ✓</li>
  <li><a href="/tomma-kallare">Källar-</a> och vindstömning ✓</li>
  <li><a href="/tomma-forrad">Förrådsrensning</a> ✓</li>
  <li>Städning av <a href="/tomma-lagenhet">lägenheten</a> eller huset ✓</li>
  <li>Bortforsling av trädgårdsavfall (om det gäller ett hus) ✓</li>
  <li>Transport av dödsboföremål ✓</li>
</ul>
<p><strong>Vad som inte är RUT-berättigat:</strong></p>
<ul>
  <li>Värdering av dödsboets tillgångar</li>
  <li>Juridisk hjälp med bouppteckning</li>
  <li>Försäljning av möbler och föremål</li>
  <li>Material och körsträcka</li>
  <li>Moms</li>
</ul>`,
      },
      {
        heading: "Hur mycket sparar du med RUT-avdrag?",
        html: `<p>RUT-avdraget täcker 50 % av arbetskostnaden. Det kombinerade taket för RUT och ROT tillsammans är 75 000 kr per person och år, varav RUT-avdraget maximalt är 50 000 kr.</p>
<h3>Räkneexempel — typiska dödsbouppdrags</h3>
<table>
  <thead>
    <tr>
      <th>Uppdrag</th>
      <th>Arbetskostnad</th>
      <th>Du betalar</th>
      <th>Staten betalar</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Tömning av 1-rk lägenhet</td>
      <td>4 000 kr</td>
      <td>2 000 kr</td>
      <td>2 000 kr</td>
    </tr>
    <tr>
      <td>Tömning av 3-rk lägenhet</td>
      <td>8 000 kr</td>
      <td>4 000 kr</td>
      <td>4 000 kr</td>
    </tr>
    <tr>
      <td>Tömning av villa med källare</td>
      <td>15 000 kr</td>
      <td>7 500 kr</td>
      <td>7 500 kr</td>
    </tr>
    <tr>
      <td>Komplett tömning + städning</td>
      <td>12 000 kr</td>
      <td>6 000 kr</td>
      <td>6 000 kr</td>
    </tr>
  </tbody>
</table>
<p>RUT-avdraget gäller per person i dödsboet. Om ni är tre arvingar kan varje arving utnyttja avdraget upp till 50 000 kr var — det ger ett samlat utrymme som täcker även stora och komplexa dödsbon. Testa vår <a href="/guide/kalkyl-rut-rot">RUT-kalkylator</a> för att räkna ut din exakta besparing.</p>`,
      },
      {
        heading: "Vem kan använda RUT-avdraget för dödsboet?",
        html: `<p>Det är dödsboet som är skattesubjekt och formell beställare. I praktiken är det den eller de dödsbodelägare som betalar fakturan som utnyttjar avdraget — avdraget fördelas på de personnummer som anges på fakturan.</p>
<h3>Praktiska regler</h3>
<ul>
  <li>Dödsbodelägare måste vara folkbokförda i Sverige och betala skatt här</li>
  <li>Arbetet måste utföras i den avlidnes bostad</li>
  <li>Fakturan ska specificera arbetskostnad och RUT-avdrag separat</li>
  <li>Företaget ansöker om avdraget hos Skatteverket — du behöver inte göra något i deklarationen</li>
</ul>
<p><strong>En viktig detalj:</strong> RUT-avdraget är kopplat till skattebetalning. Om en dödsbodelägare har väldigt låg inkomst eller inte betalar tillräcklig skatt under det aktuella året kan avdraget inte utnyttjas fullt ut. Vid osäkerhet — kontakta Skatteverket. Läs vår guide om <a href="/guide/rut-avdrag-pensionar">RUT-avdrag för pensionärer</a> om någon av delägarna är äldre.</p>`,
      },
      {
        heading: "Hur går du tillväga praktiskt?",
        html: `<p>Processen är enklare än de flesta tror:</p>
<ol>
  <li><strong>Anlita ett godkänt företag</strong> — måste ha F-skattsedel och vara momsregistrerat. <a href="/guide/ungdomskraft-vs-tiptapp">Privatpersoner via appar</a> ger aldrig <a href="/guide/vad-ar-rut-avdrag">RUT-avdrag</a>.</li>
  <li><strong>Ange dödsboets personnummer</strong> — eller de dödsbodelägares personnummer som ska stå på fakturan</li>
  <li><strong>Betala din del</strong> — 50 % av arbetskostnaden direkt till företaget</li>
  <li><strong>Företaget ansöker</strong> — om resten från Skatteverket</li>
  <li><strong>Klart</strong> — du behöver inte göra något mer</li>
</ol>
<p>Du ser alltid exakt vad du betalar innan uppdraget börjar. Hos Ungdomskraft ingår RUT-avdraget automatiskt i prisuppgiften — du ser din nettokostnad direkt.</p>`,
      },
      {
        heading:
          "Vad kostar det att tömma ett dödsbo — med och utan RUT?",
        html: `<p>Priset beror på bostadens storlek, hur mycket som finns kvar, hur många trappor det är och om det finns hiss. Ungdomskraft ger dig ett fast pris baserat på just ditt uppdrag — inga överraskningar efteråt.</p>
<p>Som riktmärke:</p>
<table>
  <thead>
    <tr>
      <th>Bostadstyp</th>
      <th>Utan RUT</th>
      <th>Med RUT (50 %)</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>1-rk lägenhet</td>
      <td>3 000–5 000 kr</td>
      <td>1 500–2 500 kr</td>
    </tr>
    <tr>
      <td>2-rk lägenhet</td>
      <td>5 000–9 000 kr</td>
      <td>2 500–4 500 kr</td>
    </tr>
    <tr>
      <td>3-rk lägenhet</td>
      <td>8 000–14 000 kr</td>
      <td>4 000–7 000 kr</td>
    </tr>
    <tr>
      <td>Villa</td>
      <td>12 000–25 000 kr</td>
      <td>6 000–12 500 kr</td>
    </tr>
  </tbody>
</table>
<p>Dessa siffror avser arbetskostnad. Exakt pris sätts alltid efter en genomgång av uppdragets omfattning.</p>`,
      },
      {
        heading: "Vad händer med sakerna?",
        html: `<p>En av de vanligaste frågorna vi möter: vad händer med det som tömts ur bostaden?</p>
<p>Ungdomskraft hanterar det med respekt och ansvar:</p>
<ul>
  <li><strong>Saker i gott skick</strong> doneras till Myrorna eller liknande välgörenhetsorganisation — de får ett nytt hem istället för att slängas</li>
  <li><strong>Återvinningsbart material</strong> sorteras och lämnas på återvinningscentral</li>
  <li><strong>Skräp och uttjänat material</strong> lämnas till miljöstation enligt kommunens regler</li>
</ul>
<p>Du behöver inte sortera eller förbereda något i förväg. Vi tar hand om hela processen — inklusive <a href="/bortforsling-mobler">bortforsling av möbler</a> och transport.</p>`,
      },
      {
        heading:
          "Ungdomskraft — hjälp med dödsbo i Stockholm och Göteborg",
        html: `<p>Vi förstår att ett dödsbo inte är ett vanligt städuppdrag. Det är en känslig situation som kräver både praktisk effektivitet och mänsklig respekt. Våra medarbetare är vana vid att arbeta i hem med personliga minnen och värdesaker — vi arbetar lugnt, noggrant och diskret.</p>
<p>Vad du alltid får när du anlitar oss för dödsbo:</p>
<ul>
  <li><strong>Fast pris</strong> — du vet exakt vad det kostar innan vi börjar</li>
  <li><strong>RUT-avdrag inräknat</strong> — du betalar bara halva arbetskostnaden</li>
  <li><strong>Anställda medarbetare</strong> — bakgrundskontrollerade, utbildade och försäkrade</li>
  <li><strong>Trygg Hansa-försäkring</strong> — fullständigt ansvar om något mot förmodan skulle hända</li>
  <li><strong>Respektfullt bemötande</strong> — vi förstår att det är mer än ett städjobb</li>
  <li><strong>Miljöansvar</strong> — donation till välgörenhet och laglig återvinning</li>
</ul>
<p>Vi erbjuder även <a href="/bortforsling-dodsbo">bortforsling av dödsbo</a>, <a href="/tomma-dodsbo">dödsbotömning</a>, <a href="/flytthjalp">flytthjälp</a> och <a href="/barhjalp">bärhjälp</a> — allt med RUT-avdrag. Behöver ni flytta saker? Läs om <a href="/guide/rut-avdrag-flytt">RUT-avdrag på flytt</a>.</p>
<p>Vi är verksamma i Stockholm och Göteborg. Boka direkt på <a href="/boka"><strong>ungdomskraft.com/boka</strong></a> eller ring oss om du hellre vill prata med någon om uppdraget.</p>
<p><a href="/boka"><strong>Boka tömning av dödsbo med RUT-avdrag →</strong></a></p>
<p><a href="tel:0851708281"><strong>Ring oss → 08-517 082 81</strong></a></p>`,
      },
      {
        heading: "Vanliga frågor om RUT-avdrag vid dödsbostädning",
        html: `<h3>Gäller RUT-avdrag om den avlidne bodde i hyresrätt?</h3>
<p>Ja. RUT-avdraget gäller oavsett om det är en hyresrätt, bostadsrätt eller villa. Det enda kravet är att arbetet utförs i bostaden och att dödsboet är beställare.</p>

<h3>Kan jag använda RUT-avdraget om jag inte är arvinge?</h3>
<p>Nej. Det är dödsboet och dess delägare som har rätt till avdraget. En vän eller annan person som hjälper till praktiskt kan inte använda avdraget.</p>

<h3>Vad händer om vi är flera arvingar — kan vi dela på avdraget?</h3>
<p>Ja. Avdraget är personbundet och varje dödsbodelägare kan utnyttja sitt eget utrymme upp till 50 000 kr per år. Ju fler delägare, desto mer total avdragsrätt. Ange de personnummer som ska utnyttja avdraget när ni faktureras.</p>

<h3>Måste arbetet utföras före bouppteckning?</h3>
<p>Nej, det finns inget krav på att bouppteckning ska vara klar. Arbetet kan beställas och utföras parallellt med dödsboförvaltningen.</p>

<h3>Kan vi använda RUT för både tömning och städning?</h3>
<p>Ja. Både tömning/<a href="/guide/rut-avdrag-bortforsling">bortforsling</a> och städning är RUT-berättigade. De kan beställas som ett samlat uppdrag eller separat — avdraget gäller i båda fallen.</p>

<h3>Hur lång tid tar det att tömma ett dödsbo?</h3>
<p>Det beror på storlek och mängd. En 2-rumslägenhet tar vanligtvis 3–6 timmar med ett erfaret team. En villa med källare kan ta en heldag. Ungdomskraft ger dig en tidsuppskattning när du bokar.</p>

<h3>Erbjuder ni värdering av dödsboets innehåll?</h3>
<p>Nej, det ingår inte i vår tjänst. För värdering av möbler, konst och föremål rekommenderar vi en auktionsfirma eller värderingsman. Vi kan hjälpa till efter att värderingen är klar.</p>`,
      },
    ],
  },
  {
    slug: "rut-avdrag-mobelmontering",
    title:
      "RUT-avdrag på möbelmontering – så betalar du halva priset (2026)",
    excerpt:
      "Möbelmontering är RUT-berättigat – du betalar bara halva arbetskostnaden. Läs vad som gäller, vad som ingår och boka direkt med RUT inräknat.",
    coverImage: "/guide-rut-avdrag-mobelmontering-cover.png",
    coverImageAlt:
      "Ungdomskraft-medarbetare monterar möbler med RUT-avdrag",
    publishedAt: "2026-03-06",
    tags: ["rut-avdrag", "möbelmontering"],
    metaTitle:
      "RUT-avdrag på möbelmontering – Betala halva priset (2026)",
    metaDescription:
      "Möbelmontering är RUT-berättigat – du betalar bara halva arbetskostnaden. Läs vad som gäller, vad som ingår och boka direkt med RUT inräknat.",
    faq: [
      {
        question: "Kan jag använda RUT för att montera IKEA-kök?",
        answer:
          "Ja. Montering av IKEA-kök är RUT-berättigat. Tänk på att avdraget gäller arbetskostnaden — inte materialet (köksluckor, bänkskivor, beslag).",
      },
      {
        question:
          "Gäller RUT om möbelmonteringen sker på ett fritidshus?",
        answer:
          "Ja, under förutsättning att du äger och bor på fritidshuset åtminstone delvis under året.",
      },
      {
        question:
          "Kan man använda RUT för demontering av gamla möbler?",
        answer:
          "Ja, demontering i samband med att nya möbler monteras räknas som en del av uppdraget och är RUT-berättigat.",
      },
      {
        question:
          "Vad är skillnaden på RUT och ROT för möbler?",
        answer:
          "RUT gäller hushållsnära tjänster som möbelmontering — 50 % avdrag. ROT gäller reparation, ombyggnad och tillbyggnad av fastigheten — 30 % men med ett separat tak på 50 000 kr som ingår i det kombinerade taket om 75 000 kr. Möbelmontering är alltid RUT, inte ROT.",
      },
      {
        question:
          "Kan företag använda RUT-avdrag för möbelmontering?",
        answer:
          "Nej. RUT-avdraget gäller bara privatpersoner. Företag kan inte använda RUT för kontorsmöblering — men privatpersoner som möblerar hemmakontoret kan.",
      },
      {
        question: "Är möbelmontering RUT-berättigat?",
        answer:
          "Ja. Montering av möbler i hemmet räknas som en hushållsnära tjänst och omfattas av RUT-avdraget. Du betalar bara halva arbetskostnaden — företaget ansöker om resten hos Skatteverket.",
      },
      {
        question:
          "Hur mycket kostar möbelmontering med RUT-avdrag?",
        answer:
          "Priset beror på antal möbler och komplexitet. Montering av en IKEA-säng kostar cirka 600 kr efter RUT. En PAX-garderob kostar cirka 1 000 kr efter RUT. Ungdomskraft ger alltid fast pris med RUT inräknat.",
      },
      {
        question:
          "Spelar det någon roll var möblerna är köpta för att få RUT?",
        answer:
          "Nej. RUT-avdraget är kopplat till arbetet, inte till var du köpt möbeln. Det gäller oavsett om möblerna kommer från IKEA, Jysk, Mio, Hem eller ett loppisfynd.",
      },
      {
        question:
          "Gäller RUT-avdrag på möbelmontering i hyresrätt?",
        answer:
          "Ja. RUT-avdraget gäller oavsett boendeform — hyresrätt, bostadsrätt, villa och fritidshus. Det enda kravet är att arbetet utförs i din bostad.",
      },
      {
        question:
          "Kan jag använda RUT för montering av garderober och förvaringssystem?",
        answer:
          "Ja, montering av garderober som PAX, ELVARLI och andra förvaringssystem är RUT-berättigat. Det gäller både fristående och väggmonterade lösningar — så länge de inte räknas som fast inredning (då gäller ROT istället).",
      },
      {
        question:
          "Behöver jag packa upp möblerna innan ni kommer?",
        answer:
          "Nej. Ungdomskrafts medarbetare packar upp, monterar och städar undan emballaget. Du behöver inte förbereda något — vi tar hand om allt.",
      },
      {
        question:
          "Hur snabbt kan ni komma och montera?",
        answer:
          "Vi har ofta tillgänglighet inom några dagar. Boka direkt på ungdomskraft.com/boka eller ring 08-517 082 81 för att hitta en tid som passar.",
      },
      {
        question:
          "Vad händer om en möbel skadas under monteringen?",
        answer:
          "Ungdomskraft har fullständig ansvarsförsäkring via Trygg Hansa. Om något mot förmodan skulle gå sönder under monteringen tar vi fullt ansvar och ersätter eller reparerar skadan.",
      },
      {
        question:
          "Kan Ungdomskraft montera möbler i hela Stockholm och Göteborg?",
        answer:
          "Ja. Vi är verksamma i hela Stockholmsområdet och Göteborgsområdet inklusive kranskommuner. Fast pris med RUT-avdrag och Trygg Hansa-försäkring ingår alltid.",
      },
    ],
    content: [
      {
        heading:
          "RUT-avdrag på möbelmontering – så betalar du halva priset (2026)",
        html: `<p>Köpt ny möbel men inte tid eller lust att montera? <a href="/mobelmontering">Möbelmontering</a> är RUT-berättigat — det betyder att du betalar halva arbetskostnaden och staten betalar resten. Det gäller oavsett om det är ett IKEA-kök, en garderob, en säng eller ett helt kontorslandskap. Använd gärna vår <a href="/guide/kalkyl-rut-rot">RUT-kalkylator</a> för att räkna ut din besparing.</p>`,
      },
      {
        heading: "Är möbelmontering RUT-berättigat?",
        html: `<p>Ja. Montering av möbler i hemmet räknas som en hushållsnära tjänst och omfattas av <a href="/guide/vad-ar-rut-avdrag">RUT-avdraget</a>. Det gäller montering av köpta möbler i din bostad — oavsett om du bor i hyresrätt, bostadsrätt eller villa.</p>
<p>Det vanligaste missförståndet är att folk tror att möbelmontering är ROT — alltså renoveringsavdrag. Det stämmer inte. Möbelmontering är RUT, vilket innebär 50 % avdrag på arbetskostnaden.</p>`,
      },
      {
        heading: "Vad ingår i RUT-avdraget vid möbelmontering?",
        html: `<p>Följande är RUT-berättigat:</p>
<ul>
  <li>Montering av IKEA-möbler ✓</li>
  <li>Montering av garderober och förvaringssystem ✓</li>
  <li>Montering av sängar och sänggavlar ✓</li>
  <li>Montering av skrivbord och kontorsmöbler ✓</li>
  <li>Montering av hyllor och bokhyllor ✓</li>
  <li>Montering av TV-bänkar och mediamöbler ✓</li>
  <li>Montering av köksmöbler och badrumsmöbler ✓</li>
  <li>Demontering av gamla möbler inför montering av nya ✓</li>
</ul>
<p>Behöver du forsla bort de gamla möblerna? <a href="/bortforsling-mobler">Bortforsling av möbler</a> är också RUT-berättigat.</p>
<p><strong>Vad som inte ingår:</strong></p>
<ul>
  <li>Material och skruvar ingår inte i avdraget</li>
  <li>Montering av inbyggda lösningar som räknas som ROT (t.ex. infällda spotlights, fast kakling)</li>
  <li>Moms</li>
  <li>Körsträcka och parkering</li>
</ul>`,
      },
      {
        heading: "Hur mycket sparar du?",
        html: `<p><a href="/guide/vad-ar-rut-avdrag">RUT-avdraget</a> täcker 50 % av arbetskostnaden. Det kombinerade taket för RUT och ROT är 75 000 kr per person och år — varav max 50 000 kr får vara RUT.</p>
<h3>Räkneexempel</h3>
<table>
  <thead>
    <tr>
      <th>Uppdrag</th>
      <th>Arbetskostnad</th>
      <th>Du betalar</th>
      <th>Staten betalar</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Montering av IKEA-säng</td>
      <td>1 200 kr</td>
      <td>600 kr</td>
      <td>600 kr</td>
    </tr>
    <tr>
      <td>Montering av PAX-garderob</td>
      <td>2 000 kr</td>
      <td>1 000 kr</td>
      <td>1 000 kr</td>
    </tr>
    <tr>
      <td>Montering av kök (IKEA)</td>
      <td>6 000 kr</td>
      <td>3 000 kr</td>
      <td>3 000 kr</td>
    </tr>
    <tr>
      <td>Möblering av helt sovrum</td>
      <td>3 500 kr</td>
      <td>1 750 kr</td>
      <td>1 750 kr</td>
    </tr>
  </tbody>
</table>
<p>För ett vanligt IKEA-shoppingpass med 4–5 möbler är RUT-avdraget ofta den skillnad som gör det självklart att anlita hjälp istället för att tillbringa en hel lördag med en insexkantsnyckel.</p>`,
      },
      {
        heading:
          "IKEA, Jysk, Mio — spelar det någon roll varifrån möbeln kommer?",
        html: `<p>Nej. RUT-avdraget är kopplat till <strong>arbetet</strong>, inte till var du köpt möbeln. Det spelar ingen roll om möblerna kommer från IKEA, Jysk, Mio, Hem eller ett loppisfynd — <a href="/mobelmontering">montering</a> i din bostad är alltid RUT-berättigat.</p>
<p>Däremot är det viktigt att det är <strong>ett registrerat företag</strong> som utför monteringen. En kompis som hjälper till för en slant, eller en privatperson via en app, ger aldrig RUT-avdrag — läs mer om varför i vår <a href="/guide/ungdomskraft-vs-tiptapp">jämförelse Ungdomskraft vs Tiptapp</a>. Du behöver anlita ett momsregistrerat företag med F-skattsedel. Handlar du möbler från IKEA? Kolla in vår <a href="/hemleverans-ikea">hemleverans från IKEA</a> — vi kör hem och monterar samma dag.</p>`,
      },
      {
        heading: "Hur använder du RUT-avdraget i praktiken?",
        html: `<p>Enkelt:</p>
<ol>
  <li><strong>Boka ett godkänt företag</strong> — kontrollera att de har F-skattsedel</li>
  <li><strong>Ange ditt personnummer</strong> vid bokning</li>
  <li><strong>Betala bara din halva</strong> — 50 % av arbetskostnaden direkt till företaget</li>
  <li><strong>Företaget ansöker</strong> om resten från Skatteverket</li>
  <li><strong>Klart</strong> — inget att göra i deklarationen</li>
</ol>
<p>Hos Ungdomskraft ser du priset med RUT inräknat direkt när du bokar. Du betalar aldrig mer än vad som visas — inga överraskningar på fakturan.</p>`,
      },
      {
        heading:
          "RUT-avdrag på möbelmontering i hyresrätt — gäller det?",
        html: `<p>Ja. RUT-avdraget gäller oavsett boendeform. Hyresrätt, bostadsrätt, villa och fritidshus omfattas alla — det enda kravet är att arbetet utförs i din bostad.</p>`,
      },
      {
        heading:
          "Boka möbelmontering med RUT-avdrag hos Ungdomskraft",
        html: `<p>Vi monterar möbler i <a href="/mobelmontering-stockholm">Stockholm</a> och <a href="/mobelmontering-goteborg">Göteborg</a> — allt från en enstaka stol till ett helt hem. Våra medarbetare är vana vid IKEA:s monteringsanvisningar och arbetar snabbt och noggrant. Du slipper leta efter skruvar, tyda instruktioner och slita ont om ryggen.</p>
<p>Vad du alltid får:</p>
<ul>
  <li><strong>Fast pris med RUT inräknat</strong> — du ser din nettokostnad direkt</li>
  <li><strong>Anställda medarbetare</strong> — inte en slumpmässig privatperson</li>
  <li><strong>Trygg Hansa-försäkring</strong> — om något mot förmodan skulle gå sönder</li>
  <li><strong>Ingen app att ladda ner</strong> — boka direkt i mobilen på <a href="/boka">ungdomskraft.com/boka</a></li>
  <li><strong>Eller ring oss</strong> — så bokar vi det åt dig</li>
</ul>
<p>Sedan 2014 har vi genomfört över 50 000 uppdrag med ett snittbetyg på 4,9/5. Varje uppdrag utförs av ungdomar som får sitt första jobb — du gör en insats för samhället samtidigt som soffan monteras.</p>
<p>Vi erbjuder även <a href="/flytthjalp">flytthjälp</a>, <a href="/barhjalp">bärhjälp</a> och <a href="/bortforsling">bortforsling</a> — allt med RUT-avdrag. Läs om <a href="/guide/rut-avdrag-flytt">RUT-avdrag på flytt</a>, <a href="/guide/rut-avdrag-bortforsling">RUT-avdrag på bortforsling</a> eller <a href="/guide/rut-avdrag-pensionar">RUT-avdrag för pensionärer</a>.</p>
<p><a href="/boka"><strong>Boka möbelmontering med RUT-avdrag →</strong></a></p>
<p><a href="tel:0851708281"><strong>Ring oss → 08-517 082 81</strong></a></p>`,
      },
      {
        heading: "Vanliga frågor om RUT-avdrag på möbelmontering",
        html: `<h3>Kan jag använda RUT för att montera IKEA-kök?</h3>
<p>Ja. Montering av IKEA-kök är RUT-berättigat. Tänk på att avdraget gäller arbetskostnaden — inte materialet (köksluckor, bänkskivor, beslag).</p>

<h3>Gäller RUT om möbelmonteringen sker på ett fritidshus?</h3>
<p>Ja, under förutsättning att du äger och bor på fritidshuset åtminstone delvis under året.</p>

<h3>Kan man använda RUT för demontering av gamla möbler?</h3>
<p>Ja, demontering i samband med att nya möbler monteras räknas som en del av uppdraget och är RUT-berättigat.</p>

<h3>Vad är skillnaden på RUT och ROT för möbler?</h3>
<p>RUT gäller hushållsnära tjänster som <a href="/mobelmontering">möbelmontering</a> — 50 % avdrag. ROT gäller reparation, ombyggnad och tillbyggnad av fastigheten — 30 % men med ett separat tak på 50 000 kr som ingår i det kombinerade taket om 75 000 kr. Möbelmontering är alltid RUT, inte ROT.</p>

<h3>Kan företag använda RUT-avdrag för möbelmontering?</h3>
<p>Nej. RUT-avdraget gäller bara privatpersoner. Företag kan inte använda RUT för kontorsmöblering — men privatpersoner som möblerar hemmakontoret kan.</p>`,
      },
    ],
  },
  {
    slug: "kalkyl-rut-rot",
    title:
      "RUT & ROT-kalkylator – Räkna ut ditt avdrag (2026)",
    excerpt:
      "Räkna ut hur mycket RUT- och ROT-avdrag du kan använda baserat på din lön och skattesats. Kalkylator + tabeller för alla lönenivåer.",
    coverImage: "/guide-kalkyl-rut-rot-cover.png",
    coverImageAlt:
      "Ungdomskraft – RUT- och ROT-kalkylator för att räkna ut ditt avdrag",
    publishedAt: "2026-03-06",
    tags: ["rut-avdrag"],
    metaTitle:
      "RUT ROT-kalkylator 2026 – Räkna ut hur mycket du kan dra av",
    metaDescription:
      "Räkna ut hur mycket RUT- och ROT-avdrag du kan använda baserat på din lön och skattesats. Kalkylator + tabeller för alla lönenivåer.",
    faq: [
      {
        question:
          "Hur vet jag hur mycket RUT-avdrag jag har kvar i år?",
        answer:
          "Logga in på Skatteverkets Mina Sidor på skatteverket.se. Under \"Husarbete\" ser du exakt hur mycket av ditt utrymme som är utnyttjat och hur mycket som återstår.",
      },
      {
        question: "Kan jag använda RUT om jag är pensionär?",
        answer:
          "Ja. Sedan 2021 gäller samma tak för alla vuxna oavsett ålder — 50 000 kr i RUT per person och år. Förutsättningen är att du betalar tillräckligt med skatt för att täcka avdraget.",
      },
      {
        question:
          "Kan jag använda RUT om jag jobbar deltid?",
        answer:
          "Ja, men ditt utrymme begränsas av hur mycket skatt du faktiskt betalar. Vid låg inkomst kan det totala utrymmet vara lägre än 50 000 kr — se tabellerna ovan för en uppskattning.",
      },
      {
        question:
          "Kan jag använda RUT och ROT samma år?",
        answer:
          "Ja, men det kombinerade taket är 75 000 kr. Du kan till exempel använda 30 000 kr i ROT och 45 000 kr i RUT under samma år.",
      },
      {
        question:
          "Vad händer om jag använt för mycket avdrag?",
        answer:
          "Om Skatteverket beviljar mer avdrag än du har rätt till — till exempel för att din inkomst var lägre än beräknat — återkräver de mellanskillnaden i deklarationen. Du kan då få restskatt. Kontrollera ditt utrymme på Mina Sidor om du är osäker.",
      },
      {
        question: "Vad är RUT-avdrag?",
        answer:
          "RUT står för Rengöring, Underhåll och Tvätt. Det är en skattereduktion på arbetskostnaden för hushållsnära tjänster som städning, flytt, möbelmontering och trädgårdsarbete (50 %) samt bortforsling till återanvändning (25 %). Max 50 000 kr per person och år.",
      },
      {
        question: "Vad är ROT-avdrag?",
        answer:
          "ROT står för Reparation, Ombyggnad och Tillbyggnad. Det är en skattereduktion på 30 % av arbetskostnaden för renoveringsarbeten i bostaden. Max 50 000 kr per person och år. Det kombinerade taket för RUT och ROT är 75 000 kr.",
      },
      {
        question:
          "Hur mycket RUT-avdrag kan jag få per år?",
        answer:
          "Maximalt 50 000 kr per person och år. Men avdraget är kopplat till din skatt — du kan aldrig få mer tillbaka än vad du betalat in i skatt under året.",
      },
      {
        question:
          "Är avdraget personbundet eller per hushåll?",
        answer:
          "Personbundet. Varje vuxen i hushållet har ett eget utrymme. Är ni två vuxna kan ni tillsammans utnyttja upp till 100 000 kr i RUT.",
      },
      {
        question:
          "Spelar det någon roll vilket år jag betalar fakturan?",
        answer:
          "Ja. Det är betalningsdatumet som styr — inte när arbetet utförs. Betalar du en faktura i januari 2027 räknas det mot 2027 års avdrag, även om arbetet gjordes i december 2026.",
      },
      {
        question:
          "Behöver jag göra något i deklarationen för RUT-avdrag?",
        answer:
          "Nej. Det är företaget du anlitar som ansöker om utbetalning från Skatteverket. Avdraget syns sedan automatiskt i din deklaration.",
      },
      {
        question:
          "Kan privatpersoner via Tiptapp ge RUT-avdrag?",
        answer:
          "Nej. RUT-avdrag kräver att utföraren är ett registrerat företag med F-skattsedel. Privatpersoner via appar som Tiptapp ger aldrig RUT-avdrag.",
      },
      {
        question:
          "Vilka tjänster hos Ungdomskraft är RUT-berättigade?",
        answer:
          "Flytt, bortforsling, möbelmontering, trädgårdsarbete, källar- och vindstömning, dödsbotömning och bärhjälp. Allt hanteras med automatiskt RUT-avdrag på fakturan.",
      },
      {
        question:
          "Hur påverkar kommunalskatten mitt RUT-utrymme?",
        answer:
          "Högre kommunalskatt innebär att du betalar mer skatt, vilket ger ett större utrymme för RUT och ROT. Vid låga inkomster gör skillnaden störst effekt — se tabellerna i guiden för exakta siffror per skattesats.",
      },
    ],
    content: [
      {
        heading:
          "RUT & ROT-kalkylator – Räkna ut ditt avdrag (2026)",
        html: `<p>Hur mycket <a href="/guide/vad-ar-rut-avdrag">RUT</a>- eller ROT-avdrag du faktiskt kan använda beror på hur mycket skatt du betalar. Avdraget är en skattereduktion — inte ett bidrag — vilket betyder att du aldrig kan få mer tillbaka än vad du betalat in i skatt under året.</p>
<p>Använd kalkylatorn nedan för att se exakt vad som gäller för dig.</p>`,
      },
      {
        heading: "Räkna ut ditt RUT- och ROT-avdrag",
        html: `<div data-component="rut-rot-calculator"></div>`,
      },
      {
        heading: "Vad är RUT- och ROT-avdrag?",
        html: `<p>RUT och ROT är två olika typer av skattereduktion för arbete i och kring hemmet.</p>
<p><strong>RUT</strong> — Rengöring, Underhåll och Tvätt — gäller hushållsnära tjänster som städning, <a href="/flytthjalp">flytt</a>, <a href="/mobelmontering">möbelmontering</a> och trädgårdsarbete (50 % avdrag) samt <a href="/bortforsling">bortforsling</a> till återanvändning (25 % avdrag). Max 50 000 kr per person och år.</p>
<p><strong>ROT</strong> — Reparation, Ombyggnad och Tillbyggnad — gäller renoveringsarbeten i bostaden som målning, badrumsrenovering och VVS. Du får 30 % av arbetskostnaden i avdrag, upp till 50 000 kr per person och år.</p>
<p>Det kombinerade taket för RUT och ROT tillsammans är <strong>75 000 kr per person och år</strong>. Det betyder att om du utnyttjar 40 000 kr i ROT återstår maximalt 35 000 kr i möjligt RUT — inte 50 000 kr.</p>`,
      },
      {
        heading: "Varför spelar lönen roll?",
        html: `<p>Avdraget är kopplat till din faktiska skatt. Om du tjänar 10 000 kr i månaden betalar du kanske 12 000–14 000 kr i skatt per år efter grundavdrag och jobbskatteavdrag. Det är också det maximala avdrag du kan utnyttja — oavsett vad tabellerna säger.</p>
<p>Tjänar du däremot 30 000 kr per månad eller mer kan du i de flesta fall utnyttja hela RUT-utrymmet på 50 000 kr.</p>
<p>Tabellerna nedan visar detta tydligt per lönenivå och skattesats.</p>`,
      },
      {
        heading: "Tabeller: max RUT och ROT per lönenivå",
        html: `<p>Tabellerna är beräknade med hänsyn till grundavdrag och jobbskatteavdrag. Siffrorna är uppskattningar — din exakta skattesituation kan skilja sig beroende på andra avdrag, kapitalinkomster eller om du har flera arbetsgivare. Kontrollera alltid med Skatteverkets Mina Sidor hur mycket du har kvar.</p>

<h3>Låg kommunalskatt (30 %)</h3>
<table>
  <thead>
    <tr>
      <th>Månadslön</th>
      <th>Approx. skatt/år</th>
      <th>Max RUT</th>
      <th>Max ROT</th>
      <th>Kombinerat max</th>
    </tr>
  </thead>
  <tbody>
    <tr><td>10 000 kr</td><td>~12 500 kr</td><td>12 500 kr</td><td>12 500 kr</td><td>12 500 kr</td></tr>
    <tr><td>15 000 kr</td><td>~23 000 kr</td><td>23 000 kr</td><td>23 000 kr</td><td>23 000 kr</td></tr>
    <tr><td>20 000 kr</td><td>~36 000 kr</td><td>36 000 kr</td><td>36 000 kr</td><td>36 000 kr</td></tr>
    <tr><td>25 000 kr</td><td>~49 500 kr</td><td>49 500 kr</td><td>49 500 kr</td><td>49 500 kr</td></tr>
    <tr><td>30 000 kr</td><td>~63 000 kr</td><td>50 000 kr</td><td>50 000 kr</td><td>63 000 kr</td></tr>
    <tr><td>35 000 kr</td><td>~77 000 kr</td><td>50 000 kr</td><td>50 000 kr</td><td>75 000 kr</td></tr>
    <tr><td>40 000 kr</td><td>~91 000 kr</td><td>50 000 kr</td><td>50 000 kr</td><td>75 000 kr</td></tr>
    <tr><td>45 000 kr+</td><td>~109 000 kr+</td><td>50 000 kr</td><td>50 000 kr</td><td>75 000 kr</td></tr>
  </tbody>
</table>

<h3>Genomsnittlig kommunalskatt (32,2 %)</h3>
<table>
  <thead>
    <tr>
      <th>Månadslön</th>
      <th>Approx. skatt/år</th>
      <th>Max RUT</th>
      <th>Max ROT</th>
      <th>Kombinerat max</th>
    </tr>
  </thead>
  <tbody>
    <tr><td>10 000 kr</td><td>~13 000 kr</td><td>13 000 kr</td><td>13 000 kr</td><td>13 000 kr</td></tr>
    <tr><td>15 000 kr</td><td>~25 000 kr</td><td>25 000 kr</td><td>25 000 kr</td><td>25 000 kr</td></tr>
    <tr><td>20 000 kr</td><td>~38 500 kr</td><td>38 500 kr</td><td>38 500 kr</td><td>38 500 kr</td></tr>
    <tr><td>25 000 kr</td><td>~53 000 kr</td><td>50 000 kr</td><td>50 000 kr</td><td>53 000 kr</td></tr>
    <tr><td>30 000 kr</td><td>~68 000 kr</td><td>50 000 kr</td><td>50 000 kr</td><td>68 000 kr</td></tr>
    <tr><td>35 000 kr</td><td>~82 500 kr</td><td>50 000 kr</td><td>50 000 kr</td><td>75 000 kr</td></tr>
    <tr><td>40 000 kr</td><td>~97 000 kr</td><td>50 000 kr</td><td>50 000 kr</td><td>75 000 kr</td></tr>
    <tr><td>45 000 kr+</td><td>~117 000 kr+</td><td>50 000 kr</td><td>50 000 kr</td><td>75 000 kr</td></tr>
  </tbody>
</table>

<h3>Hög kommunalskatt (34,5 %)</h3>
<table>
  <thead>
    <tr>
      <th>Månadslön</th>
      <th>Approx. skatt/år</th>
      <th>Max RUT</th>
      <th>Max ROT</th>
      <th>Kombinerat max</th>
    </tr>
  </thead>
  <tbody>
    <tr><td>10 000 kr</td><td>~14 000 kr</td><td>14 000 kr</td><td>14 000 kr</td><td>14 000 kr</td></tr>
    <tr><td>15 000 kr</td><td>~26 500 kr</td><td>26 500 kr</td><td>26 500 kr</td><td>26 500 kr</td></tr>
    <tr><td>20 000 kr</td><td>~41 500 kr</td><td>41 500 kr</td><td>41 500 kr</td><td>41 500 kr</td></tr>
    <tr><td>25 000 kr</td><td>~57 000 kr</td><td>50 000 kr</td><td>50 000 kr</td><td>57 000 kr</td></tr>
    <tr><td>30 000 kr</td><td>~72 500 kr</td><td>50 000 kr</td><td>50 000 kr</td><td>72 500 kr</td></tr>
    <tr><td>35 000 kr</td><td>~88 000 kr</td><td>50 000 kr</td><td>50 000 kr</td><td>75 000 kr</td></tr>
    <tr><td>40 000 kr</td><td>~104 000 kr</td><td>50 000 kr</td><td>50 000 kr</td><td>75 000 kr</td></tr>
    <tr><td>45 000 kr+</td><td>~125 000 kr+</td><td>50 000 kr</td><td>50 000 kr</td><td>75 000 kr</td></tr>
  </tbody>
</table>
<p><em>Beräkningarna inkluderar grundavdrag och jobbskatteavdrag. Exakta belopp varierar — använd Skatteverkets Mina Sidor för att se din faktiska skattenivå.</em></p>`,
      },
      {
        heading: "Tre viktiga saker att veta om avdraget",
        html: `<p><strong>1. Avdraget är personbundet.</strong> Varje person i hushållet har ett eget utrymme på upp till 50 000 kr RUT. Är ni två vuxna kan ni tillsammans utnyttja upp till 100 000 kr i RUT — förutsatt att ni båda betalar tillräckligt med skatt.</p>
<p><strong>2. Det är betalningsdatumet som styr.</strong> Det är när du betalar fakturan som avgör vilket år avdraget räknas mot — inte när jobbet utförs. Betalar du en faktura i januari 2027 räknas det mot 2027, även om arbetet utfördes i december 2026.</p>
<p><strong>3. Du behöver inte göra något i deklarationen.</strong> Det är företaget du anlitar som ansöker om utbetalning från Skatteverket. Avdraget syns sedan automatiskt i din deklaration. Förutsättningen är att du anlitar ett godkänt företag med F-skattsedel — <a href="/guide/ungdomskraft-vs-tiptapp">privatpersoner via appar som Tiptapp</a> ger aldrig RUT-avdrag.</p>`,
      },
      {
        heading: "Vilka tjänster räknas som RUT?",
        html: `<p>Det är vanligare än man tror att kvalificera sig för RUT-avdrag. Följande tjänster som Ungdomskraft erbjuder är alla RUT-berättigade:</p>
<ul>
  <li><a href="/flytthjalp">Flytt och flytthjälp</a> ✓ — läs om <a href="/guide/rut-avdrag-flytt">RUT-avdrag på flytt</a></li>
  <li><a href="/bortforsling">Bortforsling av skräp och möbler</a> ✓ — läs om <a href="/guide/rut-avdrag-bortforsling">RUT-avdrag på bortforsling</a></li>
  <li><a href="/mobelmontering">Möbelmontering</a> ✓ — läs om <a href="/guide/rut-avdrag-mobelmontering">RUT-avdrag på möbelmontering</a></li>
  <li>Trädgårdsarbete och tomtröjning ✓</li>
  <li><a href="/tomma-kallare">Källar-</a>, vind- och förrådstömning ✓</li>
  <li><a href="/guide/rut-avdrag-dodsbostadning">Tömning av dödsbo</a> / <a href="/tomma-dodsbo">dödsbotömning</a> ✓</li>
  <li><a href="/barhjalp">Bärhjälp</a> och <a href="/transporthjalp">transporthjälp</a> ✓</li>
</ul>
<p>Städning är också RUT-berättigat — men det är en tjänst vi inte erbjuder. Är du pensionär? Läs vår guide om <a href="/guide/rut-avdrag-pensionar">RUT-avdrag för pensionärer</a>.</p>`,
      },
      {
        heading: "Boka en RUT-berättigad tjänst hos Ungdomskraft",
        html: `<p>Vi hanterar RUT-avdraget automatiskt. Du anger ditt personnummer vid bokning, vi drar av din andel direkt på fakturan och ansöker om resten från Skatteverket. Du betalar bara halva arbetskostnaden — inget att hålla koll på i deklarationen. Vi finns i <a href="/bortforsling-stockholm">Stockholm</a> och <a href="/flytthjalp-stockholm">Göteborg</a> med lokal service.</p>
<p><a href="/boka"><strong>Boka med RUT-avdrag →</strong></a></p>
<p><a href="tel:0851708281"><strong>Ring oss → 08-517 082 81</strong></a></p>`,
      },
      {
        heading: "Vanliga frågor om RUT- och ROT-avdrag",
        html: `<h3>Hur vet jag hur mycket RUT-avdrag jag har kvar i år?</h3>
<p>Logga in på Skatteverkets Mina Sidor på skatteverket.se. Under "Husarbete" ser du exakt hur mycket av ditt utrymme som är utnyttjat och hur mycket som återstår.</p>

<h3>Kan jag använda RUT om jag är pensionär?</h3>
<p>Ja. Sedan 2021 gäller samma tak för alla vuxna oavsett ålder — 50 000 kr i RUT per person och år. Förutsättningen är att du betalar tillräckligt med skatt för att täcka avdraget.</p>

<h3>Kan jag använda RUT om jag jobbar deltid?</h3>
<p>Ja, men ditt utrymme begränsas av hur mycket skatt du faktiskt betalar. Vid låg inkomst kan det totala utrymmet vara lägre än 50 000 kr — se tabellerna ovan för en uppskattning.</p>

<h3>Kan jag använda RUT och ROT samma år?</h3>
<p>Ja, men det kombinerade taket är 75 000 kr. Du kan till exempel använda 30 000 kr i ROT och 45 000 kr i RUT under samma år.</p>

<h3>Vad händer om jag använt för mycket avdrag?</h3>
<p>Om Skatteverket beviljar mer avdrag än du har rätt till — till exempel för att din inkomst var lägre än beräknat — återkräver de mellanskillnaden i deklarationen. Du kan då få restskatt. Kontrollera ditt utrymme på Mina Sidor om du är osäker.</p>`,
      },
    ],
  },
  {
    slug: "rut-avdrag-pensionar",
    title: "RUT-avdrag för pensionärer – vad gäller 2026?",
    excerpt:
      "Pensionärer har samma rätt till RUT-avdrag som alla andra — 50 % av arbetskostnaden, upp till 50 000 kr per person och år. Läs vad som gäller, vad du kan anlita hjälp med och hur du kontrollerar ditt utrymme.",
    coverImage: "/guide-rut-avdrag-pensionar-cover.png",
    coverImageAlt:
      "Ungdomskraft-medarbetare utför trädgårdsarbete — hushållsnära tjänst med RUT-avdrag för pensionärer",
    publishedAt: "2026-03-06",
    tags: ["rut-avdrag", "flytt", "bortforsling", "dödsbo"],
    metaTitle:
      "RUT-avdrag för pensionärer – Regler, belopp och vad som ingår (2026)",
    metaDescription:
      "Pensionärer har samma rätt till RUT-avdrag som alla andra – 50 % av arbetskostnaden upp till 50 000 kr. Läs vad som gäller och vad du kan anlita hjälp med.",
    faq: [
      {
        question: "Har pensionärer rätt till mer RUT-avdrag än yngre?",
        answer:
          "Nej, inte längre. Sedan 2021 gäller samma tak för alla — 50 000 kr i RUT per person och år oavsett ålder. Det gamla systemet med ett högre tak för äldre avskaffades.",
      },
      {
        question:
          "Kan jag använda RUT-avdrag om jag har garantipension?",
        answer:
          "Det beror på om du betalar tillräcklig skatt. Garantipension är låg och skatten på den kan vara minimal. Kontrollera din faktiska skatt på Skatteverkets Mina Sidor innan du räknar med fullt avdrag.",
      },
      {
        question:
          "Gäller RUT-avdrag om jag bor i en seniorlägenhet?",
        answer:
          "Ja, RUT-avdraget gäller oavsett boendeform — hyresrätt, bostadsrätt, villa eller seniorlägenhet — förutsatt att det är din bostad.",
      },
      {
        question:
          "Kan mina vuxna barn boka tjänster åt mig och använda mitt RUT-avdrag?",
        answer:
          "Nej. RUT-avdraget är personbundet. Det är du som bostadsinnehavare och skattebetalare som kan utnyttja avdraget — inte ett barn eller annan anhörig. Fakturan måste ställas ut med ditt personnummer.",
      },
      {
        question:
          "Vad händer med RUT-avdraget om jag dör under året?",
        answer:
          "RUT-avdraget är kopplat till ditt skatteår. Arbete som utförts och betalats innan dödsdagen kan utnyttja avdraget. Dödsboet kan i vissa fall använda avdraget för arbete utfört i bostaden.",
      },
      {
        question:
          "Kan jag använda RUT för trädgård vid min sommarstuga?",
        answer:
          "Ja, förutsatt att du äger och bor på fritidshuset åtminstone del av året. RUT gäller för din permanenta bostad och fritidshus du själv använder.",
      },
      {
        question: "Hur mycket skatt måste jag betala för att få fullt RUT-avdrag?",
        answer:
          "Du måste betala minst lika mycket i skatt som det avdrag du vill utnyttja. Om din skatt för året är 20 000 kr kan du max utnyttja 20 000 kr i RUT — inte 50 000 kr. Kontrollera din skatt på Skatteverkets Mina Sidor.",
      },
      {
        question: "Är flytt RUT-berättigat för pensionärer?",
        answer:
          "Ja, flytt av bohag och personliga tillhörigheter är RUT-berättigat oavsett ålder. Du betalar bara 50 % av arbetskostnaden. Det gäller allt från en hel lägenhetsflytt till att bära ner en soffa.",
      },
      {
        question: "Är tömning av dödsbo RUT-berättigat?",
        answer:
          "Ja, tömning och bortforsling av dödsbo är RUT-berättigat. Dödsbodelägare kan utnyttja avdraget.",
      },
      {
        question: "Kan jag kombinera RUT och ROT under samma år?",
        answer:
          "Ja. Det kombinerade taket för RUT och ROT är 75 000 kr per person och år — varav max 50 000 kr får vara ROT. Du kan alltså använda till exempel 25 000 kr i ROT och 50 000 kr i RUT under samma år.",
      },
      {
        question: "Behöver jag deklarera RUT-avdraget själv?",
        answer:
          "Nej. Företaget ansöker om avdraget från Skatteverket och det syns automatiskt i din deklaration. Du behöver inte spara kvitton eller fylla i några blanketter.",
      },
      {
        question: "Vad räknas som arbetskostnad i RUT-avdraget?",
        answer:
          "Bara själva arbetet ger RUT-avdrag — alltså tid som personalen lägger på att utföra tjänsten. Material, transport av varor, avgifter och moms på material ingår inte i avdraget.",
      },
      {
        question: "Kan jag få RUT-avdrag via Tiptapp eller andra gig-appar?",
        answer:
          "Nej. RUT-avdraget kräver att arbetet utförs av ett företag med F-skattsedel. Privatpersoner som utför jobb via gig-appar kan aldrig erbjuda RUT-avdrag.",
      },
      {
        question: "Hur kontrollerar jag mitt RUT-utrymme?",
        answer:
          "Logga in på Skatteverkets Mina Sidor. Under \"Husarbete\" ser du ditt aktuella utrymme och hur mycket som redan är använt under året.",
      },
      {
        question: "Gäller RUT-avdraget även för min partner?",
        answer:
          "Ja. RUT-avdraget är personbundet. Om ni är två i hushållet kan vardera person utnyttja upp till 50 000 kr, förutsatt att ni båda betalar tillräcklig skatt. Det ger ett kombinerat utrymme på upp till 100 000 kr per år.",
      },
      {
        question: "Vad händer om jag använder mer RUT-avdrag än jag har rätt till?",
        answer:
          "Om Skatteverket beviljar mer avdrag än din skatt täcker återkräver de mellanskillnaden i deklarationen. Du kan då få restskatt. Kontrollera alltid ditt utrymme på Mina Sidor innan du bokar stora uppdrag.",
      },
    ],
    content: [
      {
        heading: "RUT-avdrag för pensionärer — vad gäller 2026?",
        html: `<p>Ja, pensionärer har rätt till <a href="/guide/vad-ar-rut-avdrag">RUT-avdrag</a>. Sedan 2021 gäller samma regler och samma tak för alla vuxna oavsett ålder — 50 % av arbetskostnaden, upp till 50 000 kr per person och år. Det gamla systemet med ett lägre tak för yngre och ett högre för äldre finns inte längre.</p>
<p>Det enda kravet är att du betalar tillräckligt med skatt i Sverige för att avdraget ska kunna utnyttjas. Hur mycket det är beror på din pension och eventuell annan inkomst.</p>`,
      },
      {
        heading: "Samma regler som för alla — men en viktig skillnad",
        html: `<p>Reglerna för RUT-avdrag är desamma oavsett om du är 35 eller 75 år. Det som skiljer pensionärer från förvärvsarbetande är inte reglerna — det är skatten.</p>
<p>En pensionär med låg pension betalar kanske 15 000–25 000 kr i skatt per år. Det är också det maximala du kan utnyttja i avdrag. Avdraget är en skattereduktion, inte ett bidrag — du kan aldrig få mer tillbaka än vad du betalat in.</p>
<p>En pensionär med god pension — eller som kombinerar pension med kapitalinkomster eller lönearbete — kan däremot utnyttja hela utrymmet på 50 000 kr.</p>
<p><strong>Hur vet du hur mycket du kan utnyttja?</strong> Logga in på Skatteverkets Mina Sidor. Under "Husarbete" ser du ditt aktuella utrymme och hur mycket som eventuellt redan är använt under året. Du kan också testa vår <a href="/guide/kalkyl-rut-rot">RUT-kalkylator</a> för att räkna ut din besparing.</p>`,
      },
      {
        heading: "Vad kan du anlita hjälp med via RUT som pensionär?",
        html: `<p>De flesta tjänster som är praktiskt relevanta för pensionärer är RUT-berättigade. Det handlar framför allt om:</p>
<p><strong>Flytt och bärhjälp</strong></p>
<p>Att flytta till ett mindre boende, flytta in på seniorboende eller sälja villan är situationer som många pensionärer befinner sig i. <a href="/flytthjalp">Flytt</a> och <a href="/barhjalp">bärhjälp</a> är fullt RUT-berättigat — oavsett om det är en hel lägenhetsflytt eller bara hjälp att bära ner tunga möbler till sopcontainern. Läs mer om <a href="/guide/rut-avdrag-flytt">RUT-avdrag på flytt</a>.</p>
<p><strong>Bortforsling</strong></p>
<p>Att rensa ut efter ett långt liv i samma bostad innebär ofta att det finns möbler, vitvaror och prylar som ska bort. <a href="/bortforsling">Bortforsling</a> av <a href="/bortforsling-mobler">möbler</a> och skräp kan ge 25 % RUT-avdrag — men bara om sakerna lämnas till återanvändning. Läs mer om <a href="/guide/rut-avdrag-bortforsling">RUT-avdrag på bortforsling</a>.</p>
<p><strong>Tömning av dödsbo</strong></p>
<p>Förlorar du en make, maka eller förälder är det ofta du som pensionär som hanterar det praktiska. Tömning och bortforsling av <a href="/tomma-dodsbo">dödsbo</a> är RUT-berättigat, och dödsbodelägare kan utnyttja avdraget. Läs mer i vår guide om <a href="/guide/rut-avdrag-dodsbostadning">RUT-avdrag vid dödsbostädning</a>.</p>
<p><strong>Möbelmontering</strong></p>
<p>Köpt en ny bokhylla eller säng som behöver monteras? <a href="/mobelmontering">Möbelmontering</a> är RUT-berättigat. Läs om <a href="/guide/rut-avdrag-mobelmontering">RUT-avdrag på möbelmontering</a>.</p>
<p><strong>Trädgårdsarbete</strong></p>
<p>Klippa häck, kratsa löv, röja sly och sköta trädgården ingår i RUT-avdraget.</p>
<p><strong>Städning</strong></p>
<p>Hemstädning är RUT-berättigat. Ungdomskraft erbjuder inte städtjänster, men det är värt att känna till.</p>`,
      },
      {
        heading: "Hur mycket sparar du?",
        html: `<p>RUT-avdraget täcker 50 % av arbetskostnaden — inte materialet, inte transporten, inte momsen. Bara arbetet.</p>
<p><strong>Räkneexempel för vanliga pensionärsuppdrag:</strong></p>
<table>
<thead><tr><th>Tjänst</th><th>Arbetskostnad</th><th>Du betalar</th><th>Staten betalar</th></tr></thead>
<tbody>
<tr><td>Flytt av 2-rk lägenhet</td><td>8 000 kr</td><td>4 000 kr</td><td>4 000 kr</td></tr>
<tr><td>Bortforsling av möbler</td><td>3 000 kr</td><td>1 500 kr</td><td>1 500 kr</td></tr>
<tr><td>Tömning av villa</td><td>12 000 kr</td><td>6 000 kr</td><td>6 000 kr</td></tr>
<tr><td>Trädgårdshjälp (säsong)</td><td>4 000 kr</td><td>2 000 kr</td><td>2 000 kr</td></tr>
<tr><td>Möbelmontering</td><td>1 500 kr</td><td>750 kr</td><td>750 kr</td></tr>
</tbody>
</table>
<p>Är ni två pensionärer i hushållet — ett par — kan ni vardera utnyttja upp till 50 000 kr, förutsatt att ni båda betalar tillräcklig skatt. Det innebär att ett hushåll kan ha upp till 100 000 kr i kombinerat RUT-utrymme per år.</p>`,
      },
      {
        heading: "Hur fungerar det i praktiken?",
        html: `<p>Processen är enkel och kräver inget arbete från din sida:</p>
<ol>
<li><strong>Anlita ett godkänt företag</strong> med F-skattsedel. Det är ett krav — <a href="/guide/ungdomskraft-vs-tiptapp">privatpersoner via appar</a> ger aldrig RUT-avdrag.</li>
<li><strong>Ange ditt personnummer</strong> vid bokning eller på fakturan.</li>
<li><strong>Betala bara din halva</strong> — 50 % av arbetskostnaden direkt till företaget.</li>
<li><strong>Företaget ansöker</strong> om resten från Skatteverket inom 30 dagar.</li>
<li><strong>Inget att göra i deklarationen</strong> — avdraget syns automatiskt.</li>
</ol>
<p>Hos Ungdomskraft ser du priset med RUT inräknat direkt när du bokar. Du betalar aldrig mer än vad som visas.</p>`,
      },
      {
        heading: "Vad händer om min pension är för låg?",
        html: `<p>Om din skatt under året understiger det avdrag du vill utnyttja täcker avdraget bara upp till den faktiska skatten. Resten faller bort.</p>
<p><strong>Exempel:</strong> Din skatt för 2026 är 20 000 kr. Du bokar en flytt som kostar 15 000 kr i arbetskostnad. RUT-avdraget är 7 500 kr — det ryms inom din skatt. Klart.</p>
<p>Men om du samma år också bokar bortforsling för 20 000 kr i arbetskostnad (10 000 kr i RUT) går det totala avdraget upp till 17 500 kr. Fortfarande under din skatt på 20 000 kr — det fungerar fint.</p>
<p>Däremot: om du totalt försöker utnyttja 25 000 kr i RUT men bara betalar 20 000 kr i skatt täcks bara 20 000 kr. De återstående 5 000 kr beviljas inte och du kan bli återbetalningsskyldig om företaget redan fått utbetalningen.</p>
<p><strong>Råd:</strong> Kolla ditt utrymme på Skatteverkets Mina Sidor innan du bokar stora uppdrag.</p>`,
      },
      {
        heading: "Kan man kombinera RUT och ROT?",
        html: `<p>Ja. Det kombinerade taket för RUT och ROT är 75 000 kr per person och år — varav max 50 000 kr får vara ROT. För de flesta pensionärer med normal pensionsnivå är det sällan ett problem att nå taket, men det är bra att känna till om du till exempel renoverar badrummet (ROT) och samtidigt anlitar hjälp med flytt (RUT) under samma år.</p>`,
      },
      {
        heading: "Ungdomskraft hjälper pensionärer i Stockholm och Göteborg",
        html: `<p>Vi hjälper regelbundet pensionärer med flytt, bortforsling, <a href="/transporthjalp">transporthjälp</a> och bärhjälp. Vare sig det handlar om att rensa ut ett gammalt hus, flytta till ett nytt boende eller forsla bort möbler som ska doneras — vi hanterar det praktiskt och respektfullt. Vi finns i <a href="/flytthjalp-stockholm">Stockholm</a> och <a href="/flytthjalp-goteborg">Göteborg</a> med extra snabb service, men verkar i hela Sverige. Behöver du bortforsling? Vi har lokal service i <a href="/bortforsling-stockholm">Stockholm</a> och <a href="/bortforsling-goteborg">Göteborg</a>.</p>
<p>RUT-avdraget räknas alltid in automatiskt i priset. Du ser din nettokostnad direkt när du bokar — ingen rörig faktura att lista ut efteråt.</p>
<ul>
<li><strong>Fast pris</strong> — inga överraskningar</li>
<li><strong>RUT inräknat direkt</strong> — du betalar bara din halva</li>
<li><strong>Anställda medarbetare</strong> — bakgrundskontrollerade och försäkrade</li>
<li><strong>Trygg Hansa-försäkring</strong> — om något mot förmodan skulle hända</li>
<li><strong>Boka enkelt</strong> — direkt i mobilen eller ring oss</li>
</ul>
<p><strong><a href="/boka">Boka med RUT-avdrag →</a></strong></p>
<p><strong><a href="tel:0851708281">Ring oss → 08-517 082 81</a></strong></p>`,
      },
    ],
  },
];
